import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Divider,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  AppBar,
  Toolbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Menu as MenuIcon,
  Add as AddIcon,
  BusinessCenter as ProjectIcon,
  Map as MapIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  UnfoldMore as UnfoldMoreIcon,
  ScatterPlot as DrillsIcon,
  ManageSearch as DiscoveryIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from './auth/AuthContext';
import MapView, { AnalysisGridEntry } from './components/MapView';
import SidebarAnalysisRuns from './components/SidebarAnalysisRuns';
import SidebarPermitAlerts from './components/SidebarPermitAlerts';
import DrillClusterReviewPanel from './components/DrillClusterReviewPanel';
import DataTree from './components/DataTree';
import { preventSleep, allowSleep, isWakeLockActive } from './utils/wakeLock';
import {
  DEFAULT_ANALYSIS_GRID_SETTINGS,
  GridSettings,
  sanitizeGridSettings,
  deserializeGridSettings,
  serializeGridSettings,
} from './utils/gridSettings';
import { ModelRunSettings, ModelRegionAssignment } from './components/ModelControlCenter';
import { apiRequest, checkBackendMode } from './config/apiClient';
import { NORWAY_TARGET_POLYGON_SEEDS } from './config/norwayTargets';
import './App.css';
import 'ol/ol.css';

// CommandCenter removed — use UI workflows and terminal directly
const Terminal = lazy(() => import('./components/Terminal'));
const DiscoveryDesk = lazy(() => import('./components/DiscoveryDesk'));

// Manual debugging tools will be loaded via script tag or console

const SIDEBAR_MIN_WIDTH = 280;
const SIDEBAR_MAX_WIDTH = 520;
const DEFAULT_SIDEBAR_WIDTH = 360;
const DEFAULT_LEFT_PANEL_WIDTH = 58;
const DEFAULT_RIGHT_PANEL_WIDTH = 42;
const SIDEBAR_PERSIST_DEBOUNCE_MS = 200;
const PANEL_PERSIST_DEBOUNCE_MS = 200;
const APP_UI_FONT = '"IBM Plex Sans", "Aptos", "Segoe UI", system-ui, sans-serif';
// v2 resets the original one-third default once so existing localStorage does
// not keep the workbench narrower than the new 60/40 map-to-panel layout.
const MAP_WORKBENCH_RATIO_KEY = 'mineral-ai-map-workbench-ratio-v2';
const MAP_WORKBENCH_COLLAPSED_KEY = 'mineral-ai-map-workbench-collapsed';
const MAP_WORKBENCH_DEFAULT_RATIO = 0.4;
const MAP_WORKBENCH_MIN_RATIO = 0.16;
const MAP_WORKBENCH_MIN_WIDTH = 420;
const MAP_WORKBENCH_MIN_MAP_WIDTH = 560;
const MAP_WORKBENCH_MAX_RATIO = 0.55;
const MAP_WORKBENCH_COLLAPSED_WIDTH = 48;

type MapWorkbenchTab = 'layers' | 'selection';

type MainViewMode = 'map' | 'discovery' | 'drills';

const restoreMainViewMode = (): MainViewMode => {
  try {
    const saved = localStorage.getItem('mineral-ai-main-view-mode');
    if (saved === 'map' || saved === 'discovery' || saved === 'drills') {
      return saved;
    }
    if (saved === 'campaigns') {
      return 'discovery';
    }
  } catch {
    // Ignore localStorage failures and fall back to the map.
  }
  return 'map';
};

const readSharedProjectId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const value = new URLSearchParams(window.location.search).get('project');
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
};

const writeSharedProjectId = (projectId: string | null) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const url = new URL(window.location.href);
    if (projectId && projectId.trim()) {
      url.searchParams.set('project', projectId.trim());
    } else {
      url.searchParams.delete('project');
    }
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // Ignore URL sync failures; localStorage still preserves the active project.
  }
};

const clampSidebarWidth = (width: number) => {
  return Math.min(Math.max(width, SIDEBAR_MIN_WIDTH), SIDEBAR_MAX_WIDTH);
};

const readMapWorkbenchRatio = () => {
  if (typeof window === 'undefined') {
    return MAP_WORKBENCH_DEFAULT_RATIO;
  }
  const parsed = Number(window.localStorage.getItem(MAP_WORKBENCH_RATIO_KEY));
  return Number.isFinite(parsed) && parsed >= MAP_WORKBENCH_MIN_RATIO && parsed <= MAP_WORKBENCH_MAX_RATIO
    ? parsed
    : MAP_WORKBENCH_DEFAULT_RATIO;
};

const readMapWorkbenchCollapsed = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.localStorage.getItem(MAP_WORKBENCH_COLLAPSED_KEY) === 'true';
};

const getMapWorkbenchBounds = (viewportWidth: number) => {
  const responsiveMin = viewportWidth < 980 ? Math.min(320, viewportWidth * 0.44) : MAP_WORKBENCH_MIN_WIDTH;
  const availableMax = Math.max(responsiveMin, viewportWidth - MAP_WORKBENCH_MIN_MAP_WIDTH);
  return {
    min: responsiveMin,
    max: Math.max(responsiveMin, Math.min(viewportWidth * MAP_WORKBENCH_MAX_RATIO, availableMax)),
  };
};

const clampMapWorkbenchWidth = (width: number, viewportWidth: number) => {
  const bounds = getMapWorkbenchBounds(viewportWidth);
  return Math.min(Math.max(width, bounds.min), bounds.max);
};

const getMapAppViewportWidth = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 1440;
  }
  const explicitRootWidth = Number.parseFloat(document.documentElement.style.width || '');
  return Number.isFinite(explicitRootWidth) && explicitRootWidth > 0
    ? explicitRootWidth
    : window.innerWidth;
};

const initialMapWorkbenchWidth = () => {
  if (typeof window === 'undefined') {
    return 640;
  }
  const viewportWidth = getMapAppViewportWidth();
  return clampMapWorkbenchWidth(viewportWidth * readMapWorkbenchRatio(), viewportWidth);
};

// Enhanced interfaces for enterprise features
interface Project {
  id: string;
  name: string;
  location: string;
  coordinates?: { lat: number; lng: number };
  target_minerals: string[];
  project_type: string;
  status: string;
  analysis_count: number;
  site_data_count: number;
  updated_at: string;
  project_boundary?: any;
  boundary_method?: string;
}

const PROJECT_FLAG_BY_ID: Record<string, string> = {
  'project-sgu-sweden': '🇸🇪',
  'project-sweden-uranium': '🇸🇪',
  'project-gtk-finland': '🇫🇮',
  'project-peru-wide': '🇵🇪',
  'project-png-wide': '🇵🇬',
  'project-yukon-wide': '🇨🇦',
  'project-caujul': '🇵🇪',
  'project-australia-wa': '🇦🇺',
  'project-canada-quebec': '🇨🇦',
  'project-canada-ontario': '🇨🇦',
  'project-canada-nl': '🇨🇦',
  'project-ireland-gsi': '🇮🇪',
  'project-greenland-geus': '🇬🇱',
  'project-norway-ngu': '🇳🇴',
  'project-chile-sernageomin': '🇨🇱',
  'project-france-brgm': '🇫🇷',
  'project-colombia-sgc': '🇨🇴',
  'project-drc-cami': '🇨🇩',
  'project-usa-usgs': '🇺🇸',
  'project-bolivia': '🇧🇴',
  'project-zambia-mmmd': '🇿🇲',
};

const getProjectFlag = (project?: Project | null) => {
  if (!project) return '🌐';
  if (PROJECT_FLAG_BY_ID[project.id]) return PROJECT_FLAG_BY_ID[project.id];
  const label = `${project.name} ${project.location}`.toLowerCase();
  if (label.includes('sweden')) return '🇸🇪';
  if (label.includes('finland')) return '🇫🇮';
  if (label.includes('peru')) return '🇵🇪';
  if (label.includes('australia')) return '🇦🇺';
  if (label.includes('canada') || label.includes('ontario') || label.includes('quebec')) return '🇨🇦';
  if (label.includes('ireland')) return '🇮🇪';
  if (label.includes('greenland')) return '🇬🇱';
  if (label.includes('norway')) return '🇳🇴';
  if (label.includes('chile')) return '🇨🇱';
  if (label.includes('france')) return '🇫🇷';
  if (label.includes('colombia')) return '🇨🇴';
  if (label.includes('drc') || label.includes('congo')) return '🇨🇩';
  if (label.includes('usa') || label.includes('united states')) return '🇺🇸';
  if (label.includes('bolivia')) return '🇧🇴';
  if (label.includes('zambia')) return '🇿🇲';
  return '🌐';
};

interface SystemMessage {
  timestamp: string;
  message_type: string;
  content: string;
  data?: any;
}

interface PolygonMetadata {
  area_km2: number;
  perimeter_km: number;
  vertex_count: number;
  bounds: {
    min_lat: number;
    min_lon: number;
    max_lat: number;
    max_lon: number;
  };
}

interface PolygonRegion {
  id: string;
  name: string;
  description?: string;
  coordinates: number[][];
  region_type: 'project_boundary' | 'analysis_region';
  metadata: PolygonMetadata;
  is_valid: boolean;
  validation_errors: string[];
  visible: boolean;
  color: string;
  opacity: number;
  used_in_analyses?: string[];
  last_analysis_date?: string;
  created_at: string;
  updated_at: string;
}

const SWEDEN_BBOX = [8.21, 54.88, 25.47, 69.14];
const SWEDEN_CENTER = { lat: 62.0, lng: 16.0 };
const SWEDEN_URANIUM_CENTER = { lat: 64.5, lng: 17.0 }; // Northern Sweden focus for uranium

// Finland boundaries and center
const FINLAND_BBOX = [20.5, 59.8, 31.6, 70.1];
const FINLAND_CENTER = { lat: 64.5, lng: 26.0 };

// Peru boundaries and center
const PERU_BBOX = [-81.4, -18.4, -68.7, -0.0];
const PERU_CENTER = { lat: -9.19, lng: -75.02 };

const SWEDEN_PROJECT: Project = {
  id: 'project-sgu-sweden',
  name: 'Sweden (SGU)',
  location: 'Sweden',
  coordinates: SWEDEN_CENTER,
  target_minerals: ['copper', 'gold', 'nickel', 'battery_metals', 'rare_earths'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { name: 'Sweden' },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [SWEDEN_BBOX[0], SWEDEN_BBOX[1]],
              [SWEDEN_BBOX[2], SWEDEN_BBOX[1]],
              [SWEDEN_BBOX[2], SWEDEN_BBOX[3]],
              [SWEDEN_BBOX[0], SWEDEN_BBOX[3]],
              [SWEDEN_BBOX[0], SWEDEN_BBOX[1]]
            ]
          ]
        }
      }
    ]
  },
  boundary_method: 'preset'
};

const SWEDEN_URANIUM_PROJECT: Project = {
  id: 'project-sweden-uranium',
  name: 'Sweden Uranium',
  location: 'Sweden',
  coordinates: SWEDEN_URANIUM_CENTER,
  target_minerals: ['uranium'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { name: 'Sweden Uranium' },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [SWEDEN_BBOX[0], SWEDEN_BBOX[1]],
              [SWEDEN_BBOX[2], SWEDEN_BBOX[1]],
              [SWEDEN_BBOX[2], SWEDEN_BBOX[3]],
              [SWEDEN_BBOX[0], SWEDEN_BBOX[3]],
              [SWEDEN_BBOX[0], SWEDEN_BBOX[1]]
            ]
          ]
        }
      }
    ]
  },
  boundary_method: 'preset'
};

const FINLAND_PROJECT: Project = {
  id: 'project-gtk-finland',
  name: 'Finland (GTK)',
  location: 'Finland',
  coordinates: FINLAND_CENTER,
  target_minerals: ['gold', 'nickel', 'copper', 'cobalt', 'lithium', 'battery_metals'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { name: 'Finland' },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [FINLAND_BBOX[0], FINLAND_BBOX[1]],
              [FINLAND_BBOX[2], FINLAND_BBOX[1]],
              [FINLAND_BBOX[2], FINLAND_BBOX[3]],
              [FINLAND_BBOX[0], FINLAND_BBOX[3]],
              [FINLAND_BBOX[0], FINLAND_BBOX[1]]
            ]
          ]
        }
      }
    ]
  },
  boundary_method: 'preset'
};

const PERU_PROJECT: Project = {
  id: 'project-peru-wide',
  name: 'Peru (INGEMMET)',
  location: 'Peru',
  coordinates: PERU_CENTER,
  target_minerals: ['gold', 'silver', 'copper', 'zinc', 'lead'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { name: 'Peru' },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [PERU_BBOX[0], PERU_BBOX[1]],
              [PERU_BBOX[2], PERU_BBOX[1]],
              [PERU_BBOX[2], PERU_BBOX[3]],
              [PERU_BBOX[0], PERU_BBOX[3]],
              [PERU_BBOX[0], PERU_BBOX[1]]
            ]
          ]
        }
      }
    ]
  },
  boundary_method: 'preset'
};

// Papua New Guinea project - jurisdiction-level working project (MRA cadastre)
const PNG_BBOX = [140.8, -11.7, 156.0, -1.0];
const PNG_CENTER = { lat: -6.0, lng: 147.0 };

const PNG_PROJECT: Project = {
  id: 'project-png-wide',
  name: 'Papua New Guinea (MRA)',
  location: 'Papua New Guinea',
  coordinates: PNG_CENTER,
  target_minerals: ['gold', 'copper', 'silver', 'molybdenum', 'nickel'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { name: 'Papua New Guinea' },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [PNG_BBOX[0], PNG_BBOX[1]],
              [PNG_BBOX[2], PNG_BBOX[1]],
              [PNG_BBOX[2], PNG_BBOX[3]],
              [PNG_BBOX[0], PNG_BBOX[3]],
              [PNG_BBOX[0], PNG_BBOX[1]]
            ]
          ]
        }
      }
    ]
  },
  boundary_method: 'preset'
};

// Yukon project - jurisdiction-level working project (GeoYukon ArcGIS REST stack)
const YUKON_BBOX = [-141.1, 59.9, -123.7, 69.7];
const YUKON_CENTER = { lat: 63.7, lng: -135.0 };

const YUKON_PROJECT: Project = {
  id: 'project-yukon-wide',
  name: 'Yukon (GeoYukon)',
  location: 'Yukon, Canada',
  coordinates: YUKON_CENTER,
  target_minerals: ['tungsten', 'tin', 'antimony', 'silver', 'gold', 'copper', 'REE'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { name: 'Yukon' },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [YUKON_BBOX[0], YUKON_BBOX[1]],
              [YUKON_BBOX[2], YUKON_BBOX[1]],
              [YUKON_BBOX[2], YUKON_BBOX[3]],
              [YUKON_BBOX[0], YUKON_BBOX[3]],
              [YUKON_BBOX[0], YUKON_BBOX[1]]
            ]
          ]
        }
      }
    ]
  },
  boundary_method: 'preset'
};

// Caujul Project - Peru silver-gold epithermal deposit
// Bounding box from 37 mining concessions (13,237 ha total)
const CAUJUL_BBOX = [-77.06, -10.82, -76.915, -10.617];
const CAUJUL_CENTER = { lat: -10.718, lng: -76.986 };

const CAUJUL_PROJECT: Project = {
  id: 'project-caujul',
  name: 'Caujul',
  location: 'Peru - Junín',
  coordinates: CAUJUL_CENTER,
  target_minerals: ['silver', 'gold', 'copper', 'molybdenum', 'lead', 'zinc'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { name: 'Caujul' },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [CAUJUL_BBOX[0], CAUJUL_BBOX[1]],
              [CAUJUL_BBOX[2], CAUJUL_BBOX[1]],
              [CAUJUL_BBOX[2], CAUJUL_BBOX[3]],
              [CAUJUL_BBOX[0], CAUJUL_BBOX[3]],
              [CAUJUL_BBOX[0], CAUJUL_BBOX[1]]
            ]
          ]
        }
      }
    ]
  },
  boundary_method: 'preset'
};

// Western Australia project - jurisdiction-level working project
const WESTERN_AUSTRALIA_BBOX = [112.8, -35.2, 129.1, -13.5];
const WESTERN_AUSTRALIA_CENTER = { lat: -25.3, lng: 121.0 };

const WESTERN_AUSTRALIA_PROJECT: Project = {
  id: 'project-australia-wa',
  name: 'Western Australia',
  location: 'Australia - Western Australia',
  coordinates: WESTERN_AUSTRALIA_CENTER,
  target_minerals: ['gold', 'nickel', 'copper', 'lithium', 'rare_earths'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Western Australia' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [WESTERN_AUSTRALIA_BBOX[0], WESTERN_AUSTRALIA_BBOX[1]],
          [WESTERN_AUSTRALIA_BBOX[2], WESTERN_AUSTRALIA_BBOX[1]],
          [WESTERN_AUSTRALIA_BBOX[2], WESTERN_AUSTRALIA_BBOX[3]],
          [WESTERN_AUSTRALIA_BBOX[0], WESTERN_AUSTRALIA_BBOX[3]],
          [WESTERN_AUSTRALIA_BBOX[0], WESTERN_AUSTRALIA_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// Ontario project - jurisdiction-level working project
const ONTARIO_BBOX = [-95.2, 41.6, -74.3, 56.9];
const ONTARIO_CENTER = { lat: 49.0, lng: -84.5 };

// Québec
const QUEBEC_BBOX = [-79.8, 45.0, -57.1, 62.6];
const QUEBEC_CENTER = { lat: 52.0, lng: -70.0 };

const QUEBEC_PROJECT: Project = {
  id: 'project-canada-quebec',
  name: 'Québec',
  location: 'Canada - Québec',
  coordinates: QUEBEC_CENTER,
  target_minerals: ['gold', 'copper', 'nickel', 'cobalt', 'lithium', 'iron', 'rare_earths'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Québec' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [QUEBEC_BBOX[0], QUEBEC_BBOX[1]],
          [QUEBEC_BBOX[2], QUEBEC_BBOX[1]],
          [QUEBEC_BBOX[2], QUEBEC_BBOX[3]],
          [QUEBEC_BBOX[0], QUEBEC_BBOX[3]],
          [QUEBEC_BBOX[0], QUEBEC_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

const ONTARIO_PROJECT: Project = {
  id: 'project-canada-ontario',
  name: 'Ontario',
  location: 'Canada - Ontario',
  coordinates: ONTARIO_CENTER,
  target_minerals: ['gold', 'copper', 'nickel', 'cobalt', 'lithium', 'uranium'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Ontario' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [ONTARIO_BBOX[0], ONTARIO_BBOX[1]],
          [ONTARIO_BBOX[2], ONTARIO_BBOX[1]],
          [ONTARIO_BBOX[2], ONTARIO_BBOX[3]],
          [ONTARIO_BBOX[0], ONTARIO_BBOX[3]],
          [ONTARIO_BBOX[0], ONTARIO_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// Newfoundland & Labrador
const NEWFOUNDLAND_BBOX = [-67.8, 46.6, -52.6, 60.4];
const NEWFOUNDLAND_CENTER = { lat: 52.5, lng: -58.0 };

const NEWFOUNDLAND_PROJECT: Project = {
  id: 'project-canada-nl',
  name: 'Newfoundland & Labrador',
  location: 'Canada - NL',
  coordinates: NEWFOUNDLAND_CENTER,
  target_minerals: ['gold', 'copper', 'zinc', 'nickel', 'rare_earths', 'tungsten', 'antimony'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Newfoundland & Labrador' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [NEWFOUNDLAND_BBOX[0], NEWFOUNDLAND_BBOX[1]],
          [NEWFOUNDLAND_BBOX[2], NEWFOUNDLAND_BBOX[1]],
          [NEWFOUNDLAND_BBOX[2], NEWFOUNDLAND_BBOX[3]],
          [NEWFOUNDLAND_BBOX[0], NEWFOUNDLAND_BBOX[3]],
          [NEWFOUNDLAND_BBOX[0], NEWFOUNDLAND_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// Ireland boundaries and center
const IRELAND_BBOX = [-10.5, 51.4, -5.5, 55.4];
const IRELAND_CENTER = { lat: 53.4, lng: -8.0 };

const IRELAND_PROJECT: Project = {
  id: 'project-ireland-gsi',
  name: 'Ireland (GSI)',
  location: 'Ireland',
  coordinates: IRELAND_CENTER,
  target_minerals: ['gold', 'zinc', 'lead', 'copper', 'silver'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Ireland' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [IRELAND_BBOX[0], IRELAND_BBOX[1]],
          [IRELAND_BBOX[2], IRELAND_BBOX[1]],
          [IRELAND_BBOX[2], IRELAND_BBOX[3]],
          [IRELAND_BBOX[0], IRELAND_BBOX[3]],
          [IRELAND_BBOX[0], IRELAND_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// Greenland boundaries and center
const GREENLAND_BBOX = [-73.0, 59.8, -11.0, 83.6];
const GREENLAND_CENTER = { lat: 72.0, lng: -42.0 };

const GREENLAND_PROJECT: Project = {
  id: 'project-greenland-geus',
  name: 'Greenland (GEUS)',
  location: 'Greenland',
  coordinates: GREENLAND_CENTER,
  target_minerals: ['rare_earths', 'uranium', 'lithium', 'copper', 'gold', 'zinc'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Greenland' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [GREENLAND_BBOX[0], GREENLAND_BBOX[1]],
          [GREENLAND_BBOX[2], GREENLAND_BBOX[1]],
          [GREENLAND_BBOX[2], GREENLAND_BBOX[3]],
          [GREENLAND_BBOX[0], GREENLAND_BBOX[3]],
          [GREENLAND_BBOX[0], GREENLAND_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// Norway boundaries and center
const NORWAY_BBOX = [4.5, 57.9, 31.2, 71.2];
const NORWAY_CENTER = { lat: 64.5, lng: 17.0 };

const NORWAY_PROJECT: Project = {
  id: 'project-norway-ngu',
  name: 'Norway (NGU)',
  location: 'Norway',
  coordinates: NORWAY_CENTER,
  target_minerals: ['copper', 'nickel', 'cobalt', 'titanium', 'rare_earths'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Norway' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [NORWAY_BBOX[0], NORWAY_BBOX[1]],
          [NORWAY_BBOX[2], NORWAY_BBOX[1]],
          [NORWAY_BBOX[2], NORWAY_BBOX[3]],
          [NORWAY_BBOX[0], NORWAY_BBOX[3]],
          [NORWAY_BBOX[0], NORWAY_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// Chile boundaries and center
const CHILE_BBOX = [-75.7, -55.9, -66.4, -17.5];
const CHILE_CENTER = { lat: -35.7, lng: -71.5 };

const CHILE_PROJECT: Project = {
  id: 'project-chile-sernageomin',
  name: 'Chile (SERNAGEOMIN)',
  location: 'Chile',
  coordinates: CHILE_CENTER,
  target_minerals: ['copper', 'lithium', 'gold', 'silver', 'molybdenum'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Chile' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [CHILE_BBOX[0], CHILE_BBOX[1]],
          [CHILE_BBOX[2], CHILE_BBOX[1]],
          [CHILE_BBOX[2], CHILE_BBOX[3]],
          [CHILE_BBOX[0], CHILE_BBOX[3]],
          [CHILE_BBOX[0], CHILE_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// France boundaries and center (metropolitan only, excludes overseas territories)
const FRANCE_BBOX = [-5.2, 41.3, 9.6, 51.1];
const FRANCE_CENTER = { lat: 46.2, lng: 2.2 };

const FRANCE_PROJECT: Project = {
  id: 'project-france-brgm',
  name: 'France (BRGM)',
  location: 'France',
  coordinates: FRANCE_CENTER,
  target_minerals: ['gold', 'tungsten', 'antimony', 'lithium', 'copper', 'rare_earths'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'France' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [FRANCE_BBOX[0], FRANCE_BBOX[1]],
          [FRANCE_BBOX[2], FRANCE_BBOX[1]],
          [FRANCE_BBOX[2], FRANCE_BBOX[3]],
          [FRANCE_BBOX[0], FRANCE_BBOX[3]],
          [FRANCE_BBOX[0], FRANCE_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// Colombia
const COLOMBIA_BBOX = [-79.0, -4.3, -66.9, 12.5];
const COLOMBIA_CENTER = { lat: 4.6, lng: -74.0 };

const COLOMBIA_PROJECT: Project = {
  id: 'project-colombia-sgc',
  name: 'Colombia (SGC/ANM)',
  location: 'Colombia',
  coordinates: COLOMBIA_CENTER,
  target_minerals: ['gold', 'copper', 'silver', 'porphyry', 'epithermal'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Colombia' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [COLOMBIA_BBOX[0], COLOMBIA_BBOX[1]],
          [COLOMBIA_BBOX[2], COLOMBIA_BBOX[1]],
          [COLOMBIA_BBOX[2], COLOMBIA_BBOX[3]],
          [COLOMBIA_BBOX[0], COLOMBIA_BBOX[3]],
          [COLOMBIA_BBOX[0], COLOMBIA_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// DRC boundaries and center (Democratic Republic of the Congo, ISO COD)
const DRC_BBOX = [12.2, -13.5, 31.3, 5.4];
const DRC_CENTER = { lat: -4.0, lng: 23.5 };

const DRC_PROJECT: Project = {
  id: 'project-drc-cami',
  name: 'DRC (CAMI)',
  location: 'Democratic Republic of the Congo',
  coordinates: DRC_CENTER,
  target_minerals: ['copper', 'cobalt', 'lithium', 'gold', 'tin', 'tantalum'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'DRC' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [DRC_BBOX[0], DRC_BBOX[1]],
          [DRC_BBOX[2], DRC_BBOX[1]],
          [DRC_BBOX[2], DRC_BBOX[3]],
          [DRC_BBOX[0], DRC_BBOX[3]],
          [DRC_BBOX[0], DRC_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// USA boundaries and center
const USA_BBOX = [-125.0, 24.5, -66.9, 49.4];
const USA_CENTER = { lat: 39.0, lng: -98.0 };

const USA_PROJECT: Project = {
  id: 'project-usa-usgs',
  name: 'USA (USGS)',
  location: 'United States',
  coordinates: USA_CENTER,
  target_minerals: ['gold', 'copper', 'lithium', 'rare_earths', 'uranium'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'USA' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [USA_BBOX[0], USA_BBOX[1]],
          [USA_BBOX[2], USA_BBOX[1]],
          [USA_BBOX[2], USA_BBOX[3]],
          [USA_BBOX[0], USA_BBOX[3]],
          [USA_BBOX[0], USA_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// Bolivia project scaffold - source layers will be attached as curated outputs arrive
const BOLIVIA_BBOX = [-69.7, -22.9, -57.4, -9.7];
const BOLIVIA_CENTER = { lat: -16.3, lng: -64.7 };

const BOLIVIA_PROJECT: Project = {
  id: 'project-bolivia',
  name: 'Bolivia',
  location: 'Bolivia',
  coordinates: BOLIVIA_CENTER,
  target_minerals: ['gold', 'silver', 'tin', 'lithium', 'zinc', 'lead'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name: 'Bolivia' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [BOLIVIA_BBOX[0], BOLIVIA_BBOX[1]],
          [BOLIVIA_BBOX[2], BOLIVIA_BBOX[1]],
          [BOLIVIA_BBOX[2], BOLIVIA_BBOX[3]],
          [BOLIVIA_BBOX[0], BOLIVIA_BBOX[3]],
          [BOLIVIA_BBOX[0], BOLIVIA_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// Zambia jurisdiction project — national MMMD/GSD/public-source stack.
// Target layers are restricted to live-audited screening-open polygons.
const ZAMBIA_BBOX = [21.9, -18.2, 33.7, -8.2];
const ZAMBIA_CENTER = { lat: -13.13, lng: 27.85 };

const ZAMBIA_PROJECT: Project = {
  id: 'project-zambia-mmmd',
  name: 'Zambia (MMMD / GSD)',
  location: 'Zambia',
  coordinates: ZAMBIA_CENTER,
  target_minerals: ['copper', 'cobalt', 'gold', 'nickel', 'zinc', 'lead', 'manganese'],
  project_type: 'exploration',
  status: 'draft',
  analysis_count: 0,
  site_data_count: 0,
  updated_at: new Date().toISOString(),
  project_boundary: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {
        name: 'Zambia',
        workflow: 'national data, live cadastre screening, and open-ground target generation',
        target_guardrail: 'zero publishable targets after current title/application and source-location QA'
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [ZAMBIA_BBOX[0], ZAMBIA_BBOX[1]],
          [ZAMBIA_BBOX[2], ZAMBIA_BBOX[1]],
          [ZAMBIA_BBOX[2], ZAMBIA_BBOX[3]],
          [ZAMBIA_BBOX[0], ZAMBIA_BBOX[3]],
          [ZAMBIA_BBOX[0], ZAMBIA_BBOX[1]]
        ]]
      }
    }]
  },
  boundary_method: 'preset'
};

// List of all default country projects for easy management
const DEFAULT_COUNTRY_PROJECTS = [
  SWEDEN_PROJECT,
  SWEDEN_URANIUM_PROJECT,
  FINLAND_PROJECT,
  PERU_PROJECT,
  PNG_PROJECT,
  YUKON_PROJECT,
  CAUJUL_PROJECT,
  WESTERN_AUSTRALIA_PROJECT,
  ONTARIO_PROJECT,
  QUEBEC_PROJECT,
  NEWFOUNDLAND_PROJECT,
  IRELAND_PROJECT,
  GREENLAND_PROJECT,
  NORWAY_PROJECT,
  CHILE_PROJECT,
  USA_PROJECT,
  FRANCE_PROJECT,
  COLOMBIA_PROJECT,
  DRC_PROJECT,
  BOLIVIA_PROJECT,
  ZAMBIA_PROJECT
];

const findDefaultProject = (projectId: string | null | undefined): Project | null => {
  if (!projectId) {
    return null;
  }
  return DEFAULT_COUNTRY_PROJECTS.find((project) => project.id === projectId) || null;
};

const ensureDefaultProjects = (projects: Project[]): Project[] => {
  const retiredDefaultProjectIds = new Set(['project-australia-ga', 'project-canada-gsc']);
  let result = projects.filter((project) => !retiredDefaultProjectIds.has(project.id));

  for (const defaultProject of DEFAULT_COUNTRY_PROJECTS) {
    const exists = projects.some((p) => p.id === defaultProject.id || p.name === defaultProject.name);
    if (!exists) {
      result = [...result, defaultProject];
    }
  }

  return result;
};

// Keep old names for backwards compatibility
const ensureSwedenProjects = ensureDefaultProjects;
const ensureSwedenProject = ensureDefaultProjects;

interface CreatePolygonRequest {
  name: string;
  description?: string;
  coordinates: number[][];
  region_type: 'project_boundary' | 'analysis_region';
  metadata: PolygonMetadata;
  visible?: boolean;
  color?: string;
  opacity?: number;
  skip_validation?: boolean;
}

interface AnalysisConfig {
  commodity: string;
  analysis_name: string;
  confidence_threshold: number;
  output_formats: string[];
  run_settings?: ModelRunSettings;
}

interface GridControlContext {
  key: string;
  label: string;
  projectId?: string | null;
  projectName?: string | null;
  polygonId?: string | null;
  polygonName?: string | null;
  bounds?: [number, number, number, number] | null;
  resolution: number;
  disabled?: boolean;
  lastRefresh?: string | null;
  isRefreshing?: boolean;
}

interface FieldProgram {
  id: string;
  program_type: string;
  status: string;
  budget_total: number;
  timeline_weeks: number;
  objectives: string[];
  created_at: string;
}

interface TechnicalReport {
  id: string;
  report_type: string;
  title: string;
  status: string;
  created_at: string;
}

const normalizeCommodity = (value: unknown): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim().toLowerCase();
  }
  return 'unknown';
};

const resolveStringCandidate = (candidates: unknown[]): string | null => {
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) {
      continue;
    }
    const value = String(candidate).trim();
    if (value.length > 0) {
      return value;
    }
  }
  return null;
};

const resolveCommodityCandidate = (candidates: unknown[], fallback = 'unknown'): string => {
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') {
      continue;
    }
    const normalized = normalizeCommodity(candidate);
    if (normalized && normalized !== 'unknown' && normalized !== 'none') {
      return normalized;
    }
  }
  return fallback;
};

const normalizeAnalysisResult = (
  result: any,
  context: { projectId?: string | null; projectName?: string | null; commodity?: string | null } = {}
) => {
  if (!result || typeof result !== 'object') {
    return result;
  }

  const normalized: any = { ...result };

  const commodity = resolveCommodityCandidate(
    [
      normalized.commodity,
      normalized.results?.commodity,
      normalized.results?.primary_commodity,
      normalized.results?.metadata?.commodity,
      normalized.results?.report_summary?.commodity,
      normalized.results?.ml_results?.commodity,
      normalized.results?.enhanced_results?.commodity,
      normalized.analysis_metadata?.commodity,
      normalized.metadata?.commodity,
      context.commodity
    ]
  );
  normalized.commodity = commodity;

  const projectId = resolveStringCandidate([
    normalized.project_id,
    normalized.projectId,
    normalized.project?.id,
    normalized.results?.project_id,
    normalized.results?.metadata?.project_id,
    normalized.results?.analysis_metadata?.project_id,
    normalized.metadata?.project_id,
    context.projectId
  ]);
  if (projectId) {
    normalized.project_id = projectId;
  }

  const projectName = resolveStringCandidate([
    normalized.project_name,
    normalized.projectName,
    normalized.project?.name,
    normalized.results?.project_name,
    normalized.results?.metadata?.project_name,
    normalized.results?.analysis_metadata?.project_name,
    normalized.metadata?.project_name,
    context.projectName
  ]);
  if (projectName) {
    normalized.project_name = projectName;
  }

  const analysisId = resolveStringCandidate([
    normalized.analysis_id,
    normalized.analysisId,
    normalized.id,
    normalized.results?.analysis_id,
    normalized.results?.id
  ]);
  if (analysisId) {
    normalized.analysis_id = analysisId;
    normalized.id = analysisId;
  }

  if (normalized.results && typeof normalized.results === 'object') {
    normalized.results = {
      ...normalized.results,
      commodity: normalized.results?.commodity ?? commodity,
      project_id: normalized.results?.project_id ?? projectId ?? undefined,
      project_name: normalized.results?.project_name ?? projectName ?? undefined
    };

    if (normalized.results.report_summary && typeof normalized.results.report_summary === 'object') {
      normalized.results.report_summary = {
        ...normalized.results.report_summary,
        commodity: normalized.results.report_summary?.commodity ?? commodity
      };
    }

    if (normalized.results.metadata && typeof normalized.results.metadata === 'object') {
      normalized.results.metadata = {
        ...normalized.results.metadata,
        commodity: normalized.results.metadata?.commodity ?? commodity
      };
    }
  }

  normalized.metadata = {
    ...(normalized.metadata || {}),
    commodity
  };

  return normalized;
};

const getAnalysisId = (result: any): string | null => {
  if (!result || typeof result !== 'object') {
    return null;
  }
  const idCandidate =
    result.id ||
    result.analysis_id ||
    result.analysisId ||
    result.results?.analysis_id ||
    result.results?.id ||
    null;
  if (typeof idCandidate === 'string') {
    const trimmed = idCandidate.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
};

const mergeAnalysisLists = (base: any[], updates: any[]): any[] => {
  const map = new Map<string, any>();
  const orphaned: any[] = [];

  // Helper to check if an analysis has full grid data
  const hasGridData = (item: any): boolean => {
    const ml = item?.results?.ml_results;
    return !!(
      (ml?.grid_coordinates && ml.grid_coordinates.length > 0) ||
      (ml?.grid_probabilities && ml.grid_probabilities.length > 0) ||
      (ml?.grid_points && ml.grid_points.length > 0)
    );
  };

  // Helper to check if an analysis is lightweight/summary-only
  const isLightweight = (item: any): boolean => {
    return !!(item?.results?.__lightweight || item?.results?.__summary_only);
  };

  const insert = (item: any, preserveFullData: boolean = false) => {
    const normalized = normalizeAnalysisResult(item);
    const id = getAnalysisId(normalized);
    if (!id) {
      orphaned.push(normalized);
      return;
    }

    const existing = map.get(id);

    // CRITICAL: Preserve full grid data when merging
    // Don't let lightweight summaries overwrite full analysis data
    if (existing && hasGridData(existing) && !hasGridData(normalized)) {
      // Update metadata but preserve the results with grid data
      map.set(id, {
        ...normalized,
        results: existing.results // Keep the full results with grid data
      });
    } else if (existing && isLightweight(normalized) && !isLightweight(existing)) {
      // Don't let lightweight data overwrite full data
      map.set(id, {
        ...normalized,
        results: existing.results
      });
    } else {
      // Normal merge - update overwrites base
      map.set(id, {
        ...existing,
        ...normalized
      });
    }
  };

  base.forEach((item) => insert(item));
  updates.forEach((item) => insert(item));

  const merged: any[] = [];
  map.forEach((value) => {
    merged.push(value);
  });

  // Debug: Log merge results
  if (process.env.NODE_ENV === 'development') {
    console.info('[mergeAnalysisLists] Merge complete:', {
      base_count: base.length,
      updates_count: updates.length,
      merged_count: merged.length,
      orphaned_count: orphaned.length,
      entries_with_grid_data: merged.filter(m => hasGridData(m)).map(m => m?.analysis_id || m?.id),
      entries_lightweight: merged.filter(m => isLightweight(m)).map(m => m?.analysis_id || m?.id),
    });
  }

  return merged.concat(orphaned);
};

const mergeProjectAnalyses = (previous: any[], projectId: string | null, updates: any[]): any[] => {
  if (!projectId) {
    return mergeAnalysisLists(previous, updates);
  }

  const normalizedUpdates = updates.map((item) => normalizeAnalysisResult(item, { projectId }));

  const existingForProject = previous.filter((item) => item?.project_id === projectId);
  const mergedProject = mergeAnalysisLists(existingForProject, normalizedUpdates);
  const others = previous.filter((item) => item?.project_id !== projectId);
  const result = [...others, ...mergedProject];

  // Debug: Log merge results to diagnose missing analyses
  if (process.env.NODE_ENV === 'development') {
    console.info('[mergeProjectAnalyses] Merge result:', {
      previous_count: previous.length,
      previous_ids: previous.map(p => p?.analysis_id || p?.id),
      updates_count: updates.length,
      updates_ids: updates.map(u => u?.analysis_id || u?.id),
      existingForProject_count: existingForProject.length,
      mergedProject_count: mergedProject.length,
      result_count: result.length,
      result_ids: result.map(r => r?.analysis_id || r?.id),
    });
  }

  return result;
};
// Legacy helper removed - new normalization helpers defined above

const normalizePolygonVisibility = (polygon: PolygonRegion): PolygonRegion => ({
  ...polygon,
  visible: polygon.visible !== false
});

const loadCachedPolygons = (projectId: string): PolygonRegion[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const cached = localStorage.getItem(`mineral-ai-polygons-${projectId}`);
    if (!cached) {
      return [];
    }
    const parsed = JSON.parse(cached);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map((polygon: PolygonRegion) => normalizePolygonVisibility(polygon));
  } catch (error) {
    console.warn('Failed to restore cached polygon regions:', error);
    return [];
  }
};

const persistProjects = (projectsList: Project[]) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem('mineral-ai-projects', JSON.stringify(projectsList));
  } catch (error) {
    console.error('Failed to cache projects:', error);
  }
};

const persistProjectPolygons = (projectId: string, polygons: PolygonRegion[]) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const serializable = polygons.map((polygon) => normalizePolygonVisibility(polygon));
    if (serializable.length === 0) {
      localStorage.removeItem(`mineral-ai-polygons-${projectId}`);
    } else {
      localStorage.setItem(`mineral-ai-polygons-${projectId}`, JSON.stringify(serializable));
    }
  } catch (error) {
    console.warn('Failed to cache polygon regions:', error);
  }
};

interface ImportedPolygonFeature {
  name: string;
  coordinates: number[][];
  description?: string;
  color?: string;
}

const sanitizeGeoJsonRing = (ring: any[]): number[][] => {
  if (!Array.isArray(ring)) {
    return [];
  }
  const sanitized: number[][] = [];
  ring.forEach((point) => {
    if (!Array.isArray(point) || point.length < 2) {
      return;
    }
    const lon = Number(point[0]);
    const lat = Number(point[1]);
    if (Number.isFinite(lon) && Number.isFinite(lat)) {
      sanitized.push([lon, lat]);
    }
  });
  if (sanitized.length >= 3) {
    const [firstLon, firstLat] = sanitized[0];
    const [lastLon, lastLat] = sanitized[sanitized.length - 1];
    if (Math.abs(firstLon - lastLon) < 1e-9 && Math.abs(firstLat - lastLat) < 1e-9) {
      sanitized.pop();
    }
  }
  return sanitized;
};

const approximatePolygonMetadata = (coordinates: number[][]): PolygonMetadata => {
  if (!coordinates.length) {
    return {
      area_km2: 0,
      perimeter_km: 0,
      vertex_count: 0,
      bounds: { min_lat: 0, min_lon: 0, max_lat: 0, max_lon: 0 }
    };
  }
  const lats = coordinates.map(([, lat]) => lat);
  const lons = coordinates.map(([lon]) => lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const latDiff = maxLat - minLat;
  const lonDiff = maxLon - minLon;
  const avgLat = (maxLat + minLat) / 2;
  const kmPerDegreeLat = 111.32;
  const kmPerDegreeLon = Math.abs(Math.cos((avgLat * Math.PI) / 180)) * 111.32;
  const area = Math.max(latDiff * kmPerDegreeLat * lonDiff * kmPerDegreeLon, 0);
  const perimeter = 2 * ((latDiff * kmPerDegreeLat) + (lonDiff * kmPerDegreeLon));
  return {
    area_km2: Number.isFinite(area) ? Number(area.toFixed(2)) : 0,
    perimeter_km: Number.isFinite(perimeter) ? Number(perimeter.toFixed(2)) : 0,
    vertex_count: coordinates.length,
    bounds: {
      min_lat: minLat,
      min_lon: minLon,
      max_lat: maxLat,
      max_lon: maxLon
    }
  };
};

const getDefaultProjectPolygonSeeds = (projectId: string) => {
  if (projectId === 'project-norway-ngu') {
    return NORWAY_TARGET_POLYGON_SEEDS;
  }
  return [];
};

const normalizePolygonIdentity = (polygon: Pick<PolygonRegion, 'name' | 'region_type'>): string =>
  `${polygon.region_type}:${polygon.name.trim().toLowerCase()}`;

const buildSeedPolygonId = (projectId: string, name: string): string => {
  const normalized = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `seed-${projectId}-${normalized}`;
};

const mergeAndSortProjectPolygons = (projectId: string, polygons: PolygonRegion[]): PolygonRegion[] => {
  const merged = new Map<string, PolygonRegion>();

  polygons.forEach((polygon) => {
    const key = normalizePolygonIdentity(polygon);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, polygon);
      return;
    }

    const existingIsSeed = existing.id.startsWith('seed-');
    const nextIsSeed = polygon.id.startsWith('seed-');
    if (existingIsSeed && !nextIsSeed) {
      merged.set(key, polygon);
      return;
    }
    if (!existingIsSeed && nextIsSeed) {
      return;
    }

    merged.set(key, polygon);
  });

  const ordered = Array.from(merged.values());
  if (projectId !== 'project-norway-ngu') {
    return ordered;
  }

  const order = new Map(NORWAY_TARGET_POLYGON_SEEDS.map((seed, index) => [seed.name, index]));
  return ordered.sort((a, b) => {
    const aIndex = order.get(a.name) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = order.get(b.name) ?? Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }
    return a.name.localeCompare(b.name);
  });
};

const buildSeedPolygonFallback = (
  projectId: string,
  seed: (typeof NORWAY_TARGET_POLYGON_SEEDS)[number]
): PolygonRegion => {
  const timestamp = new Date().toISOString();
  return normalizePolygonVisibility({
    id: buildSeedPolygonId(projectId, seed.name),
    name: seed.name,
    description: seed.description,
    coordinates: seed.coordinates,
    region_type: 'analysis_region',
    metadata: approximatePolygonMetadata(seed.coordinates),
    is_valid: true,
    validation_errors: [],
    visible: true,
    color: seed.color,
    opacity: seed.opacity,
    used_in_analyses: [],
    created_at: timestamp,
    updated_at: timestamp
  });
};

const deriveImportedPolygonName = (
  properties: Record<string, unknown> | undefined,
  fallbackBase: string,
  featureIndex: number,
  partIndex?: number
): string => {
  const candidate = properties
    ? resolveStringCandidate([
        properties.name,
        properties.NAME,
        properties.title,
        properties.TITLE,
        properties.id,
        properties.ID,
        properties.codigo,
        properties.CODIGO,
        properties.codigou,
        properties.CODIGOU
      ])
    : null;
  const suffix = partIndex && partIndex > 1 ? ` ${featureIndex}-${partIndex}` : ` ${featureIndex}`;
  return (candidate || `${fallbackBase}${suffix}`).trim();
};

const extractGeoJsonPolygons = (data: any, fallbackBase: string): ImportedPolygonFeature[] => {
  const polygons: ImportedPolygonFeature[] = [];

  const pushPolygon = (
    coordinates: number[][],
    properties: Record<string, unknown> | undefined,
    featureIndex: number,
    partIndex?: number
  ) => {
    if (coordinates.length < 3) {
      return;
    }
    const description = properties
      ? resolveStringCandidate([
          properties.description,
          properties.DESCRIPTION,
          properties.descripcion,
          properties.DESCRIPCION
        ])
      : null;
    const color = properties
      ? resolveStringCandidate([properties.color, properties.COLOR, properties.stroke, properties.STROKE])
      : null;
    polygons.push({
      name: deriveImportedPolygonName(properties, fallbackBase, featureIndex, partIndex),
      coordinates,
      description: description || undefined,
      color: color || undefined
    });
  };

  const processGeometry = (
    geometry: any,
    properties: Record<string, unknown> | undefined,
    featureIndex: number
  ) => {
    if (!geometry || typeof geometry !== 'object') {
      return;
    }
    if (geometry.type === 'Polygon') {
      const outerRing = Array.isArray(geometry.coordinates) ? geometry.coordinates[0] : null;
      const coords = sanitizeGeoJsonRing(outerRing || []);
      pushPolygon(coords, properties, featureIndex + 1);
      return;
    }
    if (geometry.type === 'MultiPolygon') {
      if (!Array.isArray(geometry.coordinates)) {
        return;
      }
      geometry.coordinates.forEach((polygonCoords: any, idx: number) => {
        const outerRing = Array.isArray(polygonCoords) ? polygonCoords[0] : null;
        const coords = sanitizeGeoJsonRing(outerRing || []);
        pushPolygon(coords, properties, featureIndex + 1, idx + 1);
      });
    }
  };

  if (!data) {
    return polygons;
  }

  if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
    data.features.forEach((feature: any, index: number) => {
      processGeometry(feature?.geometry, feature?.properties ?? {}, index);
    });
    return polygons;
  }

  if (data.type === 'Feature') {
    processGeometry(data.geometry, data.properties ?? {}, 0);
    return polygons;
  }

  if (data.type === 'Polygon' || data.type === 'MultiPolygon') {
    processGeometry(data, {}, 0);
    return polygons;
  }

  if (Array.isArray(data.coordinates) && data.type) {
    processGeometry(data, {}, 0);
    return polygons;
  }

  return polygons;
};

const loadCachedAnalyses = (projectId: string): any[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const cached = localStorage.getItem(`mineral-ai-analyses-${projectId}`);
    if (!cached) {
      return [];
    }
    const parsed = JSON.parse(cached);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const normalizedList = parsed.map((item) =>
      normalizeAnalysisResult(item, {
        projectId,
        projectName: item?.project_name || item?.projectName || null,
        commodity: item?.commodity || item?.results?.commodity || null
      })
    );

    try {
      localStorage.setItem(`mineral-ai-analyses-${projectId}`, JSON.stringify(normalizedList));
    } catch (error) {
      console.warn('Failed to migrate cached analyses to normalized format:', error);
    }

    return normalizedList;
  } catch (error) {
    console.warn('Failed to restore cached analyses:', error);
    return [];
  }
};

const persistProjectAnalyses = (projectId: string, results: any[]) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (!results || results.length === 0) {
      localStorage.removeItem(`mineral-ai-analyses-${projectId}`);
    } else {
      // Only cache lightweight metadata, not full results
      // IMPORTANT: Include analysis_id, project_id, polygon_id for proper identification
      const lightweight = results.map(analysis => ({
        id: analysis.id,
        analysis_id: analysis.analysis_id || analysis.id, // Critical for unique identification
        project_id: analysis.project_id || projectId,     // Critical for project filtering
        polygon_id: analysis.polygon_id,                   // Critical for polygon association
        commodity: analysis.commodity,
        date: analysis.date,
        created_at: analysis.created_at,
        completed_at: analysis.completed_at || analysis.completedAt,
        status: analysis.status,
        analysis_type: analysis.analysis_type,
        summary: analysis.summary,
        region: analysis.region,
        high_potential_count: analysis.high_potential_count,
        polygon_name: analysis.polygon_name,
        // Exclude large 'results' field
      }));
      localStorage.setItem(`mineral-ai-analyses-${projectId}`, JSON.stringify(lightweight));
    }
  } catch (error) {
    console.warn('Failed to cache analysis results:', error);
  }
};

function App() {
  const { signOut, user } = useAuth();

  // State management
const [projects, setProjects] = useState<Project[]>(() => {
  try {
    const saved = localStorage.getItem('mineral-ai-projects');
    if (saved) {
      const parsed = JSON.parse(saved);
      return ensureSwedenProject(parsed);
    }
    return ensureSwedenProject([]);
  } catch (error) {
    console.error('❌ Error parsing localStorage projects:', error);
    localStorage.removeItem('mineral-ai-projects');
    return ensureSwedenProject([]);
  }
});

  // Project context menu state
  const [projectMenuAnchor, setProjectMenuAnchor] = useState<null | HTMLElement>(null);
  const [projectMenuTarget, setProjectMenuTarget] = useState<string | null>(null);

  const [serviceStatus, setServiceStatus] = useState<{backend: string, frontend: string, gee: string}>({
    backend: 'unknown',
    frontend: 'unknown',
    gee: 'unknown'
  });
const [currentProject, setCurrentProject] = useState<Project | null>(() => {
  try {
    const sharedProjectId = readSharedProjectId();
    if (sharedProjectId) {
      const sharedProject = projects.find((project) => project.id === sharedProjectId);
      if (sharedProject) {
        return sharedProject;
      }
      const defaultProject = findDefaultProject(sharedProjectId);
      if (defaultProject) {
        return defaultProject;
      }
    }
    const saved = localStorage.getItem('mineral-ai-current-project');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate that it has the required fields
      if (parsed && parsed.id && parsed.name) {
        return parsed;
      } else {
                  localStorage.removeItem('mineral-ai-current-project');
      }
    }
    return SWEDEN_PROJECT;
  } catch (error) {
    console.error('❌ Error parsing localStorage project:', error);
    localStorage.removeItem('mineral-ai-current-project');
    return SWEDEN_PROJECT;
  }
});
  const [, setIsLoading] = useState(false); // For file upload processing
  const [messages, setMessages] = useState<SystemMessage[]>([]); // For App-level messaging
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('mineral-ai-sidebar-open');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });
  const toggleSidebar = () => {
    setSidebarOpen((prev: boolean) => !prev);
  };

  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_SIDEBAR_WIDTH;
    }
    const saved = window.localStorage.getItem('mineral-ai-sidebar-width');
    if (!saved) {
      return DEFAULT_SIDEBAR_WIDTH;
    }
    const parsed = parseInt(saved, 10);
    if (Number.isFinite(parsed)) {
      return clampSidebarWidth(parsed);
    }
    return DEFAULT_SIDEBAR_WIDTH;
  });
  const [isSidebarResizing, setIsSidebarResizing] = useState(false);
  const sidebarResizeStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const sidebarWidthRef = useRef(sidebarWidth);
  const sidebarPersistTimeoutRef = useRef<number | null>(null);
  const persistSidebarWidth = useCallback((width: number) => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem('mineral-ai-sidebar-width', String(width));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const rounded = Math.round(sidebarWidth);
    document.documentElement.style.setProperty('--palantir-panel-width-sidebar', `${rounded}px`);
  }, [sidebarWidth]);

  useEffect(() => {
    sidebarWidthRef.current = sidebarWidth;
    if (typeof window === 'undefined') {
      return;
    }

    const rounded = Math.round(sidebarWidth);
    if (sidebarPersistTimeoutRef.current !== null) {
      window.clearTimeout(sidebarPersistTimeoutRef.current);
    }
    sidebarPersistTimeoutRef.current = window.setTimeout(() => {
      persistSidebarWidth(rounded);
      sidebarPersistTimeoutRef.current = null;
    }, SIDEBAR_PERSIST_DEBOUNCE_MS);

    return () => {
      if (sidebarPersistTimeoutRef.current !== null) {
        window.clearTimeout(sidebarPersistTimeoutRef.current);
        sidebarPersistTimeoutRef.current = null;
      }
    };
  }, [sidebarWidth, persistSidebarWidth]);

  const [mainViewMode, setMainViewMode] = useState<MainViewMode>(restoreMainViewMode);
  const showRightPanel = mainViewMode === 'map';
  const [mapWorkbenchTab, setMapWorkbenchTab] = useState<MapWorkbenchTab>('layers');
  const [mapHasSelection, setMapHasSelection] = useState(false);
  const [mapWorkbenchCollapsed, setMapWorkbenchCollapsed] = useState(readMapWorkbenchCollapsed);
  const [mapWorkbenchWidth, setMapWorkbenchWidth] = useState(initialMapWorkbenchWidth);
  const [isMapWorkbenchResizing, setIsMapWorkbenchResizing] = useState(false);
  const mapWorkbenchWidthRef = useRef(mapWorkbenchWidth);
  const mapWorkbenchRatioRef = useRef(readMapWorkbenchRatio());
  const mapWorkbenchResizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    mapWorkbenchWidthRef.current = mapWorkbenchWidth;
  }, [mapWorkbenchWidth]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const frame = window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    const settled = window.setTimeout(() => window.dispatchEvent(new Event('resize')), 220);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settled);
    };
  }, [mapWorkbenchCollapsed, mapWorkbenchWidth]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(MAP_WORKBENCH_COLLAPSED_KEY, String(mapWorkbenchCollapsed));
    }
  }, [mapWorkbenchCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleResize = () => {
      const viewportWidth = getMapAppViewportWidth();
      setMapWorkbenchWidth(
        clampMapWorkbenchWidth(viewportWidth * mapWorkbenchRatioRef.current, viewportWidth)
      );
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMapInspectionActiveChange = useCallback((active: boolean) => {
    setMapHasSelection(active);
    if (active) {
      setMapWorkbenchTab('selection');
      setMapWorkbenchCollapsed(false);
    } else {
      setMapWorkbenchTab('layers');
    }
  }, []);

  const updateMainViewMode = (mode: MainViewMode) => {
    setMainViewMode(mode);
  };

  const [analysisResults, setAnalysisResults] = useState<any[]>(() => {
    try {
      const savedProject = localStorage.getItem('mineral-ai-current-project');
      if (!savedProject) {
        return [];
      }
      const parsedProject = JSON.parse(savedProject);
      if (!parsedProject?.id) {
        return [];
      }
      return loadCachedAnalyses(parsedProject.id);
    } catch (error) {
      console.warn('Failed to restore cached analyses:', error);
      return [];
    }
  });
  const [analysisLegendEntries, setAnalysisLegendEntries] = useState<AnalysisGridEntry[]>([]);
  const analysisResultsRef = useRef<any[]>(analysisResults);
  useEffect(() => {
    analysisResultsRef.current = analysisResults;
  }, [analysisResults]);


  const [polygonRegions, setPolygonRegions] = useState<PolygonRegion[]>(() => {
    try {
      const savedProject = localStorage.getItem('mineral-ai-current-project');
      if (!savedProject) {
        return [];
      }
      const parsedProject = JSON.parse(savedProject);
      if (!parsedProject?.id) {
        return [];
      }
      return loadCachedPolygons(parsedProject.id);
    } catch (error) {
      console.warn('Failed to restore cached polygon regions:', error);
      return [];
    }
  });
  const [selectedLegendAnalysisId, setSelectedLegendAnalysisId] = useState<string>('');
  const [gridResolutionBounds, setGridResolutionBounds] = useState<{ min: number; max: number }>({ min: 2, max: 50 });
  const [gridResolutionDefault, setGridResolutionDefault] = useState<number>(32);
  const [, setActiveGridResolution] = useState<number>(32);
  const [gridResolutionOverrides, setGridResolutionOverrides] = useState<Map<string, number>>(() => new Map());
  const [cacheRefreshTimestamps, setCacheRefreshTimestamps] = useState<Map<string, string>>(() => new Map());
  const [isRefreshingCacheKey, setIsRefreshingCacheKey] = useState<string | null>(null);

  const clampGridResolution = useCallback(
    (value: number) => Math.max(gridResolutionBounds.min, Math.min(gridResolutionBounds.max, value)),
    [gridResolutionBounds]
  );

  const makeContextKey = useCallback((projectId?: string | null, polygonId?: string | null) => {
    const normalizedProject = projectId && projectId.trim().length > 0 ? projectId.trim() : 'global';
    if (!polygonId || polygonId.trim().length === 0) {
      return projectId ? `project:${normalizedProject}` : 'global';
    }
    return `polygon:${normalizedProject}:${polygonId.trim()}`;
  }, []);

  const profileAsync = useCallback(
    async <T,>(label: string, task: () => Promise<T>): Promise<T> => {
      const start = performance.now();
      try {
        const result = await task();
        return result;
      } finally {
        const duration = performance.now() - start;
        console.log(`[Perf][ProjectSwitch] ${label} ${(duration / 1000).toFixed(2)}s`);
      }
    },
    []
  );

  useEffect(() => {
    try {
      const stored = localStorage.getItem('mineral-ai-grid-resolution-overrides');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          const restored = new Map<string, number>();
          Object.entries(parsed).forEach(([key, value]) => {
            if (typeof value === 'number' && Number.isFinite(value)) {
              restored.set(key, value);
            }
          });
          if (restored.size > 0) {
            setGridResolutionOverrides(restored);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to restore grid resolution overrides:', error);
    }
  }, []);

  useEffect(() => {
    try {
      const serializable = Object.fromEntries(gridResolutionOverrides.entries());
      localStorage.setItem('mineral-ai-grid-resolution-overrides', JSON.stringify(serializable));
    } catch (error) {
      console.warn('Failed to persist grid resolution overrides:', error);
    }
  }, [gridResolutionOverrides]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('mineral-ai-cache-refreshes');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          const restored = new Map<string, string>();
          Object.entries(parsed).forEach(([key, value]) => {
            if (typeof value === 'string') {
              restored.set(key, value);
            }
          });
          if (restored.size > 0) {
            setCacheRefreshTimestamps(restored);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to restore cache refresh timestamps:', error);
    }
  }, []);

  useEffect(() => {
    try {
      const serializable = Object.fromEntries(cacheRefreshTimestamps.entries());
      localStorage.setItem('mineral-ai-cache-refreshes', JSON.stringify(serializable));
    } catch (error) {
      console.warn('Failed to persist cache refresh timestamps:', error);
    }
  }, [cacheRefreshTimestamps]);

  const resolveGridResolution = useCallback(
    ({ projectId, polygonId }: { projectId?: string | null; polygonId?: string | null }) => {
      const polygonKey = polygonId ? makeContextKey(projectId, polygonId) : null;
      if (polygonKey && gridResolutionOverrides.has(polygonKey)) {
        return clampGridResolution(gridResolutionOverrides.get(polygonKey)!);
      }

      const projectKey = projectId ? makeContextKey(projectId, null) : null;
      if (projectKey && gridResolutionOverrides.has(projectKey)) {
        return clampGridResolution(gridResolutionOverrides.get(projectKey)!);
      }

      if (gridResolutionOverrides.has('global')) {
        return clampGridResolution(gridResolutionOverrides.get('global')!);
      }

      return clampGridResolution(gridResolutionDefault);
    },
    [gridResolutionOverrides, gridResolutionDefault, clampGridResolution, makeContextKey]
  );
useEffect(() => {
  if (!analysisLegendEntries.length) {
    setSelectedLegendAnalysisId('');
    return;
  }
  if (!selectedLegendAnalysisId || !analysisLegendEntries.some(entry => entry.analysisId === selectedLegendAnalysisId)) {
    setSelectedLegendAnalysisId(analysisLegendEntries[0].analysisId);
  }
}, [analysisLegendEntries, selectedLegendAnalysisId]);

useEffect(() => {
  if (!analysisLegendEntries.length) {
    return;
  }

  setMarkerVisibility((prev) => {
    let changed = false;
    const next = new globalThis.Map(prev);

    analysisLegendEntries.forEach((entry) => {
      const heatKey = `heatmap_${entry.analysisId}`;
      const legacyGridKey = `grid_${entry.analysisId}`;

      if (!next.has(heatKey)) {
        if (next.has(legacyGridKey)) {
          const legacyValue = next.get(legacyGridKey) ?? true;
          next.delete(legacyGridKey);
          next.set(heatKey, legacyValue);
        } else {
          next.set(heatKey, false);
        }
        changed = true;
      }
    });

    // Remove any leftover legacy grid keys once migrated
    Array.from(next.keys()).forEach((key) => {
      if (key.startsWith('grid_')) {
        next.delete(key);
        changed = true;
      }
    });

    if (!changed) {
      return prev;
    }

    try {
      const serializable = Array.from(next.entries());
      localStorage.setItem('mineral-ai-marker-visibility', JSON.stringify(serializable));
    } catch (error) {
      console.error('Failed to persist updated markerVisibility defaults:', error);
    }

    return next;
  });
}, [analysisLegendEntries]);

useEffect(() => {
  setGridSettings((prev) => {
    let changed = false;
    const next = new globalThis.Map(prev);

    analysisLegendEntries.forEach((entry) => {
      const analysisKey = entry.analysisId;
      if (!next.has(analysisKey)) {
        const commodityKey = (entry.commodity || '').toLowerCase();
        const metadataCommodityKey = (entry.metadata?.commodity || '').toLowerCase();
        const fallback =
          next.get(analysisKey) ||
          (entry.commodity ? next.get(entry.commodity) : undefined) ||
          (commodityKey ? next.get(commodityKey) : undefined) ||
          (metadataCommodityKey ? next.get(metadataCommodityKey) : undefined) ||
          DEFAULT_ANALYSIS_GRID_SETTINGS;
        next.set(analysisKey, sanitizeGridSettings(fallback));
        changed = true;
      }
    });

    if (!changed) {
      return prev;
    }

    try {
      const settingsObject = serializeGridSettings(next);
      localStorage.setItem('mineral-ai-grid-settings', JSON.stringify(settingsObject));
    } catch (error) {
      console.error('Failed to persist analysis grid settings:', error);
    }

    return next;
  });
}, [analysisLegendEntries]);

  const selectedAnalysisEntry = useMemo(
    () => analysisLegendEntries.find((entry) => entry.analysisId === selectedLegendAnalysisId) || null,
    [analysisLegendEntries, selectedLegendAnalysisId]
  );

useEffect(() => {
  if (!selectedAnalysisEntry) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent('analysis:select', {
      detail: { analysisId: selectedAnalysisEntry.analysisId, polygonId: selectedAnalysisEntry.polygonId }
    })
  );
}, [selectedAnalysisEntry]);

  const buildGridControlContext = useCallback(
    ({
      projectId,
      polygonId,
      polygonName,
    }: {
      projectId?: string | null;
      polygonId?: string | null;
      polygonName?: string | null;
    }): GridControlContext => {
      const resolvedProjectId = projectId ?? null;
      const resolvedPolygonId = polygonId ?? null;

      const key = makeContextKey(resolvedProjectId, resolvedPolygonId);
      const resolution = resolveGridResolution({ projectId: resolvedProjectId, polygonId: resolvedPolygonId });

      const projectName = (() => {
        if (!resolvedProjectId) {
          return null;
        }
        if (currentProject?.id === resolvedProjectId) {
          return currentProject.name || null;
        }
        const fallback = projects.find((project) => project.id === resolvedProjectId);
        return fallback?.name || null;
      })();

      let bounds: [number, number, number, number] | null = null;
      if (resolvedPolygonId) {
        const polygon = polygonRegions.find((region) => region.id === resolvedPolygonId);
        const b = polygon?.metadata?.bounds;
        if (b) {
          bounds = [b.min_lat, b.min_lon, b.max_lat, b.max_lon];
        }
        if (!bounds) {
          const legendMatch = analysisLegendEntries.find((entry) => entry.polygonId === resolvedPolygonId);
          if (legendMatch?.metadata?.bounds) {
            bounds = legendMatch.metadata.bounds as [number, number, number, number];
          }
        }
      }

      const labelParts: string[] = [];
      if (projectName) {
        labelParts.push(projectName);
      }
      if (polygonName) {
        labelParts.push(polygonName);
      }

      const label = labelParts.length
        ? labelParts.join(' • ')
        : resolvedProjectId
          ? 'Project default'
          : 'Global default';

      return {
        key,
        label,
        projectId: resolvedProjectId,
        projectName: projectName || null,
        polygonId: resolvedPolygonId,
        polygonName: polygonName || null,
        bounds,
        resolution,
        disabled: false,
        lastRefresh: cacheRefreshTimestamps.get(key) || null,
        isRefreshing: isRefreshingCacheKey === key,
      };
    }, [makeContextKey, resolveGridResolution, currentProject, projects, polygonRegions, analysisLegendEntries, cacheRefreshTimestamps, isRefreshingCacheKey]);

  const gridControlContext = useMemo<GridControlContext>(() => {
    if (selectedAnalysisEntry) {
      const projectId =
        selectedAnalysisEntry.projectId ||
        (selectedAnalysisEntry.metadata as any)?.project_id ||
        (selectedAnalysisEntry.metadata as any)?.projectId ||
        currentProject?.id ||
        null;
      const polygonId = selectedAnalysisEntry.polygonId || null;
      return buildGridControlContext({
        projectId,
        polygonId,
        polygonName: selectedAnalysisEntry.polygonName || null,
      });
    }

    if (currentProject?.id) {
      return buildGridControlContext({ projectId: currentProject.id, polygonId: null });
    }

    return buildGridControlContext({});
  }, [selectedAnalysisEntry, currentProject, buildGridControlContext]);

  useEffect(() => {
    setActiveGridResolution(gridControlContext.resolution);
  }, [gridControlContext.resolution]);

  const [selectedAnalysisForViewing, setSelectedAnalysisForViewing] = useState<any>(null);

  const updateCurrentProject = (project: Project | null) => {
    if (currentProject && mineralMapData) {
      setProjectMineralMaps(prev => {
        const updated = new globalThis.Map(prev);
        updated.set(currentProject.id, mineralMapData);
        return updated;
      });
    }

    currentProjectRef.current = project;
    setCurrentProject(project);

    if (selectedAnalysisForViewing) {
      setSelectedAnalysisForViewing(null);
    }

    if (analysisResults && analysisResults.length > 0) {
      setAnalysisResults([]);
    }

    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('forceMapRefresh', { detail: { newProjectId: project?.id } }));
    }, 100);

    if (project) {
      setProjectMineralMaps(currentMaps => {
        const savedMineralMap = currentMaps.get(project.id);

        if (savedMineralMap) {
          const restoredData = { ...savedMineralMap };
          setMineralMapData(restoredData);
          setIsNewMineralData(false);
        } else {
          setMineralMapData(null);
          setIsNewMineralData(false);
        }
        return currentMaps;
      });

      localStorage.setItem('mineral-ai-current-project', JSON.stringify(project));
      writeSharedProjectId(project.id);
    } else {
      setMineralMapData(null);
      setIsNewMineralData(false);
      localStorage.removeItem('mineral-ai-current-project');
      writeSharedProjectId(null);
    }
  };
  const updateCurrentProjectRef = useRef(updateCurrentProject);
  updateCurrentProjectRef.current = updateCurrentProject;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const serializeQaProject = (project: Project | null) =>
      project
        ? {
            id: project.id,
            name: project.name,
            location: project.location,
            status: project.status,
            project_type: project.project_type,
            target_minerals: project.target_minerals,
          }
        : null;

    const bridge = {
      version: '2026-05-24',
      getState: () => ({
        version: '2026-05-24',
        currentProject: serializeQaProject(currentProject),
        projectCount: projects.length,
      }),
      listProjects: () => projects.map((project) => serializeQaProject(project)),
      setProjectById: (projectId: string) => {
        const project = projects.find((candidate) => candidate.id === projectId);
        if (!project) {
          return {
            ok: false,
            error: `Project not found: ${projectId}`,
            state: {
              currentProject: serializeQaProject(currentProject),
              projectCount: projects.length,
            },
          };
        }
        updateCurrentProjectRef.current(project);
        return {
          ok: true,
          project: serializeQaProject(project),
        };
      },
    };

    (window as any).__geomorphicAppQa = bridge;
    window.dispatchEvent(new CustomEvent('geomorphic:qa-ready', {
      detail: { surface: 'app', version: bridge.version },
    }));

    return () => {
      if ((window as any).__geomorphicAppQa === bridge) {
        delete (window as any).__geomorphicAppQa;
      }
    };
  }, [currentProject, projects]);

  const [markerVisibility, setMarkerVisibility] = useState<globalThis.Map<string, boolean>>(() => {
    // Start with all analysis runs hidden on every page load/refresh
    return new globalThis.Map<string, boolean>();
  });

  // Grid settings state for threshold and opacity controls
  const [gridSettings, setGridSettings] = useState<globalThis.Map<string, GridSettings>>(() => {
    try {
      // Try to load from localStorage first
      const saved = localStorage.getItem('mineral-ai-grid-settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        const restored = deserializeGridSettings(parsed);
        if (restored.size > 0) {
          console.log('🔄 Loaded grid settings from localStorage:', parsed);
          return restored;
        }
      }
    } catch (error) {
      console.warn('Failed to load grid settings from localStorage:', error);
    }

    // Default settings if nothing saved
    const defaultSettings = new globalThis.Map<string, GridSettings>();
    ['copper', 'silver', 'gold'].forEach((mineral) => {
      defaultSettings.set(mineral, sanitizeGridSettings(DEFAULT_ANALYSIS_GRID_SETTINGS));
    });
    return defaultSettings;
  });

const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    try {
      const saved = localStorage.getItem('mineral-ai-left-panel-width');
      return saved ? parseInt(saved) : DEFAULT_LEFT_PANEL_WIDTH;
    } catch {
      return DEFAULT_LEFT_PANEL_WIDTH;
    }
  });
  const [terminalCollapsed, setTerminalCollapsed] = useState(true);
  const [mlOutputsCollapsed, setMlOutputsCollapsed] = useState(false);
  const [mlLayers, setMlLayers] = useState<Record<string, boolean>>({
    prospectivity_raw: false,
    wofe_baseline: false,
    top_clusters: false,
    polygon_scores: false,
  });
  const [mlOpacity, setMlOpacity] = useState(0.7);
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    try {
      const saved = localStorage.getItem('mineral-ai-right-panel-width');
      return saved ? parseInt(saved) : DEFAULT_RIGHT_PANEL_WIDTH;
    } catch {
      return DEFAULT_RIGHT_PANEL_WIDTH;
    }
  });
  const leftPanelWidthRef = useRef(leftPanelWidth);
  const rightPanelWidthRef = useRef(rightPanelWidth);
  const panelPersistTimeoutRef = useRef<number | null>(null);
  const persistPanelWidths = useCallback((leftWidth: number, rightWidth: number) => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem('mineral-ai-left-panel-width', leftWidth.toString());
    localStorage.setItem('mineral-ai-right-panel-width', rightWidth.toString());
  }, []);

  useEffect(() => {
    leftPanelWidthRef.current = leftPanelWidth;
  }, [leftPanelWidth]);

  useEffect(() => {
    rightPanelWidthRef.current = rightPanelWidth;
  }, [rightPanelWidth]);

  const schedulePanelWidthsPersist = useCallback((leftWidth: number, rightWidth: number) => {
    if (typeof window === 'undefined') {
      return;
    }
    if (panelPersistTimeoutRef.current !== null) {
      window.clearTimeout(panelPersistTimeoutRef.current);
    }
    panelPersistTimeoutRef.current = window.setTimeout(() => {
      persistPanelWidths(leftWidth, rightWidth);
      panelPersistTimeoutRef.current = null;
    }, PANEL_PERSIST_DEBOUNCE_MS);
  }, [persistPanelWidths]);
  
  // Dialog states
  const [newProjectDialog, setNewProjectDialog] = useState(false);
  const [fieldProgramDialog, setFieldProgramDialog] = useState(false);
  const [reportDialog, setReportDialog] = useState(false);
  const [editProjectDialog, setEditProjectDialog] = useState(false);

  // New project form data
  const [newProjectForm, setNewProjectForm] = useState({
    name: '',
    location: '',
    target_minerals: 'copper',
    coordinates: { lat: '', lng: '' },
    useManualCoordinates: false
  });
  
  // Edit project form data
  const [editProjectForm, setEditProjectForm] = useState({
    id: '',
    name: '',
    location: '',
    target_minerals: '',
    project_type: 'exploration',
    coordinates: { lat: '', lng: '' },
    useManualCoordinates: false
  });
  
  // Search functionality
  
  // Mineral mapping - store per project
  const [mineralMapData, setMineralMapData] = useState<any>(null);
  const [, setProjectMineralMaps] = useState<globalThis.Map<string, any>>(new globalThis.Map());
  const [isNewMineralData, setIsNewMineralData] = useState(false);
  const [, setSavedMineralAnalyses] = useState<any[]>([]);
  
  // Project data
  const [, setFieldPrograms] = useState<FieldProgram[]>([]);
  const [, setTechnicalReports] = useState<TechnicalReport[]>([]);
  const projectDataCacheRef = useRef<Map<string, { updatedAt: string | null; fieldPrograms: FieldProgram[]; reports: TechnicalReport[] }>>(new Map());
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentContents, setDocumentContents] = useState<Map<string, string>>(new Map());
  const [, setProjectDocuments] = useState<any[]>([]);
  const [selectedDocumentForPreview] = useState<any>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesFetchRef = useRef<Map<string, Promise<any>>>(new Map());
  const polygonFetchRef = useRef<Map<string, Promise<any>>>(new Map());
  const projectDataFetchRef = useRef<Map<string, Promise<any>>>(new Map());
  const currentProjectRef = useRef<Project | null>(currentProject);
  currentProjectRef.current = currentProject;
  // Monotonic token for project switches: async continuations (project fetch in
  // activateProject, background loadProjects refreshes) compare against it and
  // bail when a newer switch has superseded them.
  const projectSwitchSeqRef = useRef(0);
  const loadProjectsRef = useRef<((options?: { preserveActiveId?: string | null }) => Promise<{ projects: Project[]; activeProject: Project | null }>) | null>(null);
  const loadPolygonRegionsRef = useRef<((projectId: string, options?: { force?: boolean }) => Promise<any>) | null>(null);
  const loadAllBackendAnalysesRef = useRef<((projectId: string, options?: { force?: boolean; signal?: AbortSignal }) => Promise<any>) | null>(null);
  const fetchMessagesRef = useRef<((options?: { force?: boolean }) => Promise<any>) | null>(null);
  const loadProjectDataRef = useRef<((options?: { force?: boolean }) => Promise<any>) | null>(null);
  const loadProjectDocumentContentsRef = useRef<(() => void) | null>(null);
  const profileAsyncRef = useRef(profileAsync);
  profileAsyncRef.current = profileAsync;
  const API_BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const [backendLiteMode, setBackendLiteMode] = useState(false);

  // Detect if the backend is running in lite mode (server_lite.py).
  // Lite mode doesn't serve projects, analyses, polygons, messages, etc.
  useEffect(() => {
    checkBackendMode().then((result) => {
      if (result?.mode === 'lite') {
        console.error(
          '[Backend] Running in LITE mode — project management, analysis, and polygon routes are unavailable. ' +
          'Deploy main.py or point REACT_APP_API_BASE at a full backend instance.'
        );
        setBackendLiteMode(true);
      }
    });
  }, []);

  useEffect(() => {
    const fetchGridConfig = async () => {
      try {
        const response = await apiRequest('/api/configuration/summary');
        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const grid = data?.grid;
        if (grid && typeof grid === 'object') {
          const min = typeof grid.min_grid_resolution === 'number' ? grid.min_grid_resolution : 2;
          const max = typeof grid.max_grid_resolution === 'number' ? grid.max_grid_resolution : 50;
          setGridResolutionBounds({ min, max });

          if (typeof grid.default_grid_resolution === 'number') {
            const clamped = Math.max(min, Math.min(max, grid.default_grid_resolution));
            setGridResolutionDefault(clamped);
            setActiveGridResolution(clamped);
          }
        }
      } catch (error) {
        console.error('Failed to load grid configuration summary:', error);
      }
    };

    fetchGridConfig();
  }, []);

  useEffect(() => {
    setActiveGridResolution((prev) => clampGridResolution(prev));
  }, [clampGridResolution]);

  // Phase 4: Request deduplication/caching
  const requestCacheRef = useRef<Map<string, { data: any; timestamp: number; promise?: Promise<any> }>>(new Map());
  const CACHE_TTL_MS = 5000; // 5 seconds cache TTL for debugging dates

  // DEBUG: Clear cache for Tassa project to force fresh data with date fix
  const clearCacheRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (currentProject?.id === 'proj_1756434030') {
            requestCacheRef.current.clear();
      // Store the reload function to call after loadAllBackendAnalyses is defined
      if (clearCacheRef.current) {
        clearCacheRef.current();
      }
    }
  }, [currentProject?.id]);

  const getCachedRequest = useCallback((key: string) => {
    const cached = requestCacheRef.current.get(key);
    if (!cached) return null;
    
    const isExpired = Date.now() - cached.timestamp > CACHE_TTL_MS;
    if (isExpired) {
      requestCacheRef.current.delete(key);
      return null;
    }
    return cached;
  }, [CACHE_TTL_MS]);

  const setCachedRequest = useCallback((key: string, data: any, promise?: Promise<any>) => {
    requestCacheRef.current.set(key, {
      data,
      timestamp: Date.now(),
      promise
    });
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize app - projects will be loaded from cache and then refreshed from server
                // Load projects first - this is critical and shouldn't fail due to other errors
        let bootstrapProjectId: string | null = null;
        try {
          const loadProjects = loadProjectsRef.current;
          if (loadProjects) {
            const { projects: loadedProjects, activeProject } = await loadProjects();
            bootstrapProjectId =
              activeProject?.id ||
              currentProjectRef.current?.id ||
              loadedProjects[0]?.id ||
              null;
          }
        } catch (error) {
          console.error('❌ Failed to load projects:', error);
        }

        if (!bootstrapProjectId) {
          try {
            const savedProject = localStorage.getItem('mineral-ai-current-project');
            if (savedProject) {
              const parsed = JSON.parse(savedProject);
              if (parsed?.id) {
                bootstrapProjectId = parsed.id;
              }
            }
          } catch (error) {
            console.warn('Failed to read cached current project for bootstrap:', error);
          }
        }

        if (bootstrapProjectId) {
          const tasks: Array<Promise<unknown>> = [];
          if (loadPolygonRegionsRef.current) {
            tasks.push(loadPolygonRegionsRef.current(bootstrapProjectId, { force: true }));
          }
          if (loadAllBackendAnalysesRef.current) {
            tasks.push(loadAllBackendAnalysesRef.current(bootstrapProjectId, { force: true }));
          }
          if (fetchMessagesRef.current) {
            tasks.push(fetchMessagesRef.current({ force: true }));
          }
          if (tasks.length > 0) {
            await Promise.allSettled(tasks);
          }
        }

        // Start polling for service status
        const checkServiceStatus = async () => {
          try {
            const response = await axios.get(`${API_BASE}/api/services/status`);
            setServiceStatus(response.data);
          } catch (error) {
            console.error('Failed to check service status:', error);
          }
        };
        
        checkServiceStatus();
        const statusInterval = setInterval(checkServiceStatus, 30000); // Reduced frequency: 10s → 30s

        // Store cleanup function
        (window as any).serviceStatusInterval = statusInterval;
        
        // Load saved mineral analyses with cleanup
        try {
          const saved = JSON.parse(localStorage.getItem('saved-mineral-analyses') || '[]');
          
          // Cleanup old analyses on load (keep only last 20)
          if (saved.length > 20) {
                        const recent = saved
              .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
              .slice(0, 20);
            
            localStorage.setItem('saved-mineral-analyses', JSON.stringify(recent));
            setSavedMineralAnalyses(recent);
          } else {
            setSavedMineralAnalyses(saved);
          }
        } catch (error) {
          console.error('Failed to parse saved mineral analyses:', error);
          // Clear corrupted data
          localStorage.removeItem('saved-mineral-analyses');
        }
        
        // Only show initial messages if no project is active after loading
        // This prevents overwriting project-specific messages
        setTimeout(() => {
          if (!currentProjectRef.current) {
            setMessages([
              {
                timestamp: new Date().toISOString(),
                message_type: 'system',
                content: '> Geomorphic AI Platform initialized',
              },
              {
                timestamp: new Date().toISOString(),
                message_type: 'system',
                content: '> Enterprise mineral exploration system ready',
              }
            ]);
          }
        }, 100); // Small delay to ensure project activation completes
      } catch (error) {
        console.error('Failed to initialize app:', error);
      }
    };
    
    initializeApp();
    
    return () => {
      // Clean up interval on unmount
      if ((window as any).serviceStatusInterval) {
        clearInterval((window as any).serviceStatusInterval);
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

 // eslint-disable-line react-hooks/exhaustive-deps

  // Sync projectDocuments when documents change
  useEffect(() => {
    setProjectDocuments(documents);
  }, [documents]);

  const fetchMessages = useCallback(async (options?: { force?: boolean }) => {
    if (!currentProject) {
      return;
    }

    const projectId = currentProject.id;
    const projectName = currentProject.name;
    const force = options?.force ?? false;

    let storedMessages: any[] = [];
    try {
      const storedData = localStorage.getItem(`mineral-ai-messages-${projectId}`);
      if (storedData) {
        storedMessages = JSON.parse(storedData);
      }
    } catch (error) {
      console.error('Failed to load persisted messages:', error);
    }

    const inFlight = messagesFetchRef.current.get(projectId);
    if (inFlight && !force) {
      return inFlight;
    }
    if (force && inFlight) {
      messagesFetchRef.current.delete(projectId);
    }

    const request = (async () => {
      let backendMessages: any[] = [];
      try {
        const response = await axios.get(`${API_BASE}/api/messages`);
        backendMessages = response.data.messages || [];
      } catch (error) {
        console.error('Failed to fetch backend messages:', error);
        if (storedMessages.length > 0) {
          if (currentProjectRef.current?.id === projectId) {
            setMessages(storedMessages);
          }
          return;
        }
      }

      const mergedMessages = [...storedMessages];
      backendMessages.forEach((backendMsg) => {
        const exists = storedMessages.some(
          (storedMsg) =>
            storedMsg.timestamp === backendMsg.timestamp && storedMsg.content === backendMsg.content
        );
        if (!exists) {
          mergedMessages.push(backendMsg);
        }
      });

      mergedMessages.sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      if (currentProjectRef.current?.id !== projectId) {
        // Project changed while fetching — don't apply another project's messages.
        return;
      }

      setMessages(mergedMessages);

      const analysisData = mergedMessages
        .filter((msg: any) => msg.message_type === 'data_output' && msg.data)
        .map((msg: any) =>
          normalizeAnalysisResult(msg.data, {
            projectId,
            projectName: projectName || null
          })
        );

      let nextAnalysisState: any[] = [];
      setAnalysisResults((prev) => {
        nextAnalysisState = mergeProjectAnalyses(prev, projectId, analysisData);
        return nextAnalysisState;
      });

      if (projectId) {
        const projectScoped = nextAnalysisState.filter((item) => item?.project_id === projectId);
        persistProjectAnalyses(projectId, projectScoped);
      }

      try {
        localStorage.setItem(
          `mineral-ai-messages-${projectId}`,
          JSON.stringify(mergedMessages)
        );
      } catch (error) {
        console.error('Failed to persist messages:', error);
      }
    })();

    messagesFetchRef.current.set(projectId, request);
    request.finally(() => {
      messagesFetchRef.current.delete(projectId);
    });

    return request;
  }, [currentProject]);
  fetchMessagesRef.current = fetchMessages;

  // Event-driven message updates - no continuous polling
  useEffect(() => {
    if (!currentProject) return;
    
    // Listen for manual refresh triggers
    const handleMessageRefresh = () => {
      fetchMessages({ force: true });
    };
    
    const handleAnalysisStart = () => {
      fetchMessages({ force: true });
    };
    
    const handleAnalysisComplete = () => {
      fetchMessages({ force: true });
    };
    
    window.addEventListener('refreshMessages', handleMessageRefresh);
    window.addEventListener('analysisStarted', handleAnalysisStart);
    window.addEventListener('analysisCompleted', handleAnalysisComplete);
    
    return () => {
            window.removeEventListener('refreshMessages', handleMessageRefresh);
      window.removeEventListener('analysisStarted', handleAnalysisStart);
      window.removeEventListener('analysisCompleted', handleAnalysisComplete);
    };
  }, [currentProject, fetchMessages]);

  const loadProjectDocumentContents = () => {
    if (!currentProject) return;
    
    try {
      const storedContents = localStorage.getItem(`mineral-ai-document-contents-${currentProject.id}`);
      if (storedContents) {
        const parsedContents = JSON.parse(storedContents);
        const contentsMap = new Map<string, string>(Object.entries(parsedContents));
        setDocumentContents(contentsMap);
      } else {
        setDocumentContents(new Map());
      }
    } catch (error) {
      console.error('Failed to load document contents:', error);
      setDocumentContents(new Map());
    }
  };
  loadProjectDocumentContentsRef.current = loadProjectDocumentContents;
  useEffect(() => {
    // Create AbortController to cancel requests when switching projects
    const abortController = new AbortController();
    const loadProjectDocumentContents = loadProjectDocumentContentsRef.current;
    const loadProjectData = loadProjectDataRef.current;
    const loadPolygonRegions = loadPolygonRegionsRef.current;
    const fetchMessages = fetchMessagesRef.current;
    const loadAllBackendAnalyses = loadAllBackendAnalysesRef.current;
    const profileAsync = profileAsyncRef.current;

    if (currentProject?.id) {
      const cachedPolygons = loadCachedPolygons(currentProject.id);
      setPolygonRegions(cachedPolygons);

      const cachedAnalyses = loadCachedAnalyses(currentProject.id);
      setAnalysisResults(cachedAnalyses);

      // Clear accumulated marker visibility state for previous projects/analyses
      setMarkerVisibility(prev => {
        const currentEntries = Array.from(prev.entries());

        // Keep only generic rank markers and current project-specific entries
        const cleanedMap = new globalThis.Map<string, boolean>();
        currentEntries.forEach(([key, value]) => {
          // Keep generic rank markers (rank_1, rank_2, etc.) - these are reusable
          if (key.startsWith('rank_') && !key.includes('dual_analysis_')) {
            cleanedMap.set(key, value);
          }
          // Keep grid visibility settings
          else if (key.startsWith('heatmap_')) {
            cleanedMap.set(key, value);
          }
          // Remove all analysis-specific markers - they'll be recreated for current project
        });

        // Persist the cleaned state
        try {
          const serializable = Array.from(cleanedMap.entries());
          localStorage.setItem('mineral-ai-marker-visibility', JSON.stringify(serializable));
        } catch (error) {
          console.error('Failed to persist cleaned markerVisibility:', error);
        }

        return cleanedMap;
      });

      loadProjectDocumentContents?.();

      const loaders: Array<Promise<unknown>> = [];

      if (loadProjectData) {
        loaders.push(profileAsync('loadProjectData', () => loadProjectData()));
      }
      if (loadPolygonRegions) {
        loaders.push(profileAsync('loadPolygonRegions', () => loadPolygonRegions(currentProject.id)));
      }
      if (fetchMessages) {
        loaders.push(profileAsync('fetchMessages', () => fetchMessages()));
      }

      const existingAnalyses = analysisResultsRef.current.filter(
        (result) => result?.project_id === currentProject.id
      );
      if (existingAnalyses.length === 0 && loadAllBackendAnalyses) {
        loaders.push(profileAsync('loadAllBackendAnalyses', () => loadAllBackendAnalyses(currentProject.id, { signal: abortController.signal })));
      }

      Promise.allSettled(loaders).catch((error) => {
        // Ignore abort errors - they're expected when switching projects
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Project data preload failed', error);
        }
      });
    } else {
      // Clear polygons when no project is selected
      setPolygonRegions([]);
      setAnalysisResults([]);
    }

    // Cleanup: abort any in-flight requests when project changes or component unmounts
    return () => {
      abortController.abort();
    };
  }, [currentProject]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // =================== PROJECT MANAGEMENT ===================

  // Helper function to get coordinates from project boundary
  const getCoordinatesFromBoundary = (projectBoundary: any): { lat: number; lng: number } | null => {
    try {
      if (!projectBoundary || !projectBoundary.features || projectBoundary.features.length === 0) {
        return null;
      }
      
      const feature = projectBoundary.features[0];
      if (!feature.geometry || !feature.geometry.coordinates) {
        return null;
      }
      
      // Get the first coordinate from the boundary (usually the center or first vertex)
      const coords = feature.geometry.coordinates;
      if (feature.geometry.type === 'Polygon' && coords[0] && coords[0].length > 0) {
        const [lng, lat] = coords[0][0]; // GeoJSON format is [longitude, latitude]
        return { lat, lng };
      } else if (feature.geometry.type === 'Point') {
        const [lng, lat] = coords;
        return { lat, lng };
      }
      
      return null;
    } catch (error) {
      console.error('Error extracting coordinates from boundary:', error);
      return null;
    }
  };

  // Helper function to get coordinates based on location
  const getLocationCoordinates = (location: string): { lat: number; lng: number } => {
    const locationLower = location.toLowerCase();
    
    // Specific cities first
    if (locationLower.includes('denver')) return { lat: 39.7392, lng: -104.9903 };
    if (locationLower.includes('phoenix')) return { lat: 33.4484, lng: -112.0740 };
    if (locationLower.includes('salt lake')) return { lat: 40.7608, lng: -111.8910 };
    if (locationLower.includes('las vegas')) return { lat: 36.1699, lng: -115.1398 };
    if (locationLower.includes('reno')) return { lat: 39.5296, lng: -119.8138 };
    
    // Common mining regions
    if (locationLower.includes('nevada')) return { lat: 39.8283, lng: -119.8163 };
    if (locationLower.includes('arizona')) return { lat: 34.0489, lng: -111.0937 };
    if (locationLower.includes('colorado')) return { lat: 39.5501, lng: -105.7821 };
    if (locationLower.includes('utah')) return { lat: 39.3210, lng: -111.0937 };
    if (locationLower.includes('chile')) return { lat: -30.0000, lng: -71.0000 };
    if (locationLower.includes('peru')) return { lat: -9.1900, lng: -75.0152 };
    if (locationLower.includes('australia')) return { lat: -25.2744, lng: 133.7751 };
    if (locationLower.includes('zambia')) return { lat: -12.5, lng: 28.2 };
    if (locationLower.includes('canada')) return { lat: 56.1304, lng: -106.3468 };
    if (locationLower.includes('mexico')) return { lat: 23.6345, lng: -102.5528 };
    if (locationLower.includes('alaska')) return { lat: 64.2008, lng: -149.4937 };
    
    // Default to southwestern US (good for copper/gold)
    return { lat: 37.628, lng: -112.074 };
  };

  // Helper function to populate coordinates from boundary
  const populateCoordinatesFromBoundary = (isNewProject: boolean = false) => {
    const project = isNewProject ? null : projects.find(p => p.id === editProjectForm.id);
    if (!project || !project.project_boundary) {
      addMessage('system', '> No project boundary found to extract coordinates from');
      return;
    }
    
    const boundaryCoords = getCoordinatesFromBoundary(project.project_boundary);
    if (boundaryCoords) {
      if (isNewProject) {
        setNewProjectForm({
          ...newProjectForm,
          coordinates: {
            lat: boundaryCoords.lat.toString(),
            lng: boundaryCoords.lng.toString()
          },
          useManualCoordinates: true
        });
      } else {
        setEditProjectForm({
          ...editProjectForm,
          coordinates: {
            lat: boundaryCoords.lat.toString(),
            lng: boundaryCoords.lng.toString()
          },
          useManualCoordinates: true
        });
      }
      addMessage('system', `> Coordinates populated from boundary: ${boundaryCoords.lat.toFixed(6)}, ${boundaryCoords.lng.toFixed(6)}`);
    } else {
      addMessage('system', '> Could not extract coordinates from project boundary');
    }
  };

  const loadProjects = async (options?: { preserveActiveId?: string | null }): Promise<{ projects: Project[]; activeProject: Project | null }> => {
    try {
      const response = await axios.get(`${API_BASE}/api/projects`);
      const fetchedProjects: Project[] = ensureSwedenProject(response.data?.projects ?? []);

      if (fetchedProjects.length === 0) {
        const cached = localStorage.getItem('mineral-ai-projects');
        if (cached) {
          try {
            const cachedProjects: Project[] = JSON.parse(cached);
            setProjects(cachedProjects);
            const sharedProjectId = readSharedProjectId();
            const desiredActiveId = options?.preserveActiveId ?? sharedProjectId ?? currentProjectRef.current?.id ?? null;
            const cachedActive =
              cachedProjects.find((project) => project.id === desiredActiveId) ||
              (desiredActiveId ? findDefaultProject(desiredActiveId) : null) ||
              cachedProjects[0] ||
              null;
            if (cachedActive) {
              updateCurrentProject(cachedActive);
            }
            return { projects: cachedProjects, activeProject: cachedActive };
          } catch (cacheError) {
            console.error('❌ Failed to parse cached projects:', cacheError);
          }
        }

        setProjects([]);
        updateCurrentProject(null);
        return { projects: [], activeProject: null };
      }

      const projectsWithCoords = fetchedProjects.map((project: Project) => {
        if (!project.coordinates || (project.coordinates.lat === 37.628 && project.coordinates.lng === -112.074)) {
          project.coordinates = getLocationCoordinates(project.location);
        }
        return project;
      });

      const withSweden = ensureSwedenProject(projectsWithCoords);
      setProjects(withSweden);
      persistProjects(withSweden);

      let activeProject: Project | null = null;
      const sharedProjectId = readSharedProjectId();
      const desiredActiveId = options?.preserveActiveId ?? sharedProjectId ?? currentProjectRef.current?.id ?? null;

      const ensureProjectCoords = (project: Project): Project => {
        if (!project.coordinates || (project.coordinates.lat === 37.628 && project.coordinates.lng === -112.074)) {
          const generatedCoords = getLocationCoordinates(project.location || '');
          return { ...project, coordinates: generatedCoords };
        }
        return project;
      };

      const silentlyRefreshCurrentProject = (project: Project) => {
        currentProjectRef.current = project;
        writeSharedProjectId(project.id);
        setCurrentProject(prev => {
          if (!prev) {
            localStorage.setItem('mineral-ai-current-project', JSON.stringify(project));
            return project;
          }
          if (prev.id !== project.id) {
            localStorage.setItem('mineral-ai-current-project', JSON.stringify(project));
            return project;
          }

          const prevSerialized = JSON.stringify(prev);
          const nextSerialized = JSON.stringify(project);
          if (prevSerialized === nextSerialized) {
            return prev;
          }

          const merged = { ...prev, ...project };
          localStorage.setItem('mineral-ai-current-project', JSON.stringify(merged));
          return merged;
        });
      };

      if (desiredActiveId) {
        const matchingProject = withSweden.find((project) => project.id === desiredActiveId) || null;
        if (matchingProject) {
          const normalizedProject = ensureProjectCoords(matchingProject);
          activeProject = normalizedProject;
          if (!currentProjectRef.current) {
            updateCurrentProject(normalizedProject);
          } else if (currentProjectRef.current.id === matchingProject.id) {
            silentlyRefreshCurrentProject(normalizedProject);
          } else {
            // The user switched to a different project while this refresh was in
            // flight — never force the selection back to a stale preserveActiveId.
            activeProject = currentProjectRef.current;
          }
        } else {
          // Preserve existing selection; do not force-switch when the project list is in flux
          activeProject = currentProjectRef.current && desiredActiveId === currentProjectRef.current.id ? currentProjectRef.current : null;
        }
      } else {
        const firstProject = withSweden[0] || null;
        if (firstProject) {
          const normalizedFirst = ensureProjectCoords(firstProject);
          activeProject = normalizedFirst;
          updateCurrentProject(normalizedFirst);
          try {
            await axios.post(`${API_BASE}/api/projects/${normalizedFirst.id}/activate`);
          } catch (error) {
            console.error('Failed to activate first project via API:', error);
          }
        }
      }

      return { projects: withSweden, activeProject };
    } catch (error) {
      console.error('Failed to load projects:', error);
      addMessage('system', '> Failed to load projects');
      const fallback = ensureSwedenProject([]);
      const sharedProjectId = readSharedProjectId();
      const desiredActiveId = options?.preserveActiveId ?? sharedProjectId ?? currentProjectRef.current?.id ?? null;
      const activeFallback =
        (desiredActiveId ? fallback.find((project) => project.id === desiredActiveId) : null) ||
        (desiredActiveId ? findDefaultProject(desiredActiveId) : null) ||
        currentProjectRef.current ||
        fallback[0] ||
        null;
      setProjects(fallback);
      if (!currentProjectRef.current || currentProjectRef.current.id === activeFallback?.id) {
        updateCurrentProject(activeFallback);
        return { projects: fallback, activeProject: activeFallback };
      }
      // A different project was selected while this load was in flight — keep it.
      return { projects: fallback, activeProject: currentProjectRef.current };
    }
  };
  loadProjectsRef.current = loadProjects;

  const createProject = async () => {
    try {
      // Use manual coordinates if provided, otherwise generate from location
      let finalCoords;
      if (newProjectForm.useManualCoordinates && newProjectForm.coordinates.lat && newProjectForm.coordinates.lng) {
        finalCoords = { 
          lat: parseFloat(newProjectForm.coordinates.lat), 
          lng: parseFloat(newProjectForm.coordinates.lng) 
        };
              } else {
        finalCoords = getLocationCoordinates(newProjectForm.location);
              }
      
      const projectData = {
        name: newProjectForm.name,
        description: `Exploration project in ${newProjectForm.location}`,
        location: newProjectForm.location,
        coordinates: finalCoords,
        target_minerals: newProjectForm.target_minerals.split(',').map(m => m.trim()),
        project_type: 'exploration'
      };
      
            const response = await axios.post(`${API_BASE}/api/projects`, projectData);
            // Set the new project as current immediately
      const newProject = response.data;
      updateCurrentProject(newProject);
      
      // Reload all projects to get the updated list
      await loadProjects({ preserveActiveId: newProject.id });
      
      // Close the dialog and reset form
      setNewProjectDialog(false);
      setNewProjectForm({ name: '', location: '', target_minerals: 'copper', coordinates: { lat: '', lng: '' }, useManualCoordinates: false });
      addMessage('system', `> Project '${newProject.name}' created and activated`);
    } catch (error) {
      console.error('Failed to create project:', error);
      addMessage('system', '> Failed to create project');
    }
  };

  const activateProject = async (projectId: string) => {
    if (!projectId) {
      return;
    }

    console.info('[Project] Activating project', projectId);
    const switchSeq = ++projectSwitchSeqRef.current;

    const ensureCoordinates = (project: Project): Project => {
      if (!project.coordinates || (project.coordinates.lat === 37.628 && project.coordinates.lng === -112.074)) {
        const generatedCoords = getLocationCoordinates(project.location || '');
        return {
          ...project,
          coordinates: generatedCoords
        };
      }
      return project;
    };

    let resolvedProject: Project | null =
      projects.find((p) => p.id === projectId) || null;

    if (!resolvedProject) {
      try {
        const response = await axios.get(`${API_BASE}/api/projects/${projectId}`);
        const projectData: Project | null =
          response.data?.project ||
          response.data?.data ||
          response.data ||
          null;
        if (projectData) {
          resolvedProject = ensureCoordinates(projectData);
          setProjects((prev) => {
            const next = prev.filter((p) => p.id !== projectId);
            next.push(resolvedProject as Project);
            return next.sort((a, b) => a.name.localeCompare(b.name));
          });
        }
      } catch (fetchError) {
        console.error('❌ Failed to fetch project details for activation:', fetchError);
      }
    } else {
      resolvedProject = ensureCoordinates({ ...resolvedProject });
    }

    if (!resolvedProject) {
      console.error('❌ Project not found locally for activation:', projectId);
      addMessage('system', `> Could not activate project ${projectId} — project not found`);
      return;
    }

    if (switchSeq !== projectSwitchSeqRef.current) {
      // A newer switch happened while we were fetching this project — don't
      // clobber it with the older selection.
      return;
    }

    updateCurrentProject(resolvedProject);

    // Static country projects are frontend-defined and may not exist in the
    // backend project table. Keep switching instant; persist server state only
    // when the API supports the selected project.
    axios.post(`${API_BASE}/api/projects/${projectId}/activate`).catch((error) => {
      console.error('Failed to activate project via API:', error);
    });

    // Trigger background refresh of full project list (non-blocking)
    loadProjects({ preserveActiveId: projectId }).catch((error) => {
      console.error('Failed to refresh projects after activation:', error);
    });
  };

  const handleDeleteProject = async (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    if (window.confirm(`Are you sure you want to delete project "${project.name}"? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API_BASE}/api/projects/${projectId}`);
        await loadProjects();
        
        // If we deleted the current project, switch to another one
        if (currentProject?.id === projectId) {
          const remainingProjects = projects.filter(p => p.id !== projectId);
          if (remainingProjects.length > 0) {
            await activateProject(remainingProjects[0].id);
          } else {
            updateCurrentProject(null);
          }
        }
        
        addMessage('system', `> Project '${project.name}' deleted successfully`);
      } catch (error: any) {
        console.error('❌ Failed to delete project:', error);
        console.error('❌ Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        });
        
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        addMessage('system', `> Failed to delete project: ${errorMsg}`);
      }
    }
  };

  const handleUpdateProject = async () => {
    try {
      // Use manual coordinates if provided, otherwise generate from location
      let finalCoords;
      if (editProjectForm.useManualCoordinates && editProjectForm.coordinates.lat && editProjectForm.coordinates.lng) {
        finalCoords = { 
          lat: parseFloat(editProjectForm.coordinates.lat), 
          lng: parseFloat(editProjectForm.coordinates.lng) 
        };
              } else {
        finalCoords = getLocationCoordinates(editProjectForm.location);
              }
      
      const projectData = {
        name: editProjectForm.name,
        location: editProjectForm.location,
        coordinates: finalCoords,
        target_minerals: editProjectForm.target_minerals.split(',').map(m => m.trim()),
        project_type: editProjectForm.project_type || 'exploration'
      };

      await axios.put(`${API_BASE}/api/projects/${editProjectForm.id}`, projectData);
      await loadProjects({ preserveActiveId: editProjectForm.id });
      
      // Update current project if we edited the active one
      if (currentProject?.id === editProjectForm.id) {
        const updatedProject = projects.find(p => p.id === editProjectForm.id);
        if (updatedProject) {
          updateCurrentProject(updatedProject);
        }
      }
      
      setEditProjectDialog(false);
      addMessage('system', `> Project '${editProjectForm.name}' updated successfully`);
    } catch (error) {
      console.error('Failed to update project:', error);
      addMessage('system', '> Failed to update project');
    }
  };

  const loadProjectData = async (options?: { force?: boolean }) => {
    if (!currentProject || !currentProject.id) {
            return;
    }

    const projectId = currentProject.id;
    const projectVersion = currentProject.updated_at || null;
    const cacheKey = projectId;
    const cached = projectDataCacheRef.current.get(cacheKey);

    if (cached && !options?.force) {
      setFieldPrograms(cached.fieldPrograms);
      setTechnicalReports(cached.reports);

      if (cached.updatedAt === projectVersion) {
        return;
      }
    }
    
    try {
      const inFlight = projectDataFetchRef.current.get(projectId);
      if (inFlight && !options?.force) {
        await inFlight;
        return;
      }
      if (options?.force && inFlight) {
        projectDataFetchRef.current.delete(projectId);
      }

      const request = Promise.allSettled([
        axios.get(`${API_BASE}/api/projects/${projectId}/field-programs`),
        axios.get(`${API_BASE}/api/projects/${projectId}/reports`)
      ]);
      projectDataFetchRef.current.set(projectId, request);
      const [fpResult, reportsResult] = await request;
      // The project may have changed while the fetch was in flight; still cache
      // the data (keyed by projectId), but don't apply it to visible state.
      const stillCurrent = currentProjectRef.current?.id === projectId;

      if (fpResult.status === 'fulfilled') {
        const nextFieldPrograms = fpResult.value.data.field_programs || [];
        if (stillCurrent) {
          setFieldPrograms(nextFieldPrograms);
        }

        const cachedEntry = projectDataCacheRef.current.get(cacheKey) || { updatedAt: null, fieldPrograms: [], reports: [] };
        cachedEntry.fieldPrograms = nextFieldPrograms;
        cachedEntry.updatedAt = projectVersion;
        projectDataCacheRef.current.set(cacheKey, cachedEntry);
      }

      if (reportsResult.status === 'fulfilled') {
        const nextReports = reportsResult.value.data.reports || [];
        if (stillCurrent) {
          setTechnicalReports(nextReports);
        }

        const cachedEntry = projectDataCacheRef.current.get(cacheKey) || { updatedAt: null, fieldPrograms: [], reports: [] };
        cachedEntry.reports = nextReports;
        cachedEntry.updatedAt = projectVersion;
        projectDataCacheRef.current.set(cacheKey, cachedEntry);
      }
    } catch (error) {
      console.error('Failed to load project data:', error);
    } finally {
      projectDataFetchRef.current.delete(projectId);
    }
  };
  loadProjectDataRef.current = loadProjectData;

  // =================== DATA MANAGEMENT ===================

  // Document management handlers
  const handleDocumentsChange = (newDocuments: any[]) => {
    setDocuments(newDocuments);
    setProjectDocuments(newDocuments); // Sync projectDocuments for AI context
      };

  const handleDocumentContentExtracted = (docId: string, content: string) => {
    setDocumentContents(prev => new Map(prev.set(docId, content)));
  };

  // Polygon management handlers
  const ensureSeedPolygons = useCallback(async (projectId: string, existingPolygons: PolygonRegion[]) => {
    const seeds = getDefaultProjectPolygonSeeds(projectId);
    if (seeds.length === 0) {
      return mergeAndSortProjectPolygons(projectId, existingPolygons);
    }

    const existingKeys = new Set(existingPolygons.map((polygon) => normalizePolygonIdentity(polygon)));
    const createdPolygons: PolygonRegion[] = [];

    for (const seed of seeds) {
      const identity = normalizePolygonIdentity({ name: seed.name, region_type: 'analysis_region' });
      if (existingKeys.has(identity)) {
        continue;
      }

      const request: CreatePolygonRequest = {
        name: seed.name,
        description: seed.description,
        coordinates: seed.coordinates,
        region_type: 'analysis_region',
        metadata: approximatePolygonMetadata(seed.coordinates),
        visible: true,
        color: seed.color,
        opacity: seed.opacity,
        skip_validation: true
      };

      try {
        const response = await axios.post(`${API_BASE}/api/projects/${projectId}/polygons`, request);
        createdPolygons.push(normalizePolygonVisibility(response.data.polygon));
      } catch (error) {
        console.warn(`Falling back to local seed polygon for ${seed.name}`, error);
        createdPolygons.push(buildSeedPolygonFallback(projectId, seed));
      }

      existingKeys.add(identity);
    }

    return mergeAndSortProjectPolygons(projectId, [...existingPolygons, ...createdPolygons]);
  }, [API_BASE]);

  const loadPolygonRegions = async (projectId: string, options?: { force?: boolean }) => {
    const force = options?.force ?? false;

    if (!force) {
      const cachedPolygons = loadCachedPolygons(projectId);
      if (cachedPolygons.length > 0) {
        setPolygonRegions(cachedPolygons);
      }
    }

    const inFlight = polygonFetchRef.current.get(projectId);
    if (inFlight && !force) {
      return inFlight;
    }
    if (force && inFlight) {
      polygonFetchRef.current.delete(projectId);
    }

    const request = (async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/projects/${projectId}/polygons`);
        const polygons = Array.isArray(response.data?.polygons) ? response.data.polygons : [];
        const normalisedPolygons = polygons.map((polygon: PolygonRegion) => normalizePolygonVisibility(polygon));
        const ensuredPolygons = await ensureSeedPolygons(projectId, normalisedPolygons);
        if (process.env.NODE_ENV === 'development') {
          console.info('[App] loadPolygonRegions fetched', ensuredPolygons.length, 'polygons for', projectId);
        }
        // Persist regardless (keyed by projectId), but only show the polygons if
        // this is still the active project — a late response from a previous
        // project must not replace the current project's polygons.
        if (currentProjectRef.current?.id === projectId) {
          setPolygonRegions(ensuredPolygons);
        }
        persistProjectPolygons(projectId, ensuredPolygons);
      } catch (error) {
        console.error('Failed to load polygon regions:', error);
        const fallback = !force ? loadCachedPolygons(projectId) : [];
        const ensuredFallback = await ensureSeedPolygons(projectId, fallback);
        if (currentProjectRef.current?.id === projectId) {
          setPolygonRegions(ensuredFallback);
        }
        persistProjectPolygons(projectId, ensuredFallback);
      } finally {
        polygonFetchRef.current.delete(projectId);
      }
    })();

    polygonFetchRef.current.set(projectId, request);
    return request;
  };
  loadPolygonRegionsRef.current = loadPolygonRegions;

  const handlePolygonCreate = async (polygon: CreatePolygonRequest) => {
    if (!currentProject) {
      console.error('No current project for polygon creation');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/api/projects/${currentProject.id}/polygons`, polygon);
            // Add to local state
      setPolygonRegions(prev => {
        const next = [...prev, normalizePolygonVisibility(response.data.polygon)];
        persistProjectPolygons(currentProject.id, next);
        return next;
      });
      
      // Show success message
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'success',
        content: `Polygon region "${response.data.polygon.name}" created successfully`,
        data: response.data.polygon
      }]);
      
    } catch (error: any) {
      console.error('Failed to create polygon:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create polygon region';
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'error',
        content: errorMessage,
        data: error
      }]);
    }
  };

  const handlePolygonUpdate = useCallback(async (polygonId: string, updates: Partial<PolygonRegion>) => {
    if (!currentProject) {
      console.error('No current project for polygon update');
      return;
    }

    try {
      const response = await axios.put(`${API_BASE}/api/projects/${currentProject.id}/polygons/${polygonId}`, updates);
            // Update local state
      setPolygonRegions(prev => {
        const next = mergeAndSortProjectPolygons(
          currentProject.id,
          prev.map(p => (p.id === polygonId ? normalizePolygonVisibility(response.data.polygon) : p))
        );
        persistProjectPolygons(currentProject.id, next);
        return next;
      });
      
    } catch (error: any) {
      if (polygonId.startsWith('seed-')) {
        setPolygonRegions(prev => {
          const next = mergeAndSortProjectPolygons(
            currentProject.id,
            prev.map((polygon) =>
              polygon.id === polygonId
                ? normalizePolygonVisibility({
                    ...polygon,
                    ...updates,
                    updated_at: new Date().toISOString()
                  } as PolygonRegion)
                : polygon
            )
          );
          persistProjectPolygons(currentProject.id, next);
          return next;
        });
        return;
      }
      console.error('Failed to update polygon:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update polygon region';
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'error',
        content: errorMessage,
        data: error
      }]);
    }
  }, [API_BASE, currentProject]);

  const handlePolygonDelete = async (polygonId: string) => {
    if (!currentProject) {
      console.error('No current project for polygon delete');
      return;
    }

            try {
      await axios.delete(`${API_BASE}/api/projects/${currentProject.id}/polygons/${polygonId}`);
            // Remove from local state
      setPolygonRegions(prev => {
        const updatedRegions = mergeAndSortProjectPolygons(
          currentProject.id,
          prev.filter(p => p.id !== polygonId)
        );
        persistProjectPolygons(currentProject.id, updatedRegions);
                        return updatedRegions;
      });
      
      // Show success message
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'success',
        content: 'Polygon region deleted successfully'
      }]);

    } catch (error: any) {
      if (polygonId.startsWith('seed-')) {
        setPolygonRegions(prev => {
          const next = mergeAndSortProjectPolygons(
            currentProject.id,
            prev.filter((polygon) => polygon.id !== polygonId)
          );
          persistProjectPolygons(currentProject.id, next);
          return next;
        });
        return;
      }
      console.error('Failed to delete polygon:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete polygon region';
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'error',
        content: errorMessage,
        data: error
      }]);
    }
  };

  const handleDeleteAllPolygons = useCallback(async () => {
    if (!currentProject) {
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'error',
        content: 'Select a project before deleting polygons.'
      }]);
      return;
    }

    try {
      await axios.delete(`${API_BASE}/api/projects/${currentProject.id}/polygons`);
      setPolygonRegions([]);
      persistProjectPolygons(currentProject.id, []);
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'success',
        content: 'All polygon regions deleted successfully.'
      }]);
    } catch (error: any) {
      console.error('Failed to delete all polygons:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete polygon regions';
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'error',
        content: errorMessage,
        data: error
      }]);
    }
  }, [API_BASE, currentProject]);

  const handlePolygonImport = useCallback(
    async (file: File, options?: { color?: string; transparency?: number }) => {
      if (!currentProject) {
        setMessages((prev) => [
          ...prev,
          {
            timestamp: new Date().toISOString(),
            message_type: 'error',
            content: 'Select a project before importing GeoJSON polygons.'
          }
        ]);
        return;
      }

      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        const baseName =
          file.name.replace(/\.[^/.]+$/, '').trim() || currentProject.name || 'Imported Polygon';
        const extracted = extractGeoJsonPolygons(parsed, baseName);

        if (extracted.length === 0) {
          setMessages((prev) => [
            ...prev,
            {
              timestamp: new Date().toISOString(),
              message_type: 'warning',
              content: `No polygons found in ${file.name}.`
            }
          ]);
          return;
        }

        const MAX_IMPORT = 500;
        const polygonsToImport =
          extracted.length > MAX_IMPORT ? extracted.slice(0, MAX_IMPORT) : extracted;
        const truncated = extracted.length > MAX_IMPORT;

        const createdPolygons: PolygonRegion[] = [];
        const failures: Array<{ name: string; reason: string }> = [];

        for (const polygonDef of polygonsToImport) {
          const request: CreatePolygonRequest = {
            name: polygonDef.name,
            description: polygonDef.description,
            coordinates: polygonDef.coordinates,
            region_type: 'analysis_region',
            metadata: approximatePolygonMetadata(polygonDef.coordinates),
            visible: true,
            color: options?.color || polygonDef.color || '#0ea5e9',
            opacity: options?.transparency ?? 0.35,
            skip_validation: true
          };

          try {
            const response = await axios.post(
              `${API_BASE}/api/projects/${currentProject.id}/polygons`,
              request
            );
            createdPolygons.push(normalizePolygonVisibility(response.data.polygon));
          } catch (error: any) {
            const reason = error?.response?.data?.detail || error?.message || 'Unknown error';
            failures.push({ name: request.name, reason });
          }
        }

        if (createdPolygons.length > 0) {
          setPolygonRegions((prev) => {
            const next = [...prev, ...createdPolygons];
            persistProjectPolygons(currentProject.id, next);
            return next;
          });
          setMessages((prev) => [
            ...prev,
            {
              timestamp: new Date().toISOString(),
              message_type: 'success',
              content: `Imported ${createdPolygons.length} polygon${
                createdPolygons.length === 1 ? '' : 's'
              } from ${file.name}${truncated ? ` (showing first ${MAX_IMPORT})` : ''}.`
            }
          ]);
        }

        if (failures.length > 0) {
          const preview = failures.slice(0, 3).map((failure) => failure.name).join(', ');
          setMessages((prev) => [
            ...prev,
            {
              timestamp: new Date().toISOString(),
              message_type: 'warning',
              content: `Skipped ${failures.length} feature${
                failures.length === 1 ? '' : 's'
              } while importing ${file.name}: ${preview}${failures.length > 3 ? '…' : ''}`,
              data: failures
            }
          ]);
        }
      } catch (error: any) {
        setMessages((prev) => [
          ...prev,
          {
            timestamp: new Date().toISOString(),
            message_type: 'error',
            content: `Failed to import ${file.name}: ${error?.message || 'Invalid GeoJSON file.'}`,
            data: error
          }
        ]);
      }
    },
    [API_BASE, currentProject, setPolygonRegions, setMessages]
  );

  const handlePolygonAnalysis = useCallback(async (polygonId: string, config: AnalysisConfig, options?: { mode?: 'full' | 'quick' }) => {
    const polygon = polygonRegions.find(p => p.id === polygonId);
    if (!polygon || !currentProject) {
      console.error('Polygon or project not found for analysis');
      return;
    }

    const mode = options?.mode ?? 'full';
    const isQuickMode = mode === 'quick';

    // Prevent system sleep during polygon analysis
    const wakeLockActive = await preventSleep(`${config.commodity.toUpperCase()} ${isQuickMode ? 'quick ' : ''}polygon analysis for ${polygon.name}`);
    if (wakeLockActive) {
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'system',
        content: '> 🔒 Sleep prevention activated for polygon analysis'
      }]);
    }

    try {
            // Show starting message
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'info',
        content: `Starting ${config.commodity}${isQuickMode ? ' quick' : ''} analysis for region "${polygon.name}"...`
      }]);
      
      // Update polygon to mark it as used in analysis
      const updatedPolygon = {
        ...polygon,
        used_in_analyses: [...(polygon.used_in_analyses || []), config.analysis_name],
        last_analysis_date: new Date().toISOString()
      };
      
      await handlePolygonUpdate(polygonId, updatedPolygon);
      
      // Determine grid density overrides for this polygon/project context
      const gridResolution = resolveGridResolution({ projectId: currentProject.id, polygonId: polygon.id });

      // Call mineral discovery API with polygon coordinates
      const runSettings = (config.run_settings ?? {}) as ModelRunSettings & { resolvedRegion?: ModelRegionAssignment };
      const resolvedRegion = runSettings.resolvedRegion || (runSettings.defaultRegionId
        ? {
            regionId: runSettings.defaultRegionId,
            modelVersion: runSettings.defaultModelVersion
          }
        : undefined);

      const toggles = runSettings?.toggles ?? {
        enhancedPipeline: true,
        applyCalibration: true,
        computeUncertainty: false,
        adaptiveGrids: false,
        notifyOnComplete: true,
        autoRefreshCaches: false,
        useMetaEnsemble: true,
        useLightGBM: true
      };

      const analysisPayload: Record<string, any> = {
        commodity: config.commodity,
        region: currentProject.location || 'South America',
        polygon_coordinates: polygon.coordinates,
        polygon_id: polygon.id,
        polygon_name: polygon.name,
        analysis_name: config.analysis_name,
        confidence_threshold: config.confidence_threshold,
        output_formats: config.output_formats,
        project_id: currentProject.id,  // Add project association
        manual_trigger: true,
        test_mode: isQuickMode,
        analysis_mode: isQuickMode ? 'quick' : 'full',
        grid_resolution: gridResolution,
        analysis_config: {
          grid: {
            default_grid_resolution: gridResolution,
            adaptive: Boolean(toggles.adaptiveGrids)
          },
          pipeline: {
            enhanced: Boolean(toggles.enhancedPipeline),
            apply_calibrator: Boolean(toggles.applyCalibration),
            compute_uncertainty: Boolean(toggles.computeUncertainty),
            models: {
              meta_ensemble: toggles.useMetaEnsemble ?? true,
              lightgbm: toggles.useLightGBM ?? true
            }
          }
        },
        assigned_polygons: runSettings?.assignedPolygons ?? [polygon.id],
        operator_notes: runSettings?.notes?.trim() || undefined,
        notify_on_complete: Boolean(toggles.notifyOnComplete),
        enhanced_pipeline: Boolean(toggles.enhancedPipeline),
        apply_calibrator: Boolean(toggles.applyCalibration),
        compute_uncertainty: Boolean(toggles.computeUncertainty),
        adaptive_grids: Boolean(toggles.adaptiveGrids)
      };

      if (resolvedRegion?.regionId) {
        analysisPayload.model_region = resolvedRegion.regionId;
      }
      if (resolvedRegion?.modelVersion) {
        analysisPayload.model_version = resolvedRegion.modelVersion;
      } else if (runSettings.defaultModelVersion) {
        analysisPayload.model_version = runSettings.defaultModelVersion;
      }
      
      const endpoint = `${API_BASE}/api/enhanced-analysis/analyze`;

      // Surface a visible log so the user knows the request was dispatched
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'info',
        content: `Dispatching enhanced analysis for "${polygon.name}" (${config.commodity.toUpperCase()}) to ${endpoint}`,
        data: { polygonId, projectId: currentProject.id, mode }
      }]);

      const response = await axios.post(endpoint, analysisPayload);

      const returnedId = response.data.analysisId || response.data.analysis_id;

      if (returnedId) {
        setMessages(prev => [...prev, {
          timestamp: new Date().toISOString(),
          message_type: 'success',
          content: `Analysis${isQuickMode ? ' (quick)' : ''} started for "${polygon.name}" - ID: ${returnedId}`,
          data: response.data
        }]);
        
        // Monitor analysis progress
        // TODO: Add progress monitoring similar to other mineral discovery analyses
        
      } else {
        throw new Error('No analysis ID returned from server');
      }
      
    } catch (error: any) {
      console.error('Failed to start polygon analysis:', error);
      // Release wake lock on polygon analysis error
      if (wakeLockActive) {
        await allowSleep();
        setMessages(prev => [...prev, {
          timestamp: new Date().toISOString(),
          message_type: 'system',
          content: '> 🔓 Polygon analysis failed - system sleep restored'
        }]);
      }
      setMessages(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message_type: 'error',
        content: `Failed to start analysis (HTTP ${error?.response?.status || 'ERR'}): ${error?.response?.data?.detail || error.message}`,
        data: {
          error,
          endpoint: `${API_BASE}/api/enhanced-analysis/analyze`,
          polygonId,
          projectId: currentProject?.id,
          mode
        }
      }]);
    } finally {
      if (wakeLockActive && isWakeLockActive()) {
        await allowSleep();
      }
    }
  }, [API_BASE, currentProject, handlePolygonUpdate, polygonRegions, resolveGridResolution]);

  // Terminal is always open, so no toggle needed

  const handlePanelResize = useCallback((leftWidth: number, rightWidth: number) => {
    setLeftPanelWidth(leftWidth);
    setRightPanelWidth(rightWidth);
    leftPanelWidthRef.current = leftWidth;
    rightPanelWidthRef.current = rightWidth;
    schedulePanelWidthsPersist(leftWidth, rightWidth);
  }, [schedulePanelWidthsPersist]);

  // Draggable divider functionality
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const containerWidth = window.innerWidth;
    const newLeftWidth = Math.max(30, Math.min(70, (e.clientX / containerWidth) * 100));
    const newRightWidth = 100 - newLeftWidth;
    
    handlePanelResize(newLeftWidth, newRightWidth);
  }, [isDragging, handlePanelResize]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
    if (typeof window === 'undefined') {
      return;
    }
    if (panelPersistTimeoutRef.current !== null) {
      window.clearTimeout(panelPersistTimeoutRef.current);
      panelPersistTimeoutRef.current = null;
    }
    persistPanelWidths(leftPanelWidthRef.current, rightPanelWidthRef.current);
  }, [persistPanelWidths]);

  useEffect(() => {
    return () => {
      if (typeof window === 'undefined') {
        return;
      }
      if (panelPersistTimeoutRef.current !== null) {
        window.clearTimeout(panelPersistTimeoutRef.current);
        panelPersistTimeoutRef.current = null;
      }
      persistPanelWidths(leftPanelWidthRef.current, rightPanelWidthRef.current);
    };
  }, [persistPanelWidths]);

  // Handle marker visibility toggle
  const handleMarkerVisibilityChange = useCallback((markerId: string, visible: boolean) => {
    setMarkerVisibility(prev => {
      const newMap = new globalThis.Map(prev);
      newMap.set(markerId, visible);
      
      // Persist to localStorage - convert Map to array for JSON serialization
      try {
        const serializable = Array.from(newMap.entries());
        localStorage.setItem('mineral-ai-marker-visibility', JSON.stringify(serializable));
      } catch (error) {
        console.error('Failed to persist markerVisibility:', error);
      }
      
      return newMap;
    });
  }, []);

  const handleHeatmapVisibilityToggle = useCallback((analysisId: string, visible: boolean) => {
    handleMarkerVisibilityChange(`heatmap_${analysisId}`, visible);
  }, [handleMarkerVisibilityChange]);


  // Handle grid settings update
  const handleGridSettingsChange = useCallback((analysisId: string, mineralKey: string, updates: Partial<GridSettings>) => {
    setGridSettings((prev) => {
      const next = new globalThis.Map(prev);
      const base =
        next.get(analysisId) ||
        next.get(mineralKey) ||
        DEFAULT_ANALYSIS_GRID_SETTINGS;
      const sanitised = sanitizeGridSettings({ ...base, ...updates });

      next.set(analysisId, sanitised);
      next.set(mineralKey, { ...sanitised });

      try {
        const settingsObject = serializeGridSettings(next);
        localStorage.setItem('mineral-ai-grid-settings', JSON.stringify(settingsObject));
      } catch (error) {
        console.error('Failed to persist grid settings:', error);
      }

      return next;
    });
  }, []);

  // Batched visibility change for multiple markers (prevents 43 rapid state updates)
  const handleBatchMarkerVisibilityChange = useCallback((updates: Array<{markerId: string, visible: boolean}>) => {
    setMarkerVisibility(prev => {
      const newMap = new globalThis.Map(prev);
      
      // Apply all updates in a single batch
      updates.forEach(({markerId, visible}) => {
        newMap.set(markerId, visible);
      });

      // Persist to localStorage - convert Map to array for JSON serialization
      try {
        const serializable = Array.from(newMap.entries());
        localStorage.setItem('mineral-ai-marker-visibility', JSON.stringify(serializable));
      } catch (error) {
        console.error('Failed to persist batched markerVisibility:', error);
      }
      
      return newMap;
    });
  }, []);

  // Load all backend analyses for current project to sync Reports tab with markers
  const loadAllBackendAnalyses = useCallback(async (projectId: string, options?: { force?: boolean; signal?: AbortSignal }) => {
    const isRequestCanceled = (error: any) =>
      axios.isCancel(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError' ||
      error?.name === 'AbortError' ||
      error?.message === 'canceled';
    const force = options?.force ?? false;
    const signal = options?.signal;
    const existingAnalyses = analysisResultsRef.current.filter((result) => result?.project_id === projectId);
    const existingHaveGridData = existingAnalyses.some((r) =>
      r?.results?.ml_results?.grid_coordinates?.length > 0 ||
      r?.results?.ml_results?.grid_points?.length > 0
    );
    if (!force && existingAnalyses.length > 0 && existingHaveGridData) {
      return;
    }

    const cachedAnalyses = loadCachedAnalyses(projectId);
    if (!force && cachedAnalyses.length > 0) {
      // Set lightweight cached data for immediate UI render, but DON'T return early.
      // The cached data has no grid data (results field is stripped for localStorage size).
      // We must continue to fetch full results so the map can render grid overlays.
      setAnalysisResults((prev) => mergeProjectAnalyses(prev, projectId, cachedAnalyses));
    }

    // Phase 4: Check cache and prevent duplicate requests
    const cacheKey = `analyses_${projectId}`;
    const cached = getCachedRequest(cacheKey);

    if (cached?.promise) {
            return cached.promise;
    }

    if (cached?.data) {
      return;
    }

        try {
      // Create promise and cache it immediately to prevent duplicates
      const requestPromise = (async () => {
        try {
          // PERFORMANCE: Use lightweight summary endpoint instead of loading all full results
          // This eliminates the N+1 query problem: 1 summary call instead of 1+N full result calls
          const response = await axios.get(`${API_BASE}/api/mineral-discovery/analyses/${projectId}/summary`, { signal });

          // CRITICAL FIX: Access analyses array correctly
          const summaries = response.data?.analyses || response.data || [];

          if (summaries.length === 0) {
            return { success: true, noData: true };
          }

          // Transform summaries into lightweight analysis objects
          // Full results will be loaded on-demand when viewing specific analyses
          const lightweightAnalyses = summaries.map((summary: any) => {
            const analysisCommodity = summary?.commodity || 'gold';
            const analysisProjectName = summary?.project_name || null;

            // Create lightweight analysis object with summary data
            // Mark it as lightweight so UI knows full data hasn't loaded yet
            return normalizeAnalysisResult({
              id: summary.id,
              analysis_id: summary.id,
              project_id: projectId,
              commodity: analysisCommodity,
              date: summary.date || summary.created_at,
              created_at: summary.created_at,
              completedAt: summary.completed_at,
              status: summary.status || 'completed',
              type: summary.analysis_type || 'enhanced_analysis',
              analysis_type: summary.analysis_type || 'enhanced_analysis',
              // Include minimal metadata
              results: {
                high_potential_targets: summary.high_potential_count || 0,
                polygon_id: summary.polygon_id,
                polygon_name: summary.polygon_name,
                // Mark as lightweight - full data not loaded
                __lightweight: true,
                __summary_only: true
              },
              // Preserve for compatibility
              high_potential_count: summary.high_potential_count || 0,
              polygon_id: summary.polygon_id,
              polygon_name: summary.polygon_name
            }, {
              projectId,
              projectName: analysisProjectName,
              commodity: analysisCommodity
            });
          });

          let loadedResults = lightweightAnalyses;

          // CRITICAL FIX: Fetch full results for ALL analyses, not just the latest
          // This ensures all analyses have grid data for display in sidebar
          const analysisIds = summaries.map((s: any) => s.id).filter(Boolean);

          if (process.env.NODE_ENV === 'development') {
            console.info('[loadAllBackendAnalyses] Fetching full results for analyses:', analysisIds);
          }

          // Fetch full results for each analysis in parallel (limit to 10 most recent)
          const recentAnalysisIds = analysisIds.slice(0, 10);
          const fullResultsPromises = recentAnalysisIds.map(async (analysisId: string) => {
            try {
              // Use mineral-discovery endpoint (enhanced-analysis endpoint doesn't support direct results fetch)
              const response = await axios.get(`${API_BASE}/api/mineral-discovery/results/${analysisId}`, {
                signal,
                timeout: 10000
              });
              const data = response.data;

              // Grid data can be at multiple locations due to nested structure
              // Check: results.results.ml_results (double-nested), results.ml_results (single-nested), or ml_results (top-level)
              const mlResults = data?.results?.results?.ml_results || data?.results?.ml_results || data?.ml_results;
              const hasGridData = mlResults?.grid_coordinates?.length > 0 || mlResults?.grid_points?.length > 0;

              if (process.env.NODE_ENV === 'development') {
                console.info(`[loadAllBackendAnalyses] ${analysisId}: hasGridData=${hasGridData}`, {
                  double_nested: !!data?.results?.results?.ml_results,
                  single_nested: !!data?.results?.ml_results,
                  top_level: !!data?.ml_results,
                  grid_coords: mlResults?.grid_coordinates?.length,
                  grid_points: mlResults?.grid_points?.length
                });
              }

              if (hasGridData) {
                // Flatten the double-nested structure for consistency
                const flattenedResults = {
                  ...data,
                  id: analysisId,
                  analysis_id: analysisId,
                  project_id: data.project_id || projectId,
                  results: {
                    ...data.results,
                    ml_results: mlResults // Ensure ml_results is at the expected location
                  }
                };

                return normalizeAnalysisResult(flattenedResults, {
                  projectId: data.project_id || projectId,
                  projectName: data.project_name || null,
                  commodity: data.commodity || 'unknown'
                });
              }
            } catch (err: any) {
              if (process.env.NODE_ENV === 'development') {
                console.info(`[loadAllBackendAnalyses] Could not fetch full results for ${analysisId}:`, err?.message);
              }
            }
            return null;
          });

          const fullResults = (await Promise.all(fullResultsPromises)).filter(Boolean);

          if (process.env.NODE_ENV === 'development') {
            console.info('[loadAllBackendAnalyses] Fetched full results for', fullResults.length, 'of', recentAnalysisIds.length, 'analyses');
          }

          // Merge full results into loaded results (full results take priority)
          if (fullResults.length > 0) {
            loadedResults = mergeAnalysisLists(lightweightAnalyses, fullResults as any[]);
          }

          const projectResults = loadedResults.filter((r: any) => r.project_id === projectId);
          const normalizedProjectResults = mergeAnalysisLists([], projectResults);

          let nextState: any[] = [];
          setAnalysisResults((prevResults) => {
            nextState = mergeProjectAnalyses(prevResults, projectId, normalizedProjectResults);
            return nextState;
          });

          persistProjectAnalyses(projectId, normalizedProjectResults);

                    return { success: true };
        } catch (innerError: any) {
          if (isRequestCanceled(innerError)) {
            throw innerError;
          }
          console.error('❌ LOAD ALL ANALYSES: INNER ERROR:', innerError);
          throw innerError;
        }
      })();

      // Cache the promise to prevent duplicate in-flight requests
      setCachedRequest(cacheKey, null, requestPromise);
      
      // Wait for the promise and cache the result
      const result = await requestPromise;
      setCachedRequest(cacheKey, result);
            return result;
      
    } catch (error: any) {
      // Aborts are expected during project changes, React remounts, and route transitions.
      // Keep the request cache clean without reporting a production error.
      if (isRequestCanceled(error)) {
        requestCacheRef.current.delete(cacheKey);
        return { success: false, canceled: true };
      }

      console.error('❌ LOAD ALL ANALYSES: CRITICAL ERROR - Failed to load backend analyses:', error);
      console.error('❌ LOAD ALL ANALYSES: Error details:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      
      // Remove failed request from cache
      requestCacheRef.current.delete(cacheKey);
      throw error;
    }
  }, [getCachedRequest, setCachedRequest]); // Updated dependencies for Phase 4
  loadAllBackendAnalysesRef.current = loadAllBackendAnalyses;

  // Setup the cache clear reload function now that loadAllBackendAnalyses is defined
  useEffect(() => {
    clearCacheRef.current = () => {
      setTimeout(() => {
                loadAllBackendAnalyses('proj_1756434030', { force: true });
      }, 100);
    };
  }, [loadAllBackendAnalyses]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Function to add AI-generated content as documents
  const addAIGeneratedDocument = async (title: string, content: string, category: 'Field Program' | 'Geo Report' | 'AI Analysis') => {
        if (!currentProject) {
            return;
    }
    
    try {
      // Create a virtual PDF document entry
      const aiDoc = {
        id: `ai-${Date.now()}`,
        name: `${title}.pdf`,
        type: 'PDF',
        size: `${Math.ceil(content.length / 1024)}KB`,
        lastModified: 'Just now',
        status: 'completed',
        category: category
      };
      
            // Add to documents list
      const updatedDocs = [aiDoc, ...documents];
            setDocuments(updatedDocs);
      
      // Store the content for AI chat access
      const formattedContent = `AI-GENERATED DOCUMENT: ${title}\n\nGenerated: ${new Date().toLocaleString()}\nCategory: ${category}\n\n${content}`;
      setDocumentContents(prev => new Map(prev.set(aiDoc.id, formattedContent)));
      
      // Persist to localStorage using same pattern as DocumentView
      const storageKey = `mineral-ai-documents-${currentProject.id}`;
      const contentsStorageKey = `mineral-ai-document-contents-${currentProject.id}`;
      
      try {
        // Save documents list
        localStorage.setItem(storageKey, JSON.stringify(updatedDocs));
                // Save document contents
        const storedContents = localStorage.getItem(contentsStorageKey) || '{}';
        const parsedContents = JSON.parse(storedContents);
        parsedContents[aiDoc.id] = formattedContent;
        localStorage.setItem(contentsStorageKey, JSON.stringify(parsedContents));
        
              } catch (storageError) {
        console.error('❌ Failed to persist AI-generated document to localStorage:', storageError);
      }
    } catch (error) {
      console.error('❌ Failed to add AI-generated document:', error);
    }
  };

  // Removed uploadSiteData function - data uploads now handled through document library
  // =================== AI ANALYSIS ===================

  const runIntegratedAnalysis = async (mineralType: string) => {
    if (!currentProject) {
      addMessage('system', '> No active project selected');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/api/projects/${currentProject.id}/analyze`, {
        mineral_type: mineralType,
        analysis_type: 'integrated'
      });

      // Create document entry for the AI analysis
      if (response.data) {
        const analysis = response.data;
        const analysisTitle = `AI Analysis - ${mineralType.charAt(0).toUpperCase() + mineralType.slice(1)} Exploration - ${currentProject.name}`;
        
        // Format the analysis content for document
        const analysisContent = `AI EXPLORATION ANALYSIS
Analysis Type: Integrated ${mineralType.toUpperCase()} Analysis
Project: ${currentProject.name}
Mineral Target: ${mineralType.toUpperCase()}
Generated: ${new Date().toLocaleString()}
Confidence Level: ${analysis.confidence || 'High'}

ANALYSIS OVERVIEW:
${analysis.geological_interpretation || `Comprehensive AI-powered analysis of ${mineralType} mineralization potential using integrated geological, geochemical, and geophysical datasets.`}

PROBABILITY ASSESSMENT:
Mineralization Probability: ${analysis.probability ? (analysis.probability * 100).toFixed(1) + '%' : 'High'}
Analysis Methodology: ${analysis.analysis_methodology || 'Machine learning algorithms with geological domain expertise'}

EXPLORATION TARGETS:
${analysis.exploration_targets ? analysis.exploration_targets.map((target: string, index: number) => 
  `Target ${index + 1}: ${target}`
).join('\n') : 'Priority targets identified through integrated data analysis'}

KEY FINDINGS:
${analysis.reasoning || analysis.key_findings || `Advanced AI analysis indicates favorable geological conditions for ${mineralType} mineralization based on integrated exploration datasets.`}

DATA INTEGRATION QUALITY:
Quality Rating: ${analysis.data_integration_quality || 'Excellent'}
Confidence Factors: ${analysis.confidence_factors ? analysis.confidence_factors.join(', ') : 'Multi-dataset correlation, geological consistency'}
${analysis.uncertainty_factors ? `Uncertainty Factors: ${analysis.uncertainty_factors.join(', ')}` : ''}

RECOMMENDATIONS:
${analysis.recommendations ? analysis.recommendations.join('\n') : `1. Focus exploration efforts on identified priority targets
2. Conduct detailed ground-truthing of AI-identified anomalies
3. Implement systematic sampling program
4. Consider additional geophysical surveys`}

NEXT STEPS:
${analysis.next_steps || `Proceed with ground verification of AI-identified targets and expand data collection in priority areas.`}

DATA GAPS:
${analysis.data_gaps ? analysis.data_gaps.join('\n') : 'Analysis complete with available datasets'}

This analysis has been generated by Geomorphic AI AI using proprietary machine learning algorithms trained on extensive geological datasets.`;

        await addAIGeneratedDocument(analysisTitle, analysisContent, 'AI Analysis');
      }

      await fetchMessages({ force: true });
    } catch (error) {
      console.error('Analysis failed:', error);
      addMessage('system', '> Integrated analysis failed');
    } finally {
      setIsLoading(false);
    }
  };

  // =================== FIELD PROGRAMS ===================

  const designFieldProgram = async (programType: string, budgetRange: string) => {
    if (!currentProject) {
      addMessage('system', '> No active project selected');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/api/projects/${currentProject.id}/field-programs`, {
        program_type: programType,
        budget_range: budgetRange,
        timeline_months: 6
      });

      // Create document entry for the field program
      if (response.data) {
        const program = response.data;
        const programTitle = `${programType.charAt(0).toUpperCase() + programType.slice(1)} Field Program - ${currentProject.name}`;
        
        // Format the field program content for document
        const programContent = `FIELD PROGRAM DESIGN
Program Type: ${programType.toUpperCase()}
Budget Range: ${budgetRange.toUpperCase()}
Timeline: ${program.timeline_months || 6} months

OBJECTIVES:
${program.objectives || `Comprehensive ${programType} exploration program designed to advance the ${currentProject.name} project.`}

METHODOLOGY:
${program.methodology || `Systematic ${programType} approach targeting mineralization potential.`}

BUDGET OVERVIEW:
Budget Range: ${budgetRange.toUpperCase()}
${program.estimated_cost ? `Estimated Cost: $${program.estimated_cost}` : ''}

TIMELINE:
Duration: ${program.timeline_months || 6} months
${program.phases ? program.phases.map((phase: any, index: number) => 
  `Phase ${index + 1}: ${phase.duration_weeks || 4} weeks - ${phase.activities?.join(', ') || phase.description || 'Field activities'}`
).join('\n') : ''}

This field program has been designed by Geomorphic AI AI to optimize exploration efficiency and maximize discovery potential.`;

        await addAIGeneratedDocument(programTitle, programContent, 'Field Program');
      }

      await fetchMessages({ force: true });
      await loadProjectData({ force: true });
      setFieldProgramDialog(false);
      
    } catch (error) {
      console.error('Field program design failed:', error);
      addMessage('system', '> Field program design failed');
    } finally {
      setIsLoading(false);
    }
  };
  // =================== TECHNICAL REPORTS ===================

  const generateTechnicalReport = async (reportType: string) => {
    if (!currentProject) {
      addMessage('system', '> No active project selected');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/api/projects/${currentProject.id}/reports`, {
        report_type: reportType,
        author: 'Geomorphic AI AI',
        qualified_person: null
      });

      // Create document entry for the technical report
      if (response.data) {
        const report = response.data;
        const reportTitle = `${reportType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Report - ${currentProject.name}`;
        
        // Format the technical report content for document
        const reportContent = `TECHNICAL REPORT
Report Type: ${reportType.replace(/_/g, ' ').toUpperCase()}
Project: ${currentProject.name}
Author: ${report.author || 'Geomorphic AI AI'}
Generated: ${new Date().toLocaleDateString()}

EXECUTIVE SUMMARY:
${report.executive_summary || `This ${reportType.replace(/_/g, ' ')} report presents the current status and findings for the ${currentProject.name} exploration project.`}

PROJECT OVERVIEW:
Location: ${currentProject.location}
Target Minerals: ${(currentProject.target_minerals || []).join(', ')}
Project Status: Active Exploration

KEY FINDINGS:
${report.key_findings || report.summary || 'Comprehensive analysis of exploration data indicates promising mineralization potential based on integrated geological, geochemical, and geophysical datasets.'}

RECOMMENDATIONS:
${report.recommendations || `Continue systematic exploration approach with focus on priority targets identified through AI-powered data integration and analysis.`}

TECHNICAL DETAILS:
${report.technical_details || report.content || `Detailed technical analysis and data interpretation supporting the exploration strategy for ${currentProject.name}.`}

CONCLUSIONS:
${report.conclusions || `The ${currentProject.name} project demonstrates significant exploration potential warranting continued investigation and resource development.`}

This report has been generated by Geomorphic AI AI using advanced data analysis and industry best practices.`;

        await addAIGeneratedDocument(reportTitle, reportContent, 'Geo Report');
      }

      await fetchMessages({ force: true });
      await loadProjectData({ force: true });
      setReportDialog(false);
      
    } catch (error) {
      console.error('Report generation failed:', error);
      addMessage('system', '> Report generation failed');
    } finally {
      setIsLoading(false);
    }
  };

  // =================== MESSAGE HANDLING ===================



  const addMessage = useCallback((type: string, content: string, data?: any) => {
    const newMessage: SystemMessage = {
      timestamp: new Date().toISOString(),
      message_type: type,
      content,
      data
    };
    
    // Use functional form to avoid React state batching issues
    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages, newMessage];
      
      // Persist messages to localStorage for current project
      if (currentProject) {
        try {
          localStorage.setItem(`mineral-ai-messages-${currentProject.id}`, JSON.stringify(updatedMessages));
        } catch (error) {
          console.error('Failed to persist messages:', error);
        }
      }
      
      // Auto-scroll to bottom after message is added
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      
      return updatedMessages;
    });
  }, [currentProject]);

  const dispatchAnalysisRequest = useCallback((options: { polygonId: string; mineral: string; modelVersion?: string; modelRegion?: string | null; settings: ModelRunSettings }, mode: 'full' | 'quick') => {
    const polygon = polygonRegions.find((region) => region.id === options.polygonId);

    if (!polygon) {
      addMessage('system', '> Select a valid analysis polygon before running the model');
      return;
    }

    const timestamp = new Date().toISOString().split('T')[0];
    const analysisName = `${polygon.name} ${options.mineral.toUpperCase()} ${mode === 'quick' ? 'Quick ' : ''}Run ${timestamp}`;

    const outputFormats = Object.entries(options.settings.outputFormats)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([formatKey]) => formatKey);

    const runSettings = {
      ...options.settings,
      regionAssignments: { ...options.settings.regionAssignments },
      resolvedRegion:
        (options.settings as ModelRunSettings & { resolvedRegion?: ModelRegionAssignment }).resolvedRegion ||
        (options.modelRegion
          ? {
              regionId: options.modelRegion,
              modelVersion: options.modelVersion
            }
          : undefined)
    } as ModelRunSettings & { resolvedRegion?: ModelRegionAssignment };

    const config: AnalysisConfig = {
      commodity: options.mineral,
      analysis_name: analysisName,
      confidence_threshold: options.settings.confidenceThreshold,
      output_formats: outputFormats.length > 0 ? outputFormats : ['heatmap'],
      run_settings: runSettings
    };

    void handlePolygonAnalysis(polygon.id, config, { mode });
  }, [polygonRegions, handlePolygonAnalysis, addMessage]);

  const handleSidebarAnalysisRun = useCallback((options: { polygonId: string; mineral: string; modelVersion?: string; modelRegion?: string | null; settings: ModelRunSettings }) => {
    dispatchAnalysisRequest(options, 'full');
  }, [dispatchAnalysisRequest]);

  const handleSidebarQuickRun = useCallback((options: { polygonId: string; mineral: string; modelVersion?: string; modelRegion?: string | null; settings: ModelRunSettings }) => {
    dispatchAnalysisRequest(options, 'quick');
  }, [dispatchAnalysisRequest]);

  const handleSidebarEditPolygon = useCallback((polygonId: string) => {
    window.dispatchEvent(new CustomEvent('analysis:open-polygon-manager', {
      detail: { polygonId: polygonId || null }
    }));
  }, []);

  // NEW: Test Global Geology Service
  // Commented out per strategic pivot to focus on document management
  /*
  const handleTestGlobalGeology = async (coordinates: { lat: number; lng: number }) => {
    if (!currentProject) {
      addMessage('system', '> No active project selected');
      return;
    }

    addMessage('system', '> 🌍 Running Global Geology Service Test...');

    try {
      // Test global geological search
      const globalResponse = await axios.get(
        `${API_BASE}/api/global-geology/comprehensive-search`,
        {
          params: {
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            radius_km: 50,
            commodity_filter: 'Gold',
            include_regional: true
          }
        }
      );

            // Determine country based on coordinates for country-specific data
      let targetCountry = 'Unknown';
      const testLat = -22.3511;  // Using current test coordinates
      const testLng = 117.6981;
      
      // Simple coordinate-based country detection
      if (testLng > 112 && testLng < 159 && testLat > -44 && testLat < -9) {
        targetCountry = 'Australia';
      } else if (testLng > -141 && testLng < -52 && testLat > 41 && testLat < 84) {
        targetCountry = 'Canada';
      } else if (testLng > -25 && testLng < 45 && testLat > 35 && testLat < 72) {
        targetCountry = 'Europe';
      }
      
      // Test country-specific data for the detected country
      const countryResponse = await axios.get(
        `${API_BASE}/api/global-geology/country-data`,
        {
          params: {
            country: targetCountry,
            commodity: 'Gold',
            include_neighboring: false
          }
        }
      );

      // Create detailed console output
      if (globalResponse.data) {
        const summary = globalResponse.data.summary || {};
                                                      }
      
      if (countryResponse.data) {
        const countryFlag = targetCountry === 'Australia' ? '🇦🇺' : 
                           targetCountry === 'Canada' ? '🇨🇦' : 
                           targetCountry === 'Europe' ? '🇪🇺' : '🌍';
                                      }
      
            // Display results in chat
      const results = [];
      results.push('> ═══════════════════════════════════════');
      results.push('> 🌍 GLOBAL GEOLOGY SERVICE TEST COMPLETED');
      
      if (globalResponse.data) {
        const summary = globalResponse.data.summary || {};
        results.push(`> • International Sources: ${globalResponse.data.data_sources_queried?.length || 0} queried`);
        results.push(`> • Regional Coverage: ${summary.countries_covered?.join(', ') || 'None'}`);
        results.push(`> • Global Deposits: ${summary.total_deposits || 0} found`);
        results.push(`> • Search Radius: 50km international search`);
      }
      
      results.push('> • Country Data: Canada Gold resources accessed');
      results.push('> ✅ All international geology APIs functional!');
      results.push('> 💡 Check console for detailed international results');
      results.push('> ═══════════════════════════════════════');
      
      // Add each result immediately
      results.forEach(result => {
        addMessage('system', result);
      });

    } catch (error: any) {
      console.error('Global Geology test failed:', error);
      addMessage('system', `> ❌ Global Geology test failed: ${error.response?.data?.detail || error.message}`);
    }
  };
  */

  // NEW: Test Geological Data Hub
  // Commented out per strategic pivot to focus on document management
  /*
  const handleTestGeologicalHub = async (coordinates: { lat: number; lng: number }) => {
    if (!currentProject) {
      addMessage('system', '> No active project selected');
      return;
    }

    addMessage('system', '> 🧪 Running Geological Data Hub Test...');

    try {
      // Test comprehensive geological search
      const searchResponse = await axios.get(
        `${API_BASE}/api/geological-data-hub/comprehensive-search`,
        {
          params: {
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            radius_km: 25,
            commodity_filter: 'Gold',
            include_deposits: true,
            include_geology: true,
            include_geochemistry: true
          }
        }
      );

            // Test mineral potential assessment
      const potentialResponse = await axios.get(
        `${API_BASE}/api/geological-data-hub/mineral-potential`,
        {
          params: {
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            target_commodity: 'Gold',
            radius_km: 50
          }
        }
      );

            // Test project integration
      const integrationResponse = await axios.post(
        `${API_BASE}/api/projects/${currentProject.id}/integrate-geological-data`,
        {
          coordinates: coordinates,
          radius_km: 25,
          commodity_filter: 'Gold'
        }
      );

            // Create detailed console output for debugging
      if (searchResponse.data) {
        const summary = searchResponse.data.summary || {};
                                                if (summary.commodities_found && summary.commodities_found.length > 0) {
                  }
      }
      
      if (potentialResponse.data && potentialResponse.data.mineral_potential) {
        const potential = potentialResponse.data.mineral_potential;
                                      }
      
      if (integrationResponse.data && integrationResponse.data.success) {
        const recommendations = integrationResponse.data.recommendations || [];
        if (recommendations.length > 0) {
                    recommendations.forEach((rec: string, index: number) => {
                      });
        }
      }
      
            // Display comprehensive results in chat - force immediate display
      const results = [];
      results.push('> ═══════════════════════════════════════');
      results.push('> 🌍 GEOLOGICAL HUB TEST COMPLETED');
      if (searchResponse.data) {
        const summary = searchResponse.data.summary || {};
        results.push(`> • API Status: All endpoints responded (200 OK)`);
        results.push(`> • Search Area: Denver, CO (25-50km radius)`);
        results.push(`> • Deposits Found: ${summary.total_deposits || 0}`);
        results.push(`> • Geological Units: ${summary.total_geological_units || 0}`);
        results.push(`> • Data Quality: ${summary.data_quality || 'limited'}`);
      }
      results.push('> ✅ Test completed - check console for details');
      results.push('> ═══════════════════════════════════════');
      
      // Add each result immediately
      results.forEach(result => {
        addMessage('system', result);
              });
      
      // Force a small delay to ensure state updates, then check
      setTimeout(() => {
                }, 100);

    } catch (error: any) {
      console.error('Geological Hub test failed:', error);
      addMessage('system', `> ❌ Geological Hub test failed: ${error.response?.data?.detail || error.message}`);
    }
  };
  */

  // NEW: Test Enhanced Geophysical Service
  // Commented out per strategic pivot to focus on document management
  /*
  const handleTestGeophysicalData = async (coordinates: { lat: number; lng: number }) => {
    if (!currentProject) {
      addMessage('system', '> No active project selected');
      return;
    }

    addMessage('system', '> ⚡ Running Public Geophysical Data Test...');

    try {
      // Test public geophysical data query
      const geophysicalResponse = await axios.post(
        `${API_BASE}/api/geophysical/query-public-data`,
        {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          radius_km: 50,
          data_types: ['aeromagnetic', 'gravity', 'radiometric'],
          sources: ['usgs', 'geoscience_australia', 'gsc_canada']
        }
      );

      const geophysicalData = geophysicalResponse.data;
      
      addMessage('system', '> ✅ Geophysical Data Query completed successfully!');
      
      // Display summary
      const summary = geophysicalData.summary;
      addMessage('analysis', `📊 **GEOPHYSICAL DATA SUMMARY**

**Query Location:** ${coordinates.lat.toFixed(4)}, ${coordinates.lng.toFixed(4)}
**Search Radius:** 50 km
**Total Surveys Found:** ${summary.total_surveys_found}
**Data Types Available:** ${summary.data_types_available.join(', ')}
**Coverage Assessment:** ${summary.coverage_assessment.replace('_', ' ').toUpperCase()}

**DATA SOURCES:**`);

      // Display details for each source
      for (const [source, data] of Object.entries(geophysicalData.data_sources)) {
        const sourceData = data as any; // Type assertion for API response
        if (sourceData.status === 'success' && sourceData.surveys && sourceData.surveys.length > 0) {
          addMessage('analysis', `🔍 **${sourceData.source_name.toUpperCase()}**
- Status: ${sourceData.status}
- Total Surveys: ${sourceData.total_surveys}
- Notes: ${sourceData.notes}

**Survey Details:**`);
          
          sourceData.surveys.slice(0, 3).forEach((survey: any, index: number) => {
            let surveyDetails = `${index + 1}. **${survey.survey_name || 'Unnamed Survey'}**
   - Type: ${survey.survey_type}
   - Year: ${survey.year || 'Unknown'}`;
            
            if (survey.resolution) surveyDetails += `\n   - Resolution: ${survey.resolution}`;
            if (survey.coverage) surveyDetails += `\n   - Coverage: ${survey.coverage}`;
            if (survey.data_quality) surveyDetails += `\n   - Quality: ${survey.data_quality}`;
            if (survey.flight_height_m) surveyDetails += `\n   - Flight Height: ${survey.flight_height_m}`;
            if (survey.line_spacing_m) surveyDetails += `\n   - Line Spacing: ${survey.line_spacing_m}`;
            if (survey.stations) surveyDetails += `\n   - Stations: ${survey.stations}`;
            if (survey.accuracy) surveyDetails += `\n   - Accuracy: ${survey.accuracy}`;
            
            addMessage('analysis', surveyDetails);
          });
          
          if (sourceData.surveys.length > 3) {
            addMessage('analysis', `... and ${sourceData.surveys.length - 3} more surveys available`);
          }
        } else {
          addMessage('analysis', `⚠️ **${sourceData.source_name}**: ${sourceData.status === 'error' ? sourceData.error : sourceData.message || 'No data available'}`);
        }
      }

    } catch (error: any) {
      console.error('Geophysical data test error:', error);
      addMessage('system', `> ❌ Geophysical data test failed: ${error.response?.data?.detail || error.message}`);
    }
  };
  */

  // NEW: Test Drilling Database Service
  // Commented out per strategic pivot to focus on document management
  /*
  const handleTestDrillingDatabase = async (coordinates: { lat: number; lng: number }) => {
    if (!currentProject) {
      addMessage('system', '> No active project selected');
      return;
    }

    addMessage('system', '> 🛠️ Running Drilling Database Test...');

    try {
      // Test comprehensive drilling search
      const drillingResponse = await axios.get(
        `${API_BASE}/api/drilling-database/comprehensive-search`,
        {
          params: {
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            radius_km: 25,
            drilling_type: 'exploration',
            commodity_filter: 'Gold',
            min_depth: 50,
            max_depth: 500
          }
        }
      );

            // Test drilling database status
      const statusResponse = await axios.get(`${API_BASE}/api/drilling-database/status`);
            // Create detailed console output
      if (drillingResponse.data) {
        const summary = drillingResponse.data.summary || {};
                                                                if (summary.depth_statistics) {
                                                }

        if (summary.commodity_breakdown) {
                  }
      }
      
      if (statusResponse.data) {
                                      }
      
            // Display results in chat
      const results = [];
      results.push('> ═══════════════════════════════════════');
      results.push('> 🛠️ DRILLING DATABASE TEST COMPLETED');
      
      if (drillingResponse.data) {
        const summary = drillingResponse.data.summary || {};
        results.push(`> • API Status: All endpoints responded (200 OK)`);
        results.push(`> • Search Area: ${coordinates.lat.toFixed(4)}, ${coordinates.lng.toFixed(4)} (25km radius)`);
        results.push(`> • Drill Holes Found: ${summary.total_drill_holes || 0}`);
        results.push(`> • Assay Samples: ${summary.total_assay_samples || 0}`);
        results.push(`> • Data Quality: ${summary.data_quality || 'limited'}`);
        results.push(`> • International Sources: ${drillingResponse.data.data_sources_queried?.length || 0} queried`);
      }
      
      results.push('> ✅ All drilling database APIs functional!');
      results.push('> 💡 Check console for detailed drilling results');
      results.push('> ═══════════════════════════════════════');
      
      // Add each result immediately
      results.forEach(result => {
        addMessage('system', result);
      });

    } catch (error: any) {
      console.error('Drilling Database test failed:', error);
      addMessage('system', `> ❌ Drilling Database test failed: ${error.response?.data?.detail || error.message}`);
    }
  };
  */

  const handleSaveMineralAnalysis = (data: any) => {
    if (!currentProject || !data) {
      addMessage('system', '> No mineral analysis data to save');
      return;
    }

    const analysisData = {
      id: `analysis_${Date.now()}`,
      projectId: currentProject.id,
      projectName: currentProject.name,
      timestamp: new Date().toISOString(),
      coordinates: currentProject.coordinates,
      analysisMetadata: data.metadata,
      mineralLayers: data.mineral_layers,
      bounds: data.bounds
    };

    setSavedMineralAnalyses(prev => [...prev, analysisData]);
    addMessage('system', `> Mineral analysis saved for ${currentProject.name} (${Object.keys(data.mineral_layers).length} mineral types)`);
    
    // Store in localStorage with quota management
    try {
      const saved = JSON.parse(localStorage.getItem('saved-mineral-analyses') || '[]');
      saved.push(analysisData);
      
      // Try to save, handle quota exceeded
      const dataString = JSON.stringify(saved);
      
      // Check if data is too large (localStorage limit is ~5-10MB)
      if (dataString.length > 8 * 1024 * 1024) { // 8MB threshold
                // Keep only the 10 most recent analyses
        const recentAnalyses = saved
          .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, 10);
        
        localStorage.setItem('saved-mineral-analyses', JSON.stringify(recentAnalyses));
        addMessage('system', '> Storage optimized: kept 10 most recent analyses');
      } else {
        localStorage.setItem('saved-mineral-analyses', dataString);
      }
      
    } catch (error: any) {
      console.error('💾 localStorage save failed:', error);
      
      if (error.name === 'QuotaExceededError') {
        addMessage('system', '> ⚠️ Storage full - clearing old analyses to make space');
        
        try {
          // Emergency cleanup: keep only the 5 most recent analyses
          const saved = JSON.parse(localStorage.getItem('saved-mineral-analyses') || '[]');
          const recentAnalyses = saved
            .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
            .slice(0, 5);
          
          // Add the current analysis
          recentAnalyses.push(analysisData);
          
          localStorage.setItem('saved-mineral-analyses', JSON.stringify(recentAnalyses));
          addMessage('system', '> Storage cleared: kept 5 most recent analyses + current');
          
        } catch (secondError) {
          console.error('💥 Emergency cleanup failed:', secondError);
          addMessage('system', '> ❌ Failed to save analysis - storage critically full');
          
          // Last resort: clear all saved analyses
          localStorage.removeItem('saved-mineral-analyses');
          localStorage.setItem('saved-mineral-analyses', JSON.stringify([analysisData]));
          addMessage('system', '> 🆘 Storage reset - only current analysis saved');
        }
      } else {
        addMessage('system', `> ❌ Failed to save analysis: ${error.message}`);
      }
    }
  };

  const handleClearMineralAnalysis = () => {
        setMineralMapData(null);
    setIsNewMineralData(false);
    addMessage('system', '> Mineral analysis cleared from map view');
    addMessage('system', '> Generate a new mineral map to see amorphous coverage areas');
  };
  
  const handleMineralDataCentered = () => {
        setIsNewMineralData(false);
  };

  const handleGridResolutionChange = useCallback(
    (value: number, context?: GridControlContext | null) => {
      const clamped = clampGridResolution(value);
      const target = context || gridControlContext;
      const key = target?.key || 'global';

      setGridResolutionOverrides((prev) => {
        const next = new Map(prev);
        next.set(key, clamped);
        return next;
      });

      setActiveGridResolution(clamped);

      setMessages((prev) => [
        ...prev,
        {
          timestamp: new Date().toISOString(),
          message_type: 'info',
          content: `Grid density for ${target?.label || 'global default'} set to ${clamped}×${clamped}.`
        }
      ]);
    },
    [clampGridResolution, gridControlContext, setMessages]
  );

  const handleRefreshCaches = useCallback(
    async (context?: GridControlContext | null) => {
      const target = context || gridControlContext;
      const key = target?.key || 'global';

      if (isRefreshingCacheKey === key) {
        return;
      }

      setIsRefreshingCacheKey(key);
      try {
        const payload: Record<string, any> = {};
        if (target?.bounds) {
          payload.bbox = target.bounds.join(',');
        }
        if (target?.resolution) {
          payload.resolution = Math.round(target.resolution);
        }

        const response = await apiRequest('/api/enhanced-analysis/cache/refresh', {
          method: 'POST',
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Cache refresh failed');
        }

        const result = await response.json();
        const stdout = typeof result?.stdout === 'string' ? result.stdout.trim() : '';
        const timestamp = new Date().toISOString();

        setCacheRefreshTimestamps((prev) => {
          const next = new Map(prev);
          next.set(key, timestamp);
          return next;
        });

        setMessages((prev) => [
          ...prev,
          {
            timestamp,
            message_type: 'success',
            content: stdout
              ? `Caches refreshed for ${target?.label || 'default context'}. ${stdout.slice(0, 160)}${stdout.length > 160 ? '…' : ''}`
              : `Caches refreshed for ${target?.label || 'default context'}.`
          }
        ]);
      } catch (error: any) {
        setMessages((prev) => [
          ...prev,
          {
            timestamp: new Date().toISOString(),
            message_type: 'error',
            content: `Cache refresh failed for ${target?.label || 'default context'}: ${error?.message || 'Unknown error'}`
          }
        ]);
      } finally {
        setIsRefreshingCacheKey(null);
      }
    },
    [gridControlContext, isRefreshingCacheKey, setMessages]
  );

  // Mineral Discovery Handlers
  const handleRunMineralDiscovery = async (config: any) => {
    if (!currentProject) {
      addMessage('system', '> No active project selected');
      return;
    }

    // Prevent system sleep during analysis
    const wakeLockActive = await preventSleep(`${config.commodity.toUpperCase()} mineral discovery analysis`);
    if (wakeLockActive) {
      addMessage('system', '> 🔒 Sleep prevention activated - system will stay awake during analysis');
    } else {
      addMessage('system', '> ⚠️ Sleep prevention unavailable - please keep system active during analysis');
    }

    addMessage('system', `> 🤖 Running mineral discovery analysis for ${config.commodity.toUpperCase()}...`);
    
    try {
      // Add project context to configuration
      const configWithProject = {
        ...config,
        project_id: currentProject?.id
      };
      
      const gridResolution = resolveGridResolution({ projectId: currentProject?.id || null });

      const analysisPayload = {
        configuration: configWithProject,
        analysis_config: {
          grid: {
            default_grid_resolution: gridResolution
          }
        },
        grid_resolution: gridResolution,
        manual_trigger: true
      };
      
      const response = await axios.post(`${API_BASE}/api/enhanced-analysis/analyze`, analysisPayload);

      if (response.data.analysisId) {
        const analysisId = response.data.analysisId;
        addMessage('system', `> ✅ Analysis started: ${analysisId}`);
        addMessage('ai', `Analysis initiated for ${config.commodity} prospects in ${config.region}. Processing ${config.ruleWeights.filter((r: any) => r.enabled).length} active rules. Expected completion in 5-10 minutes.`);
        
        // Start polling for completion
        addMessage('system', '> ⏳ Monitoring analysis progress...');
        pollForAnalysisCompletion(analysisId, config);
      } else {
        // Release wake lock if analysis failed to start
        await allowSleep();
        addMessage('system', `> ❌ Analysis failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Mineral discovery error:', error);
      // Release wake lock on error
      await allowSleep();
      addMessage('system', '> ❌ Failed to start mineral discovery analysis');
      addMessage('ai', 'I encountered an error starting the mineral discovery analysis. Please check your project configuration and try again.');
    }
  };

  const pollForAnalysisCompletion = async (analysisId: string, config: any) => {
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    let attempts = 0;

    const checkCompletion = async () => {
      try {
        attempts++;
        const response = await axios.get(`${API_BASE}/api/mineral-discovery/analysis/${analysisId}/status`);
        
        if (response.data.status === 'completed') {
          // Release wake lock when analysis completes
          await allowSleep();
          addMessage('system', '> 🔓 Analysis completed - system sleep restored');
          
          // Fetch the detailed results
          const resultsResponse = await axios.get(`${API_BASE}/api/mineral-discovery/results/${analysisId}`);
          
          if (resultsResponse.data) {
            const analysisResult = normalizeAnalysisResult({
              id: analysisId,
              analysisDate: new Date().toISOString(),
              commodity: config.commodity,
              region: config.region || 'Unknown Region',
              configuration: config,
              results: resultsResponse.data.results || {},
              locations: resultsResponse.data.locations || [],
              highPotentialAreas: resultsResponse.data.highPotentialAreas || [],
              ruleContributions: resultsResponse.data.ruleContributions || [],
              performanceMetrics: resultsResponse.data.performanceMetrics || {}
            }, {
              projectId: currentProject?.id || null,
              projectName: currentProject?.name || null,
              commodity: config.commodity
            });
            
            let nextAnalysisState: any[] = [];
            setAnalysisResults((prev) => {
              nextAnalysisState = mergeProjectAnalyses(prev, currentProject?.id || null, [analysisResult]);
              return nextAnalysisState;
            });

            if (currentProject?.id) {
              const projectScoped = nextAnalysisState.filter((item) => item?.project_id === currentProject.id);
              persistProjectAnalyses(currentProject.id, projectScoped);
            }
            
            // Removed auto-generated message to reduce command center clutter
            // Removed auto-generated message to reduce command center clutter
            
            // Auto-switch to Reports view if user prefers
            // updateMainViewMode('reports');
            
                      }
          
        } else if (response.data.status === 'failed') {
          // Release wake lock when analysis fails
          await allowSleep();
          addMessage('system', `> ❌ Analysis failed: ${response.data.error || 'Unknown error'}`);
          addMessage('system', '> 🔓 System sleep restored');
          
        } else if (response.data.status === 'running' && attempts < maxAttempts) {
          // Continue polling
          const progress = response.data.progress || 0;
          if (attempts % 3 === 0) { // Update every 30 seconds
            addMessage('system', `> ⏳ Analysis in progress... ${Math.round(progress * 100)}%`);
          }
          setTimeout(checkCompletion, 10000); // Check again in 10 seconds
          
        } else {
          // Timeout or unknown status - release wake lock
          await allowSleep();
          addMessage('system', `> ⚠️ Analysis polling timeout. Check results manually with ID: ${analysisId}`);
          addMessage('system', '> 🔓 System sleep restored');
        }
        
      } catch (error) {
        console.error('Analysis polling error:', error);
        if (attempts < 3) {
          // Retry a few times for network errors
          setTimeout(checkCompletion, 10000);
        } else {
          // Release wake lock on polling failure after retries
          await allowSleep();
          addMessage('system', `> ⚠️ Unable to monitor analysis progress. Check results manually with ID: ${analysisId}`);
          addMessage('system', '> 🔓 System sleep restored');
        }
      }
    };

    // Start the first check
    setTimeout(checkCompletion, 5000); // Wait 5 seconds before first check
  };

  // Handle analysis completion from MapView (left panel)
  const handleAnalysisComplete = useCallback((result: any) => {
    if (process.env.NODE_ENV === 'development') {
      console.info('[handleAnalysisComplete] received result:', {
        result_project_id: result?.project_id,
        currentProject_id: currentProject?.id,
        analysis_id: result?.analysis_id || result?.id,
        has_ml_results: !!result?.results?.ml_results,
        grid_coordinates_count: result?.results?.ml_results?.grid_coordinates?.length,
        grid_points_count: result?.results?.ml_results?.grid_points?.length,
      });
    }
    // 🚨 PROJECT ISOLATION DEBUGGING - CRITICAL CHECK
    // CRITICAL: Only add to analysisResults if it belongs to current project
    if (result?.project_id && currentProject?.id && result.project_id !== currentProject.id) {
      if (process.env.NODE_ENV === 'development') {
        console.info('[handleAnalysisComplete] REJECTED - project mismatch', {
          result_project_id: result?.project_id,
          currentProject_id: currentProject?.id,
        });
      }
      return; // Don't add to analysisResults for wrong project
    }

    const normalizedResult = normalizeAnalysisResult(result, {
      projectId: currentProject?.id || null,
      projectName: currentProject?.name || null,
      commodity: result?.commodity || result?.results?.commodity || null
    });

    if (process.env.NODE_ENV === 'development') {
      console.info('[handleAnalysisComplete] normalizedResult:', {
        normalized_project_id: normalizedResult?.project_id,
        normalized_analysis_id: normalizedResult?.analysis_id || normalizedResult?.id,
        has_results_ml_results: !!normalizedResult?.results?.ml_results,
        grid_coords: normalizedResult?.results?.ml_results?.grid_coordinates?.length,
        grid_points: normalizedResult?.results?.ml_results?.grid_points?.length,
      });
    }

    // Add the result to analysisResults state so Reports tab appears
    let nextAnalysisState: any[] = [];
    setAnalysisResults((prev) => {
      nextAnalysisState = mergeProjectAnalyses(prev, currentProject?.id || null, [normalizedResult]);
      return nextAnalysisState;
    });

    if (currentProject?.id) {
      const projectScoped = nextAnalysisState.filter((item) => item?.project_id === currentProject.id);
      persistProjectAnalyses(currentProject.id, projectScoped);
    }
    
        // Add success message to terminal
    // Removed auto-generated messages to reduce command center clutter
    
      }, [currentProject]);

  const handleExportResults = async (resultId: string, format: string) => {
    addMessage('system', `> 📊 Exporting analysis results in ${format.toUpperCase()} format...`);
    
    try {
      const response = await axios.post('/api/mineral-discovery/export-results', {
        resultId,
        format
      });

      if (response.data.success) {
        addMessage('system', `> ✅ Results exported: ${response.data.filename}`);
        addMessage('ai', `Analysis results have been exported to ${response.data.filename}. The file contains ${format === 'pdf' ? 'a comprehensive report with charts and maps' : format === 'csv' ? 'tabular data for analysis' : 'GeoJSON spatial data'}.`);
      } else {
        addMessage('system', `> ❌ Export failed: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      addMessage('system', '> ❌ Failed to export results');
      addMessage('ai', 'I encountered an error exporting the results. Please try again or contact support if the issue persists.');
    }
  };

  const handleViewAnalysis = async (analysis: any) => {
                try {
      // Fetch full analysis results for map display
      const response = await apiRequest(`/api/mineral-discovery/results/${analysis.id}`);
      if (response.ok) {
        const analysisResult = await response.json();
        const normalizedResult = normalizeAnalysisResult(analysisResult, {
          projectId: currentProject?.id || analysis?.project_id || null,
          projectName: currentProject?.name || analysis?.project_name || null,
          commodity: analysis?.commodity || analysisResult?.commodity || null
        });

        let nextAnalysisState: any[] = [];
        setAnalysisResults((prev) => {
          nextAnalysisState = mergeProjectAnalyses(prev, currentProject?.id || null, [normalizedResult]);
          return nextAnalysisState;
        });

        if (currentProject?.id) {
          const projectScoped = nextAnalysisState.filter((item) => item?.project_id === currentProject.id);
          persistProjectAnalyses(currentProject.id, projectScoped);
        }
      } else {
        console.error('❌ Failed to load analysis results');
      }
    } catch (error) {
      console.error('❌ Error loading analysis results:', error);
    }
  };

  const handleMapLocationSelect = async (coordinates: { lat: number; lng: number }) => {
    if (!currentProject) {
      addMessage('system', '> No active project selected');
      return;
    }

    addMessage('user', `> Analyzing location: ${coordinates.lat.toFixed(4)}°${coordinates.lat >= 0 ? 'N' : 'S'}, ${Math.abs(coordinates.lng).toFixed(4)}°${coordinates.lng >= 0 ? 'E' : 'W'}`);

    setIsLoading(true);
    try {
      await axios.post(`${API_BASE}/api/fetch-satellite`, { coordinates: `${coordinates.lat},${coordinates.lng}` });
      await axios.post(`${API_BASE}/api/projects/${currentProject.id}/analyze`, {
        mineral_type: 'copper',
        analysis_type: 'integrated',
        coordinates
      });

      await fetchMessages();
    } catch (error) {
      console.error('Map location analysis failed:', error);
      addMessage('system', '> Location analysis failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchSatellite = async (coords: string) => {
    try {
      await axios.post(`${API_BASE}/api/fetch-satellite`, { coordinates: coords });
      addMessage('system', `> Fetching satellite imagery for ${coords}`);
    } catch (error) {
      console.error('Satellite fetch failed:', error);
      addMessage('system', '> Failed to fetch satellite imagery');
    }
  };

  const handleAnalyzeCopper = () => runIntegratedAnalysis('copper');
  const handleAnalyzeGold = () => runIntegratedAnalysis('gold');
  const handleAnalyzeSilver = () => runIntegratedAnalysis('silver');



  // =================== RENDERING ===================

  const handleSidebarResizeMove = useCallback((event: MouseEvent) => {
    const state = sidebarResizeStateRef.current;
    if (!state) {
      return;
    }
    const delta = event.clientX - state.startX;
    const nextWidth = clampSidebarWidth(state.startWidth + delta);
    setSidebarWidth((previous) => {
      if (Math.abs(previous - nextWidth) < 0.5) {
        return previous;
      }
      return nextWidth;
    });
  }, []);

  const handleSidebarResizeEnd = useCallback(() => {
    sidebarResizeStateRef.current = null;
    setIsSidebarResizing(false);
    if (typeof window !== 'undefined') {
      if (sidebarPersistTimeoutRef.current !== null) {
        window.clearTimeout(sidebarPersistTimeoutRef.current);
        sidebarPersistTimeoutRef.current = null;
      }
      persistSidebarWidth(Math.round(sidebarWidthRef.current));
    }
    if (typeof document !== 'undefined') {
      document.body.style.cursor = '';
    }
    window.removeEventListener('mousemove', handleSidebarResizeMove);
    window.removeEventListener('mouseup', handleSidebarResizeEnd);
  }, [handleSidebarResizeMove, persistSidebarWidth]);

  const handleSidebarResizeStart = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    sidebarResizeStateRef.current = {
      startX: event.clientX,
      startWidth: sidebarWidth
    };
    setIsSidebarResizing(true);
    if (typeof document !== 'undefined') {
      document.body.style.cursor = 'col-resize';
    }
    window.addEventListener('mousemove', handleSidebarResizeMove);
    window.addEventListener('mouseup', handleSidebarResizeEnd);
  }, [sidebarWidth, handleSidebarResizeMove, handleSidebarResizeEnd]);

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleSidebarResizeMove);
      window.removeEventListener('mouseup', handleSidebarResizeEnd);
      if (typeof window !== 'undefined' && sidebarPersistTimeoutRef.current !== null) {
        window.clearTimeout(sidebarPersistTimeoutRef.current);
        sidebarPersistTimeoutRef.current = null;
        persistSidebarWidth(Math.round(sidebarWidthRef.current));
      }
      if (typeof document !== 'undefined') {
        document.body.style.cursor = '';
      }
    };
  }, [handleSidebarResizeMove, handleSidebarResizeEnd, persistSidebarWidth]);

  const persistMapWorkbenchWidth = useCallback((width: number) => {
    if (typeof window === 'undefined') {
      return;
    }
    const ratio = Math.min(MAP_WORKBENCH_MAX_RATIO, Math.max(MAP_WORKBENCH_MIN_RATIO, width / getMapAppViewportWidth()));
    mapWorkbenchRatioRef.current = ratio;
    window.localStorage.setItem(MAP_WORKBENCH_RATIO_KEY, ratio.toFixed(4));
  }, []);

  const handleMapWorkbenchResizeMove = useCallback((event: PointerEvent) => {
    const state = mapWorkbenchResizeRef.current;
    if (!state || typeof window === 'undefined') {
      return;
    }
    const viewportWidth = getMapAppViewportWidth();
    const nextWidth = clampMapWorkbenchWidth(
      state.startWidth + (state.startX - event.clientX),
      viewportWidth
    );
    mapWorkbenchRatioRef.current = Math.min(
      MAP_WORKBENCH_MAX_RATIO,
      Math.max(MAP_WORKBENCH_MIN_RATIO, nextWidth / viewportWidth)
    );
    setMapWorkbenchWidth(nextWidth);
    window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }, []);

  const handleMapWorkbenchResizeEnd = useCallback(() => {
    mapWorkbenchResizeRef.current = null;
    setIsMapWorkbenchResizing(false);
    persistMapWorkbenchWidth(mapWorkbenchWidthRef.current);
    if (typeof document !== 'undefined') {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    window.removeEventListener('pointermove', handleMapWorkbenchResizeMove);
    window.removeEventListener('pointerup', handleMapWorkbenchResizeEnd);
  }, [handleMapWorkbenchResizeMove, persistMapWorkbenchWidth]);

  const handleMapWorkbenchResizeStart = useCallback((event: React.PointerEvent) => {
    if (mapWorkbenchCollapsed) {
      setMapWorkbenchCollapsed(false);
      return;
    }
    event.preventDefault();
    mapWorkbenchResizeRef.current = {
      startX: event.clientX,
      startWidth: mapWorkbenchWidthRef.current,
    };
    setIsMapWorkbenchResizing(true);
    if (typeof document !== 'undefined') {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    window.addEventListener('pointermove', handleMapWorkbenchResizeMove);
    window.addEventListener('pointerup', handleMapWorkbenchResizeEnd);
  }, [handleMapWorkbenchResizeEnd, handleMapWorkbenchResizeMove, mapWorkbenchCollapsed]);

  const resizeMapWorkbenchBy = useCallback((delta: number) => {
    if (typeof window === 'undefined') {
      return;
    }
    setMapWorkbenchCollapsed(false);
    setMapWorkbenchWidth((current) => {
      const viewportWidth = getMapAppViewportWidth();
      const next = clampMapWorkbenchWidth(current + delta, viewportWidth);
      persistMapWorkbenchWidth(next);
      window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      return next;
    });
  }, [persistMapWorkbenchWidth]);

  const handleMapWorkbenchSeparatorKeyDown = useCallback((event: React.KeyboardEvent) => {
    const step = event.shiftKey ? 48 : 16;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      resizeMapWorkbenchBy(step);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      resizeMapWorkbenchBy(-step);
    } else if (event.key === 'Home' && typeof window !== 'undefined') {
      event.preventDefault();
      const next = getMapWorkbenchBounds(getMapAppViewportWidth()).min;
      setMapWorkbenchWidth(next);
      persistMapWorkbenchWidth(next);
    } else if (event.key === 'End' && typeof window !== 'undefined') {
      event.preventDefault();
      const next = getMapWorkbenchBounds(getMapAppViewportWidth()).max;
      setMapWorkbenchWidth(next);
      persistMapWorkbenchWidth(next);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      setMapWorkbenchCollapsed((collapsed) => !collapsed);
    }
  }, [persistMapWorkbenchWidth, resizeMapWorkbenchBy]);

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handleMapWorkbenchResizeMove);
      window.removeEventListener('pointerup', handleMapWorkbenchResizeEnd);
      if (typeof document !== 'undefined') {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, [handleMapWorkbenchResizeEnd, handleMapWorkbenchResizeMove]);

  const renderProjectSidebar = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          width: sidebarWidth,
          minWidth: SIDEBAR_MIN_WIDTH,
          maxWidth: SIDEBAR_MAX_WIDTH,
          backgroundColor: 'var(--palantir-bg-secondary)',
          borderRight: '1px solid var(--palantir-border-subtle)',
          flex: 1,
          minHeight: 0,
          position: 'relative'
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minWidth: 0
          }}
        >
        <Box sx={{ pt: 1 }}>
          {/* Project selector dropdown */}
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              onClick={(e) => {
                const el = e.currentTarget;
                if (projectMenuAnchor && projectMenuTarget === '__selector__') {
                  setProjectMenuAnchor(null);
                  setProjectMenuTarget(null);
                } else {
                  setProjectMenuAnchor(el as HTMLElement);
                  setProjectMenuTarget('__selector__');
                }
              }}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: '4px',
                border: '1px solid #30363D',
                backgroundColor: '#161B22',
                cursor: 'pointer',
                minHeight: 32,
                '&:hover': { borderColor: '#58A6FF' },
              }}
            >
              <ProjectIcon sx={{ fontSize: 16, color: '#00D2FF', flexShrink: 0 }} />
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--palantir-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {currentProject?.name || 'Select project'}
                </Typography>
                {currentProject && (
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '9px', color: 'var(--palantir-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentProject.location} {'\u00B7'} {(currentProject.target_minerals || []).slice(0, 3).join(', ')}
                  </Typography>
                )}
              </Box>
              <UnfoldMoreIcon sx={{ fontSize: 16, color: 'var(--palantir-text-muted)', flexShrink: 0 }} />
            </Box>
            <IconButton size="small" onClick={() => setNewProjectDialog(true)} sx={{ p: 0.5, color: '#00D2FF' }}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
            {currentProject && (
              <IconButton
                size="small"
                onClick={(e) => {
                  setProjectMenuAnchor(e.currentTarget);
                  setProjectMenuTarget(currentProject.id);
                }}
                sx={{ p: 0.5, color: 'var(--palantir-text-muted)' }}
              >
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>

          {/* Project selector dropdown menu */}
          <Menu
            anchorEl={projectMenuTarget === '__selector__' ? projectMenuAnchor : null}
            open={projectMenuTarget === '__selector__' && Boolean(projectMenuAnchor)}
            onClose={() => { setProjectMenuAnchor(null); setProjectMenuTarget(null); }}
            PaperProps={{
              sx: {
                backgroundColor: '#161B22',
                border: '1px solid #30363D',
                maxHeight: 320,
                width: projectMenuAnchor?.clientWidth ?? 220,
              }
            }}
          >
            {projects.map((project) => (
              <MenuItem
                key={project.id}
                selected={currentProject?.id === project.id}
                onClick={() => {
                  activateProject(project.id);
                  setProjectMenuAnchor(null);
                  setProjectMenuTarget(null);
                }}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  color: 'var(--palantir-text-primary)',
                  py: 0.75,
                  '&.Mui-selected': { backgroundColor: 'var(--palantir-blue-600)', color: '#000' },
                  '&.Mui-selected:hover': { backgroundColor: 'var(--palantir-blue-600)' },
                  '&:hover': { backgroundColor: '#30363D' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ProjectIcon sx={{ fontSize: 14, color: currentProject?.id === project.id ? '#000' : 'var(--palantir-text-muted)' }} />
                  <Box>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '11px', lineHeight: 1.3 }}>{project.name}</Typography>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '9px', color: currentProject?.id === project.id ? 'rgba(0,0,0,0.6)' : 'var(--palantir-text-muted)', lineHeight: 1.2 }}>
                      {project.location}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Menu>

          {/* Project context menu (edit/delete) */}
          <Menu
            anchorEl={projectMenuTarget && projectMenuTarget !== '__selector__' ? projectMenuAnchor : null}
            open={Boolean(projectMenuAnchor) && projectMenuTarget !== null && projectMenuTarget !== '__selector__'}
            onClose={() => { setProjectMenuAnchor(null); setProjectMenuTarget(null); }}
            PaperProps={{
              sx: {
                backgroundColor: '#161B22',
                border: '1px solid #30363D',
                minWidth: 140,
              }
            }}
          >
            <MenuItem
              onClick={() => {
                const project = projects.find(p => p.id === projectMenuTarget);
                if (project) {
                  const boundaryCoords = getCoordinatesFromBoundary(project.project_boundary);
                  const currentCoords = project.coordinates || boundaryCoords || { lat: '', lng: '' };
                  setEditProjectForm({
                    id: project.id,
                    name: project.name,
                    location: project.location || '',
                    target_minerals: Array.isArray(project.target_minerals)
                      ? project.target_minerals.join(', ')
                      : (project.target_minerals || ''),
                    project_type: project.project_type || 'exploration',
                    coordinates: {
                      lat: currentCoords.lat ? currentCoords.lat.toString() : '',
                      lng: currentCoords.lng ? currentCoords.lng.toString() : ''
                    },
                    useManualCoordinates: false
                  });
                  setEditProjectDialog(true);
                }
                setProjectMenuAnchor(null);
                setProjectMenuTarget(null);
              }}
              sx={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--palantir-text-primary)', gap: 1, '&:hover': { backgroundColor: '#30363D' } }}
            >
              <EditIcon sx={{ fontSize: 14, color: 'var(--palantir-text-muted)' }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (projectMenuTarget) handleDeleteProject(projectMenuTarget);
                setProjectMenuAnchor(null);
                setProjectMenuTarget(null);
              }}
              sx={{ fontFamily: 'monospace', fontSize: '11px', color: '#E63946', gap: 1, '&:hover': { backgroundColor: '#30363D' } }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
              Delete
            </MenuItem>
          </Menu>

          {/* Data inventory tree */}
          <DataTree projectId={currentProject?.id ?? null} />

          {/* ML Runs — same section density as data tree */}
          <Box sx={{ px: 1, pb: 1 }}>
            <SidebarAnalysisRuns
              entries={analysisLegendEntries}
              selectedAnalysisId={selectedLegendAnalysisId}
              onSelectAnalysis={setSelectedLegendAnalysisId}
              visibilityMap={markerVisibility}
              onToggleVisibility={handleHeatmapVisibilityToggle}
            />
          </Box>

          <Divider sx={{ borderColor: '#30363D', mx: 2, my: 2 }} />

          <Box sx={{ px: 2, pt: 1 }}>
            <SidebarPermitAlerts />
          </Box>

          <Box sx={{ pb: 3 }} />
        </Box>
      </Box>
        <Box
          onMouseDown={handleSidebarResizeStart}
          sx={{
            width: '10px',
            cursor: 'col-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: isSidebarResizing ? 'rgba(88, 166, 255, 0.25)' : 'transparent',
            transition: 'background-color 120ms ease',
            '&:hover': {
              backgroundColor: 'rgba(88, 166, 255, 0.2)'
            }
          }}
        >
          <Box
            sx={{
              width: '2px',
              height: '32px',
              borderRadius: '999px',
              backgroundColor: isSidebarResizing ? 'var(--palantir-blue-500)' : 'rgba(148, 163, 184, 0.65)'
            }}
	          />
		      </Box>
	      </Box>
		  );
	  };
	  const renderMapProjectPicker = () => {
	    const menuOpen = Boolean(projectMenuAnchor) && projectMenuTarget === '__map_project_selector__';
	    return (
	      <Box sx={{ position: 'absolute', top: 16, left: 24, zIndex: 1400, maxWidth: 'calc(100% - 120px)' }}>
		        <Button
		          variant="outlined"
		          disableElevation
		          onClick={(event) => {
	            setProjectMenuAnchor(event.currentTarget);
	            setProjectMenuTarget('__map_project_selector__');
	          }}
	          sx={{
	            minHeight: 40,
	            maxWidth: '100%',
	            px: 1.4,
	            py: 0.75,
	            borderRadius: '8px',
		            backgroundColor: '#05080d',
		            backgroundImage: 'none',
		            border: '1px solid rgba(148, 163, 184, 0.42)',
		            boxShadow: '0 12px 28px rgba(0,0,0,0.42)',
		            color: 'var(--palantir-text-primary)',
		            textTransform: 'none',
		            fontFamily: `${APP_UI_FONT} !important`,
		            backdropFilter: 'blur(10px)',
		            '&.MuiButton-outlined': {
		              backgroundColor: '#05080d !important',
		              backgroundImage: 'none',
		              borderColor: 'rgba(148, 163, 184, 0.42) !important',
		              color: 'var(--palantir-text-primary) !important',
		            },
		            '&:hover': {
		              backgroundColor: '#0b1118',
		              borderColor: 'rgba(148, 163, 184, 0.62)',
		            },
		          }}
	        >
	          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
	            <Typography component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
	              {getProjectFlag(currentProject)}
	            </Typography>
	            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
		              <Typography sx={{ fontFamily: APP_UI_FONT, fontSize: '12px', fontWeight: 800, lineHeight: 1.15, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
		                {currentProject?.name || 'Select project'}
		              </Typography>
		              {currentProject?.location && (
		                <Typography sx={{ fontFamily: APP_UI_FONT, fontSize: '9px', lineHeight: 1.2, color: 'var(--palantir-text-muted)', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
		                  {currentProject.location}
		                </Typography>
		              )}
	            </Box>
	            <UnfoldMoreIcon sx={{ fontSize: 16, color: 'var(--palantir-text-muted)' }} />
	          </Box>
	        </Button>
	        <Menu
	          anchorEl={projectMenuAnchor}
	          open={menuOpen}
	          onClose={() => {
	            setProjectMenuAnchor(null);
	            setProjectMenuTarget(null);
	          }}
	          PaperProps={{
	            sx: {
	              mt: 1,
	              minWidth: 300,
	              maxHeight: 480,
		              backgroundColor: 'rgba(13, 17, 23, 0.98)',
		              border: '1px solid rgba(148, 163, 184, 0.42)',
	              color: 'var(--palantir-text-primary)',
	              boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
	              backdropFilter: 'blur(10px)',
	            }
	          }}
	        >
	          {projects.map((project) => (
	            <MenuItem
	              key={project.id}
	              selected={currentProject?.id === project.id}
	              onClick={() => {
	                activateProject(project.id);
	                setProjectMenuAnchor(null);
	                setProjectMenuTarget(null);
	              }}
	              sx={{
	                gap: 1.2,
	                py: 0.9,
	                color: 'var(--palantir-text-primary)',
		                '&.Mui-selected': {
		                  backgroundColor: 'rgba(148, 163, 184, 0.16)',
		                  borderLeft: '3px solid #58a6ff',
		                },
		                '&.Mui-selected:hover': {
		                  backgroundColor: 'rgba(148, 163, 184, 0.22)',
		                },
	                '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.12)' },
	              }}
	            >
	              <Typography component="span" sx={{ fontSize: 18, width: 24, textAlign: 'center' }}>
	                {getProjectFlag(project)}
	              </Typography>
	              <Box sx={{ minWidth: 0 }}>
		                <Typography sx={{ fontFamily: APP_UI_FONT, fontSize: '12px', fontWeight: 700, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
		                  {project.name}
		                </Typography>
		                <Typography sx={{ fontFamily: APP_UI_FONT, fontSize: '9px', color: 'var(--palantir-text-muted)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
		                  {project.location}
		                </Typography>
	              </Box>
	            </MenuItem>
	          ))}
	        </Menu>
	      </Box>
	    );
	  };
	  return (
	    <Box sx={{
      height: '100%',
      backgroundColor: '#0D1117',
      color: 'var(--palantir-text-primary)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'monospace'
    }}>
      {backendLiteMode && (
        <Box sx={{
          px: 2,
          py: 1,
          backgroundColor: '#5c2d00',
          borderBottom: '1px solid #b45309',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '12px', color: '#fbbf24' }}>
            Backend is running in <strong>lite mode</strong> (server_lite.py). Project management, analysis, and polygon routes are unavailable.
            Deploy main.py or update REACT_APP_API_BASE to point at a full backend.
          </Typography>
          <IconButton size="small" onClick={() => setBackendLiteMode(false)} sx={{ color: '#fbbf24', ml: 'auto', p: 0.5, fontSize: 14, lineHeight: 1 }}>
            &#x2715;
          </IconButton>
        </Box>
      )}
	      <Box
	        data-testid="map-workspace"
	        sx={{
	          flex: 1,
	          minHeight: 0,
	          minWidth: 0,
	          display: 'flex',
	          overflow: 'hidden',
	          backgroundColor: '#0D1117',
	        }}
	      >
	        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
	          <MapView
	            analysisResults={analysisResults}
	            selectedProject={currentProject}
	            availableProjects={projects}
	            onProjectSelect={activateProject}
	            onLocationSelect={handleMapLocationSelect}
	            mineralMapData={mineralMapData}
	            isNewMineralData={isNewMineralData}
	            onSaveMineralAnalysis={handleSaveMineralAnalysis}
	            onClearMineralAnalysis={handleClearMineralAnalysis}
	            onMineralDataCentered={handleMineralDataCentered}
	            onRunMineralDiscovery={handleRunMineralDiscovery}
	            polygonRegions={polygonRegions}
	            markerVisibility={markerVisibility}
	            onMarkerVisibilityChange={handleMarkerVisibilityChange}
	            onBatchMarkerVisibilityChange={handleBatchMarkerVisibilityChange}
	            onPolygonCreate={handlePolygonCreate}
	            onPolygonUpdate={handlePolygonUpdate}
	            onPolygonDelete={handlePolygonDelete}
	            onPolygonDeleteAll={handleDeleteAllPolygons}
	            onPolygonImport={handlePolygonImport}
	            onPolygonAnalysis={handlePolygonAnalysis}
	            onExportResults={handleExportResults}
	            onAnalysisComplete={handleAnalysisComplete}
	            gridSettings={gridSettings}
	            onGridSettingsChange={handleGridSettingsChange}
	            onLegendEntriesChange={setAnalysisLegendEntries}
	            gridResolutionResolver={resolveGridResolution}
	            legendPortalContainerId="map-workbench-layers"
	            inspectionPortalContainerId="map-workbench-selection"
	            onInspectionActiveChange={handleMapInspectionActiveChange}
	          />
	          {renderMapProjectPicker()}
	        </Box>

	        <Box
	          role="separator"
	          aria-label="Map workbench width"
	          aria-controls="map-workbench"
	          aria-orientation="vertical"
	          aria-valuemin={0}
	          aria-valuemax={Math.round(MAP_WORKBENCH_MAX_RATIO * 100)}
	          aria-valuenow={mapWorkbenchCollapsed ? 0 : Math.round((mapWorkbenchWidth / getMapAppViewportWidth()) * 100)}
	          tabIndex={0}
	          onPointerDown={handleMapWorkbenchResizeStart}
	          onKeyDown={handleMapWorkbenchSeparatorKeyDown}
	          onDoubleClick={() => {
	            if (typeof window === 'undefined') return;
	            const viewportWidth = getMapAppViewportWidth();
	            const next = clampMapWorkbenchWidth(viewportWidth * MAP_WORKBENCH_DEFAULT_RATIO, viewportWidth);
	            setMapWorkbenchCollapsed(false);
	            setMapWorkbenchWidth(next);
	            persistMapWorkbenchWidth(next);
	          }}
	          sx={{
	            width: 9,
	            flex: '0 0 9px',
	            position: 'relative',
	            cursor: 'col-resize',
	            backgroundColor: isMapWorkbenchResizing ? 'rgba(88, 166, 255, 0.16)' : '#0a0f14',
	            outline: 'none',
	            zIndex: 5,
	            '&::after': {
	              content: '""',
	              position: 'absolute',
	              top: 'calc(50% - 28px)',
	              left: 3,
	              width: 3,
	              height: 56,
	              borderRadius: 999,
	              backgroundColor: isMapWorkbenchResizing ? '#7dd3fc' : 'rgba(148, 163, 184, 0.42)',
	            },
	            '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.12)' },
	            '&:hover::after, &:focus-visible::after': { backgroundColor: '#7dd3fc' },
	            '&:focus-visible': { boxShadow: 'inset 0 0 0 2px rgba(125, 211, 252, 0.72)' },
	          }}
	        />

	        <Box
	          component="aside"
	          id="map-workbench"
	          aria-label="Map workbench"
	          sx={{
	            width: mapWorkbenchCollapsed ? MAP_WORKBENCH_COLLAPSED_WIDTH : mapWorkbenchWidth,
	            flex: `0 0 ${mapWorkbenchCollapsed ? MAP_WORKBENCH_COLLAPSED_WIDTH : mapWorkbenchWidth}px`,
	            minWidth: 0,
	            minHeight: 0,
	            display: 'flex',
	            flexDirection: 'column',
	            overflow: 'hidden',
	            backgroundColor: '#0e141b',
	            color: '#e8edf4',
	            transition: isMapWorkbenchResizing ? 'none' : 'width 180ms ease, flex-basis 180ms ease',
	          }}
	        >
	          {mapWorkbenchCollapsed ? (
	            <Box
	              component="button"
	              type="button"
	              aria-label="Open map workbench"
	              onClick={() => setMapWorkbenchCollapsed(false)}
	              sx={{
	                width: '100%',
	                height: '100%',
	                border: 0,
	                p: 0,
	                backgroundColor: '#0e141b',
	                color: '#94a3b8',
	                cursor: 'pointer',
	                display: 'flex',
	                flexDirection: 'column',
	                alignItems: 'center',
	                gap: 1.2,
	                pt: 2,
	                '&:hover, &:focus-visible': { color: '#dbeafe', backgroundColor: '#131c25' },
	              }}
	            >
	              <ChevronLeftIcon sx={{ fontSize: 18 }} />
	              <Typography sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontFamily: APP_UI_FONT, fontSize: '10px', fontWeight: 800, letterSpacing: 1.4 }}>
	                WORKBENCH
	              </Typography>
	              {mapHasSelection && <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#facc15', boxShadow: '0 0 0 3px rgba(250, 204, 21, 0.12)' }} />}
	            </Box>
	          ) : (
	            <Box sx={{ flexShrink: 0, borderBottom: '1px solid rgba(148, 163, 184, 0.16)', backgroundColor: '#101820' }}>
	                <Box sx={{ minHeight: 62, px: 1.7, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.1 }}>
	                  <Box sx={{ width: 22, height: 28, display: 'flex', gap: '2px', alignItems: 'stretch', flexShrink: 0 }} aria-hidden="true">
	                    {['#f59e0b', '#ef4444', '#facc15', '#38bdf8'].map((color, index) => (
	                      <Box key={color} sx={{ flex: 1, borderRadius: '2px', backgroundColor: color, opacity: 0.78, mt: `${index * 2}px`, mb: `${(3 - index) * 2}px` }} />
	                    ))}
	                  </Box>
	                  <Box sx={{ minWidth: 0, flex: 1 }}>
	                    <Typography sx={{ fontFamily: APP_UI_FONT, fontSize: '12px', fontWeight: 850, letterSpacing: 1.2, textTransform: 'uppercase', color: '#edf4fb' }}>
	                      Map workbench
	                    </Typography>
	                    <Typography sx={{ fontFamily: APP_UI_FONT, fontSize: '10px', color: '#8b9aaa', mt: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
	                      {currentProject?.name || 'No project selected'}
	                    </Typography>
	                  </Box>
	                  <Tooltip title="Collapse workbench" placement="left">
	                    <IconButton size="small" aria-label="Collapse map workbench" onClick={() => setMapWorkbenchCollapsed(true)} sx={{ color: '#94a3b8', '&:hover': { color: '#dbeafe', backgroundColor: 'rgba(148, 163, 184, 0.12)' } }}>
	                      <ChevronRightIcon sx={{ fontSize: 18 }} />
	                    </IconButton>
	                  </Tooltip>
	                </Box>
	                <Box role="tablist" aria-label="Map workbench views" sx={{ display: 'flex', px: 1.2, gap: 0.4 }}>
	                  {([
	                    { id: 'layers' as const, label: 'Layers' },
	                    { id: 'selection' as const, label: 'Selection' },
	                  ]).map((tab) => {
	                    const selected = mapWorkbenchTab === tab.id;
	                    return (
	                      <Box
	                        component="button"
	                        type="button"
	                        role="tab"
	                        aria-selected={selected}
	                        key={tab.id}
	                        onClick={() => setMapWorkbenchTab(tab.id)}
	                        sx={{
	                          position: 'relative',
	                          border: 0,
	                          borderBottom: selected ? '2px solid #7dd3fc' : '2px solid transparent',
	                          backgroundColor: 'transparent',
	                          color: selected ? '#e8edf4' : '#8796a5',
	                          px: 1.15,
	                          py: 0.85,
	                          fontFamily: APP_UI_FONT,
	                          fontSize: '11px',
	                          fontWeight: selected ? 750 : 600,
	                          cursor: 'pointer',
	                          '&:hover, &:focus-visible': { color: '#e8edf4', backgroundColor: 'rgba(125, 211, 252, 0.06)', outline: 'none' },
	                        }}
	                      >
	                        {tab.label}
	                        {tab.id === 'selection' && mapHasSelection && (
	                          <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, ml: 0.7, mb: 0.1, borderRadius: '50%', backgroundColor: '#facc15' }} />
	                        )}
	                      </Box>
	                    );
	                  })}
	                </Box>
	            </Box>
	          )}

	          <Box sx={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', display: mapWorkbenchCollapsed ? 'none' : 'block' }}>
	            <Box
	              id="map-workbench-layers"
	              role="tabpanel"
	              aria-label="Map layers"
	              sx={{ display: mapWorkbenchTab === 'layers' ? 'flex' : 'none', flexDirection: 'column', height: '100%', minHeight: 0, px: 1.35, py: 1.2 }}
	            />
	            <Box
	              id="map-workbench-selection"
	              role="tabpanel"
	              aria-label="Selected map feature"
	              sx={{ display: mapWorkbenchTab === 'selection' ? 'flex' : 'none', flexDirection: 'column', height: '100%', minHeight: 0 }}
	            />
	            {mapWorkbenchTab === 'selection' && !mapHasSelection && (
	              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', px: 4, textAlign: 'center', pointerEvents: 'none' }}>
	                <Box>
	                  <Typography sx={{ fontFamily: APP_UI_FONT, color: '#dbe4ee', fontSize: '13px', fontWeight: 700 }}>
	                    Select a map feature
	                  </Typography>
	                  <Typography sx={{ fontFamily: APP_UI_FONT, color: '#81909e', fontSize: '11px', lineHeight: 1.5, mt: 0.6 }}>
	                    Its source, status, evidence and available Morph actions will appear here.
	                  </Typography>
	                </Box>
	              </Box>
	            )}
	          </Box>
	        </Box>
	      </Box>

      {/* Dialogs */}
      
      {/* New Project Dialog */}
      <Dialog open={newProjectDialog} onClose={() => setNewProjectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#161B22', color: 'var(--palantir-text-primary)' }}>
          Create New Project
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#161B22' }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Project Name"
              variant="outlined"
              fullWidth
              value={newProjectForm.name}
              onChange={(e) => setNewProjectForm({...newProjectForm, name: e.target.value})}
              sx={{ 
                '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
              }}
            />
            <TextField
              label="Location"
              variant="outlined"
              fullWidth
              value={newProjectForm.location}
              onChange={(e) => setNewProjectForm({...newProjectForm, location: e.target.value})}
              sx={{ 
                '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
              }}
            />
            <TextField
              label="Target Minerals (comma separated)"
              variant="outlined"
              fullWidth
              value={newProjectForm.target_minerals}
              onChange={(e) => setNewProjectForm({...newProjectForm, target_minerals: e.target.value})}
              sx={{ 
                '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
              }}
            />
            
            {/* Manual Coordinates Section */}
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newProjectForm.useManualCoordinates}
                    onChange={(e) => setNewProjectForm({...newProjectForm, useManualCoordinates: e.target.checked})}
                    sx={{ color: 'var(--palantir-text-muted)', '&.Mui-checked': { color: 'var(--palantir-blue-600)' } }}
                  />
                }
                label={
                  <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '14px' }}>
                    Set manual coordinates (override location-based coordinates)
                  </Typography>
                }
              />
              
              <Collapse in={newProjectForm.useManualCoordinates}>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Latitude"
                      variant="outlined"
                      fullWidth
                      value={newProjectForm.coordinates.lat}
                      onChange={(e) => setNewProjectForm({
                        ...newProjectForm, 
                        coordinates: { ...newProjectForm.coordinates, lat: e.target.value }
                      })}
                      placeholder="e.g., 39.8283"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                        '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
                      }}
                    />
                    <TextField
                      label="Longitude"
                      variant="outlined"
                      fullWidth
                      value={newProjectForm.coordinates.lng}
                      onChange={(e) => setNewProjectForm({
                        ...newProjectForm,
                        coordinates: { ...newProjectForm.coordinates, lng: e.target.value }
                      })}
                      placeholder="e.g., -119.8163"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                        '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '12px', mt: 1 }}>
                    Coordinates will be used for USGS mineral deposit data and geological searches
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#161B22' }}>
          <Button onClick={() => setNewProjectDialog(false)} sx={{ color: 'var(--palantir-text-muted)' }}>Cancel</Button>
          <Button 
            onClick={createProject} 
            disabled={!newProjectForm.name || !newProjectForm.location}
            sx={{ color: 'var(--palantir-blue-600)' }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editProjectDialog} onClose={() => setEditProjectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#161B22', color: 'var(--palantir-text-primary)' }}>
          Edit Project
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#161B22' }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Project Name"
              variant="outlined"
              fullWidth
              value={editProjectForm.name}
              onChange={(e) => setEditProjectForm({...editProjectForm, name: e.target.value})}
              sx={{ 
                '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
              }}
            />
            <TextField
              label="Location"
              variant="outlined"
              fullWidth
              value={editProjectForm.location}
              onChange={(e) => setEditProjectForm({...editProjectForm, location: e.target.value})}
              sx={{ 
                '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
              }}
            />
            <TextField
              label="Target Minerals (comma separated)"
              variant="outlined"
              fullWidth
              value={editProjectForm.target_minerals}
              onChange={(e) => setEditProjectForm({
                ...editProjectForm, 
                target_minerals: e.target.value
              })}
              sx={{ 
                '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
              }}
            />
            
            {/* Manual Coordinates Section */}
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editProjectForm.useManualCoordinates}
                    onChange={(e) => setEditProjectForm({...editProjectForm, useManualCoordinates: e.target.checked})}
                    sx={{ color: 'var(--palantir-text-muted)', '&.Mui-checked': { color: 'var(--palantir-blue-600)' } }}
                  />
                }
                label={
                  <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '14px' }}>
                    Set manual coordinates (override location-based coordinates)
                  </Typography>
                }
              />
              
              <Collapse in={editProjectForm.useManualCoordinates}>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Latitude"
                      variant="outlined"
                      fullWidth
                      value={editProjectForm.coordinates.lat}
                      onChange={(e) => setEditProjectForm({
                        ...editProjectForm, 
                        coordinates: { ...editProjectForm.coordinates, lat: e.target.value }
                      })}
                      placeholder="e.g., 39.8283"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                        '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
                      }}
                    />
                    <TextField
                      label="Longitude"
                      variant="outlined"
                      fullWidth
                      value={editProjectForm.coordinates.lng}
                      onChange={(e) => setEditProjectForm({
                        ...editProjectForm,
                        coordinates: { ...editProjectForm.coordinates, lng: e.target.value }
                      })}
                      placeholder="e.g., -119.8163"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { color: 'var(--palantir-text-primary)' },
                        '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363D' }
                      }}
                    />
                  </Box>
                  
                  {/* Button to populate from boundary */}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button 
                      onClick={() => populateCoordinatesFromBoundary(false)}
                      variant="outlined"
                      size="small"
                      sx={{ 
                        color: 'var(--palantir-text-muted)', 
                        borderColor: '#30363D',
                        '&:hover': { color: 'var(--palantir-blue-600)', borderColor: 'var(--palantir-blue-600)' }
                      }}
                    >
                      Get from Project Boundary
                    </Button>
                    <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '12px' }}>
                      Coordinates enable USGS mineral deposit data
                    </Typography>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#161B22' }}>
          <Button onClick={() => setEditProjectDialog(false)} sx={{ color: 'var(--palantir-text-muted)' }}>Cancel</Button>
          <Button 
            onClick={handleUpdateProject} 
            disabled={!editProjectForm.name || !editProjectForm.location}
            sx={{ color: 'var(--palantir-blue-600)' }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field Program Dialog */}
      <Dialog open={fieldProgramDialog} onClose={() => setFieldProgramDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#161B22', color: 'var(--palantir-text-primary)' }}>
          Design Field Program
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#161B22' }}>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button onClick={() => designFieldProgram('drilling', 'medium')} variant="outlined" sx={{ color: 'var(--palantir-text-primary)', borderColor: '#30363D' }}>
              Drilling Program
            </Button>
            <Button onClick={() => designFieldProgram('sampling', 'low')} variant="outlined" sx={{ color: 'var(--palantir-text-primary)', borderColor: '#30363D' }}>
              Sampling Program
            </Button>
            <Button onClick={() => designFieldProgram('geophysics', 'medium')} variant="outlined" sx={{ color: 'var(--palantir-text-primary)', borderColor: '#30363D' }}>
              Geophysical Survey
            </Button>
            <Button onClick={() => designFieldProgram('comprehensive', 'high')} variant="outlined" sx={{ color: 'var(--palantir-text-primary)', borderColor: '#30363D' }}>
              Comprehensive Program
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#161B22' }}>
          <Button onClick={() => setFieldProgramDialog(false)} sx={{ color: 'var(--palantir-blue-600)' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportDialog} onClose={() => setReportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#161B22', color: 'var(--palantir-text-primary)' }}>
          Generate Technical Report
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#161B22' }}>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button onClick={() => generateTechnicalReport('exploration_summary')} variant="outlined" sx={{ color: 'var(--palantir-text-primary)', borderColor: '#30363D' }}>
              Exploration Summary
            </Button>
            <Button onClick={() => generateTechnicalReport('ni_43_101')} variant="outlined" sx={{ color: 'var(--palantir-text-primary)', borderColor: '#30363D' }}>
              NI 43-101 Report
            </Button>
            <Button onClick={() => generateTechnicalReport('jorc')} variant="outlined" sx={{ color: 'var(--palantir-text-primary)', borderColor: '#30363D' }}>
              JORC Report
            </Button>
            <Button onClick={() => generateTechnicalReport('samrec')} variant="outlined" sx={{ color: 'var(--palantir-text-primary)', borderColor: '#30363D' }}>
              SAMREC Report
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#161B22' }}>
          <Button onClick={() => setReportDialog(false)} sx={{ color: 'var(--palantir-blue-600)' }}>Close</Button>
        </DialogActions>
      </Dialog>

      </Box>
  );
}

export default App;
